/**
 * content-builder-neo.js
 *
 * Portable renderer for the AICO Neo content builder (`contentBuilderNeoJson`),
 * used for aiconeo landing pages AND news articles synced to Shopify (see
 * snippets/aico-neo-content.liquid for pages, news.js for articles).
 * Self-contained: no jQuery, no framework — it reads a JSON blob, emits HTML
 * for each section/element type, injects its own component CSS, and hydrates
 * sliders / lightboxes / countdowns / aico forms.
 *
 * COVERAGE (2026-09-10). aiconeo's element registry
 * (`src/components/pagebuilder/elements.ts` + the `ElementType` union in
 * types.ts — the oracle the aico test suite checks emitted types against)
 * defines 70 types on origin/main. 61 have an arm here.
 *
 * 52 of those render their content. Nine (blog-feed, booking,
 * category-products, chatbot, crm-list, dealer-map, dynamic-list, navigation,
 * newsletter) carry no data of their own: they name a source the AICO backend
 * resolves at view time, and a client renderer holding only the document
 * cannot invent it. Those get an EXPLICIT arm that renders the copy they do
 * carry and reports exactly what is missing — never a hole, and never a fake
 * widget.
 *
 * ARMED 2026-09-11 — `quote` and `audio`, the two types a full census of all
 * 11,929 production neo documents found 673 documents blocked on: 516 FCB news
 * articles carry a pull-quote (999 blockquotes in 632 blobs) and 157 carry a
 * podcast player (193 blobs). Unlike every other type in this file they were
 * added BEFORE a document uses them — the converter that will emit them is a
 * separate change — so their arms are written against the real source blobs
 * rather than against production elements.
 *
 * NOT YET ARMED — nine global-layout types added to the registry after this
 * sweep was measured: copyright, footer-columns, grid, logo, nav-menu,
 * news-list, social-links, theme-slot, video-box. A full census of all 11,279
 * production neo documents (FCSG 19929, FCB 19839, FCZ 19838) found ZERO
 * occurrences of any of them, and each degrades through the unknown-type net
 * below — visible copy plus a named console warning — so nothing is lost
 * today. They still want real arms; do that before a document uses one.
 *
 * WHY THAT MATTERS. On 2026-09-08 FC Zürich's 17,421 converted articles went
 * live rendering images and no text: the archive is `html`-shaped and this
 * engine's news-side sibling had every arm EXCEPT `html`. The conversion had
 * been verified against the BACKEND renderer, which does have that arm — a
 * correct measurement of the wrong code path. So the invariant here is not
 * "the common types work", it is: EVERY type either renders or degrades
 * visibly, and an unregistered type can no longer return ''.
 *
 * PROVENANCE: this file is the theme's own `assets/news-neo.js` (which keeps
 * rendering Neo NEWS articles) plus the two additions the FC Lausanne-Sport
 * theme made when it turned the same engine into a page renderer (`html` and
 * the bootstrap IIFE), plus the 2026-09-10 renderer sweep. Keep it diffable
 * against both.
 *
 * RELATIONSHIP TO content-builder-neo-ext.js. The ext file is loaded ONLY on
 * Neo PAGE templates and re-registers a richer, theme-styled version of many
 * of these types (its registerType calls run after this file, so they win
 * there). News pages load this engine WITHOUT the ext file — which is exactly
 * why every renderer has to exist here too, not only there. Where both
 * implement a type, this one mirrors the backend body renderer
 * (Modules/LandingPages/Service/ShopifySync/NeoLandingPageBodyRenderer.php)
 * so an article body looks the same on Shopify, in the popup and here.
 *
 * Drop-in usage:
 *   1. <div id="aico-content"></div>                                  (mount)
 *   2. <script id="aico-page-data" type="application/json">…</script> (data)
 *      where the JSON is shaped `{"contentBuilderNeoJson": {sections:[…]}}`
 *   3. include this file (defer). The bootstrap at the bottom finds the mount +
 *      data and paints automatically. To render elsewhere, call
 *      window.NewsNeo.render(data) / .hydrate(mount) yourself.
 *
 * Public API (window.NewsNeo — name kept for parity with the news renderer):
 *   - hasContent(data)       → boolean
 *   - render(data[, options]) → HTML string. options.themeTables: draw a plain
 *       table as bare markup the host theme styles (see renderThemeTable)
 *   - hydrate(root)          → wire sliders/lightboxes inside root
 *   - registerType(type, fn) → add or override an element renderer
 *   - resolveLocale(doc, locale, defaultLocale) → apply baseLocale /
 *       localeOverrides for `locale` to a RAW neo document (mutates + returns it)
 *   - hasLocaleStructure(doc) → boolean
 */
(function (window) {
  'use strict';

  // Forward styles.desktop from the JSON to the DOM
  var USE_API_INLINE_STYLES = true;

  // ============================================================
  // Detection
  // ============================================================

  function hasNeoContent(data) {
    if (!data || typeof data !== 'object') return false;
    var neo = data.contentBuilderNeoJson;
    if (!neo || typeof neo !== 'object') return false;
    if (!Array.isArray(neo.sections) || neo.sections.length === 0) return false;
    return true;
  }

  // ============================================================
  // Style helpers (used only when USE_API_INLINE_STYLES is true)
  // ============================================================

  function camelToKebab(s) {
    return s.replace(/([A-Z])/g, '-$1').toLowerCase();
  }

  // Expand `padding`/`margin` shorthand into longhands when both are present, so longhands always win.
  function normalizeShorthands(styleObj) {
    var out = {};
    for (var k in styleObj) {
      if (Object.prototype.hasOwnProperty.call(styleObj, k)) out[k] = styleObj[k];
    }
    var sides = ['Top', 'Right', 'Bottom', 'Left'];
    ['padding', 'margin'].forEach(function (base) {
      var hasIndividual = sides.some(function (s) { return (base + s) in out; });
      if (!hasIndividual || !(base in out)) return;
      var parts = String(out[base]).trim().split(/\s+/);
      var top = parts[0];
      var right = parts[1] !== undefined ? parts[1] : top;
      var bottom = parts[2] !== undefined ? parts[2] : top;
      var left = parts[3] !== undefined ? parts[3] : right;
      var fill = { Top: top, Right: right, Bottom: bottom, Left: left };
      sides.forEach(function (s) { if (!(base + s in out)) out[base + s] = fill[s]; });
      delete out[base];
    });
    return out;
  }

  function stylesToInline(styleObj) {
    if (!styleObj || typeof styleObj !== 'object' || Array.isArray(styleObj)) return '';
    var normalized = normalizeShorthands(styleObj);
    var out = [];
    for (var key in normalized) {
      if (!Object.prototype.hasOwnProperty.call(normalized, key)) continue;
      var val = normalized[key];
      if (val === null || val === undefined || val === '') continue;
      out.push(camelToKebab(key) + ':' + val);
    }
    return out.join(';');
  }

  function pickStyles(node, breakpoint) {
    if (!node || !node.styles) return {};
    var s = node.styles[breakpoint || 'desktop'];
    if (!s || Array.isArray(s)) return {};
    // For a legacy import, `display:none` is never written inline: the
    // element is hidden per tier by a class instead (withTierVisibility()), so
    // one hidden on the desktop but shown on a phone is not hidden at every
    // width by an inline declaration the phone tier cannot undo.
    if (isLegacyImportId(node.id) && tierDisplay(s) === 'none') {
      var copy = {};
      for (var k in s) {
        if (Object.prototype.hasOwnProperty.call(s, k) && k !== 'display') copy[k] = s[k];
      }
      return copy;
    }
    return s;
  }

  // ============================================================
  // Per-tier visibility
  //
  // The builder stores a per-viewport `display` on any element
  // (styles.desktop, styles.tablet, styles.mobile), each narrower tier
  // inheriting the value of the wider one. The converter writes it where a
  // legacy page switched a node per breakpoint, typically a desktop picture
  // and its phone crop. Where the resolved value is `none`, the element's root
  // wears `content-type-neo--hidden-<tier>` and TIER_VISIBILITY_RULES, injected
  // once per page and only when such an element is drawn, hide it at that
  // tier's widths. Same classes and rules as the aico server renderer
  // (NeoLandingPageBodyRenderer::withTierVisibility, NeoBodyStylesheet::
  // TIER_VISIBILITY_RULES), so a body rendered by either looks the same.
  // Only elements of a legacy import (UUIDv5 id, as on the server) take part;
  // an element authored in the neo builder, and an element without a
  // `display` in any tier, render byte for byte as before.
  // ============================================================

  var TIER_HIDDEN_CLASS_PREFIX = 'content-type-neo--hidden-';
  var TIER_GROUP_CLASS = 'content-type-neo-tier-group';
  var TIER_VISIBILITY_RULES = [
    // An element drawn as several siblings is wrapped once to carry the
    // class, and the wrapper takes no part in the layout.
    '.' + TIER_GROUP_CLASS + '{display:contents}',
    '@media(min-width:1024px){.content-type-neo--hidden-desktop{display:none!important}}',
    '@media(min-width:768px) and (max-width:1023px){.content-type-neo--hidden-tablet{display:none!important}}',
    '@media(max-width:767px){.content-type-neo--hidden-mobile{display:none!important}}'
  ].join('');
  var tierVisibilityInjected = false;

  function tierDisplay(tierStyles) {
    if (!tierStyles || typeof tierStyles !== 'object' || Array.isArray(tierStyles)) return null;
    var v = tierStyles.display;
    if (typeof v !== 'string') return null;
    v = v.replace(/^\s+|\s+$/g, '').toLowerCase();
    return v === '' ? null : v;
  }

  /** The tiers this element is hidden at, as class names (empty when none). */
  function tierHiddenClasses(el) {
    var styles = el && el.styles;
    if (!styles || typeof styles !== 'object') return [];
    var display = null;
    var classes = [];
    var tiers = ['desktop', 'tablet', 'mobile'];
    for (var i = 0; i < tiers.length; i++) {
      var v = tierDisplay(styles[tiers[i]]);
      if (v !== null) display = v;
      if (display === 'none') classes.push(TIER_HIDDEN_CLASS_PREFIX + tiers[i]);
    }
    return classes;
  }

  function injectTierVisibilityStyles() {
    if (tierVisibilityInjected) return;
    try {
      if (typeof document === 'undefined' || !document.createElement) return;
      if (document.querySelector && document.querySelector('style[data-neo-tier-visibility]')) {
        tierVisibilityInjected = true;
        return;
      }
      var style = document.createElement('style');
      style.setAttribute('data-neo-tier-visibility', '1');
      style.appendChild(document.createTextNode(TIER_VISIBILITY_RULES));
      (document.head || document.documentElement).appendChild(style);
      tierVisibilityInjected = true;
    } catch (e) { /* no-op */ }
  }

  /** Whether the markup is one element (comments and whitespace aside). */
  function hasSingleRoot(html) {
    try {
      if (typeof document === 'undefined' || !document.createElement) return false;
      var tpl = document.createElement('template');
      if (!('content' in tpl)) return false;
      tpl.innerHTML = html;
      var nodes = tpl.content.childNodes;
      var elements = 0;
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (n.nodeType === 1) elements++;
        else if (n.nodeType === 3 && /\S/.test(n.nodeValue)) return false;
      }
      return elements === 1;
    } catch (e) {
      return false;
    }
  }

  /** The class added to the markup's root element, or the markup wrapped in a
   *  layout-neutral group carrying it when there is no single root. */
  function addRootClass(html, cls) {
    var group = '<div class="' + TIER_GROUP_CLASS + ' ' + cls + '">' + html + '</div>';
    if (!hasSingleRoot(html)) return group;
    var lead = /^(?:\s|<!--[\s\S]*?-->)*/.exec(html)[0];
    var tag = /^<([a-zA-Z][a-zA-Z0-9-]*)((?:\s+[^\s"'>\/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)\s*(\/?)>/
      .exec(html.slice(lead.length));
    if (!tag) return group;
    var attrs = tag[2];
    var existing = /(\sclass\s*=\s*)(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i.exec(attrs);
    if (existing) {
      var value = existing[2] !== undefined ? existing[2]
        : (existing[3] !== undefined ? existing[3] : (existing[4] || ''));
      var merged = (escapeAttr(decodeEntities(value)) + ' ' + cls).replace(/^\s+/, '');
      attrs = attrs.slice(0, existing.index) + ' class="' + merged + '"' +
        attrs.slice(existing.index + existing[0].length);
    } else {
      attrs += ' class="' + cls + '"';
    }
    return lead + '<' + tag[1] + attrs + (tag[3] ? ' /' : '') + '>' +
      html.slice(lead.length + tag[0].length);
  }

  // A legacy import writes UUIDv5 ids; the neo builder writes UUIDv4 ids.
  // Same test as NeoLandingPageBodyRenderer::isLegacyImportId().
  function isLegacyImportId(id) {
    return typeof id === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-5[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
  }

  // Legacy imports only, as on the server (withTierVisibility() there): an
  // element authored in the neo builder renders exactly as before, so a
  // stored document whose element carries styles.mobile.display 'none' is not
  // hidden on phones now when it was shown before.
  function withTierVisibility(el, rendered) {
    if (!rendered || !isLegacyImportId(el && el.id)) return rendered;
    var classes = tierHiddenClasses(el);
    if (classes.length === 0) return rendered;
    injectTierVisibilityStyles();
    return addRootClass(rendered, classes.join(' '));
  }

  // Quote-only escape for style="..." payloads. Deliberately NOT escapeAttr:
  // that also encodes `&`, which would break legitimate values like url(...&...).
  function escapeStyleValue(s) {
    if (s == null) return '';
    return String(s).replace(/"/g, '&quot;');
  }

  function inlineStyleAttr(node) {
    if (!USE_API_INLINE_STYLES) return '';
    var s = stylesToInline(pickStyles(node));
    if (s && LOOK_DEPTH > 0) s = importantize(s);
    return s ? ' style="' + escapeStyleValue(s) + '"' : '';
  }

  // A STYLED ISLAND (aico registry section 7.2): a container that says
  // `drawsBox: true` or `layout: 'grid'`, a table that says `rendersLook`, a
  // button with the Button tab's keys. The product tab's stylesheet forces
  // `color:#000; font-weight:400; font-size; line-height` with !important on
  // every element in a content row (section-main-product.css, "new prodcut
  // content"), which is why the legacy widgets wrote every declaration with
  // !important. Inside an island the element's own declarations are written
  // with !important too (an inline !important outranks any stylesheet), and
  // the island's descendants inherit colour, size, weight and line height
  // from it (the `.neo-look` rules in the injected stylesheet) instead of
  // taking the forced values. Outside an island nothing changes.
  var LOOK_DEPTH = 0;

  // Split a declaration list on the `;` that separate declarations, never on
  // one inside parentheses or quotes: a url(data:image/png;base64,...) value
  // carries its own `;` and must stay one declaration.
  function splitDeclarations(css) {
    var src = String(css);
    var parts = [];
    var depth = 0;
    var quote = '';
    var start = 0;
    for (var i = 0; i < src.length; i++) {
      var ch = src.charAt(i);
      if (quote) {
        if (ch === '\\') i++;
        else if (ch === quote) quote = '';
      } else if (ch === '"' || ch === "'") {
        quote = ch;
      } else if (ch === '(') {
        depth++;
      } else if (ch === ')') {
        if (depth > 0) depth--;
      } else if (ch === ';' && depth === 0) {
        parts.push(src.slice(start, i));
        start = i + 1;
      }
    }
    parts.push(src.slice(start));
    return parts;
  }

  function importantize(css) {
    var parts = splitDeclarations(css);
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var d = parts[i].replace(/^\s+|\s+$/g, '');
      if (!d) continue;
      out.push(/!important$/i.test(d) ? d : d + '!important');
    }
    return out.join(';');
  }

  function importantizeStyleAttributes(html) {
    // The attribute value is escaped (`"` as &quot;), whose `;` is not a
    // declaration separator: unescape, importantize, escape again.
    return String(html).replace(/ style="([^"]*)"/g, function (m, css) {
      return ' style="' + escapeStyleValue(importantize(css.replace(/&quot;/g, '"'))) + '"';
    });
  }

  // The four properties the forced text styles set and the island reset
  // makes inherit (see the `.neo-look` rules in the injected stylesheet).
  var LOOK_TYPOGRAPHY = { 'color': 1, 'font-size': 1, 'font-weight': 1, 'line-height': 1 };

  // Inside an island the engine's own inline typography (a header cell's
  // weight, a caption's weight, a table's size and line height) has to win
  // over the island reset, and an inline !important outranks any stylesheet
  // rule. Only those four properties are marked: a display or a grid track
  // stays overridable by the engine's media rules and by hydration.
  function importantizeTypography(css) {
    var parts = splitDeclarations(css);
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var d = parts[i].replace(/^\s+|\s+$/g, '');
      if (!d) continue;
      var colon = d.indexOf(':');
      var prop = colon > 0 ? d.slice(0, colon).replace(/\s+$/, '').toLowerCase() : '';
      out.push(Object.prototype.hasOwnProperty.call(LOOK_TYPOGRAPHY, prop) && !/!important$/i.test(d) ? d + '!important' : d);
    }
    return out.join(';');
  }

  function importantizeTypographyAttributes(html) {
    return String(html).replace(/ style="([^"]*)"/g, function (m, css) {
      return ' style="' + escapeStyleValue(importantizeTypography(css.replace(/&quot;/g, '"'))) + '"';
    });
  }

  // Strip a single wrapping <p> so heading text from the rich-text editor isn't invalid <hN><p>.
  function stripOuterParagraph(html) {
    if (!html) return '';
    return String(html).replace(/^\s*<p[^>]*>([\s\S]*?)<\/p>\s*$/i, '$1');
  }

  function escapeAttr(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Pick the active translation: window.current_locale_code if matched, else first available.
  function pickTranslation(translations) {
    if (!translations || typeof translations !== 'object') return {};
    var loc = (typeof window !== 'undefined' && window.current_locale_code) ? window.current_locale_code : null;
    if (loc && translations[loc]) return translations[loc];
    for (var k in translations) {
      if (Object.prototype.hasOwnProperty.call(translations, k)) return translations[k] || {};
    }
    return {};
  }

  // Prefix bare domains with https:// so they don't get treated as relative paths.
  function normalizeUrl(url) {
    if (!url) return '';
    var s = String(url).trim();
    if (!s) return '';
    // Scheme test runs on a probe with control chars/spaces removed, because
    // browsers strip those when resolving (e.g. "java\tscript:"). The probe is
    // only used for this test - everything below still works on the original `s`.
    var probe = s.replace(/[\x00-\x20]+/g, '').toLowerCase();
    if (probe.indexOf('javascript:') === 0) return '';
    if (probe.indexOf('vbscript:') === 0) return '';
    if (probe.indexOf('data:') === 0 && probe.indexOf('data:image/') !== 0) return '';
    if (/^([a-z][a-z0-9+.\-]*:|\/\/|\/|#)/i.test(s)) return s;
    if (/^[^\s\/?]+\.[a-z]{2,}/i.test(s)) return 'https://' + s;
    return s;
  }

  // Element renderers return inner HTML only; row/column wrapping lives in
  // renderSection. The `columns` type is handled there, not here.
  var renderers = {};

  // ============================================================
  // Native text settings (aico native settings contract v2, section 3)
  //
  //   text, heading  content.textStyle         'default' | 'subtitle-1' |
  //                                            'subtitle-2' | 'body-2' | 'caption'
  //   text           content.paragraphSpacing  'normal' | 'none'
  //
  // `textStyle` is one of the shop's named text styles: the product tab's
  // stylesheet sizes them by class (section-main-product.css, `.aico-subtitle1`
  // and friends, with !important), so the class goes on every top-level block
  // of the text and on the heading's own tag. `paragraphSpacing: 'none'` draws
  // the text's top-level paragraphs as `div` lines, the markup the legacy block
  // printed and the tab's stylesheet gives no paragraph margin (an empty
  // `<div><br></div>` is the legacy blank line). Any other value, or none,
  // leaves the markup exactly as it was.
  // ============================================================

  var NATIVE_TEXT_STYLE_CLASSES = {
    'subtitle-1': 'aico-subtitle1',
    'subtitle-2': 'aico-subtitle2',
    'body-2': 'aico-body-text2',
    'caption': 'aico-caption'
  };

  function nativeTextStyleClass(content) {
    var style = content && typeof content.textStyle === 'string' ? content.textStyle : '';
    return Object.prototype.hasOwnProperty.call(NATIVE_TEXT_STYLE_CLASSES, style) ? NATIVE_TEXT_STYLE_CLASSES[style] : '';
  }

  function withNativeTextSettings(content, html) {
    var styleClass = nativeTextStyleClass(content);
    var lines = !!(content && content.paragraphSpacing === 'none');
    if ((!styleClass && !lines) || !html) return html;
    try {
      if (typeof document === 'undefined' || !document.createElement) return html;
      var tpl = document.createElement('template');
      if (!('content' in tpl)) return html;
      tpl.innerHTML = html;
      var nodes = [];
      for (var i = 0; i < tpl.content.childNodes.length; i++) {
        if (tpl.content.childNodes[i].nodeType === 1) nodes.push(tpl.content.childNodes[i]);
      }
      for (var n = 0; n < nodes.length; n++) {
        var node = nodes[n];
        if (lines && node.tagName.toLowerCase() === 'p') {
          // The paragraph's own attributes (an alignment set in the editor) stay on the line.
          var div = document.createElement('div');
          for (var a = 0; a < node.attributes.length; a++) div.setAttribute(node.attributes[a].name, node.attributes[a].value);
          while (node.firstChild) div.appendChild(node.firstChild);
          node.parentNode.replaceChild(div, node);
          node = div;
        }
        if (styleClass) node.className = (node.className ? node.className + ' ' : '') + styleClass;
      }
      return tpl.innerHTML;
    } catch (e) {
      return html;
    }
  }

  renderers.heading = function (el) {
    var content = el.content || {};
    var level = content.level || 'h2';
    if (!/^h[1-6]$/i.test(level)) level = 'h2';
    var text = stripOuterParagraph(content.text != null ? content.text : '');
    // content.textStyle: the shop's named text style as the heading's class (see above).
    var styleClass = nativeTextStyleClass(content);
    return '<div class="content-type-title content-type-neo-heading">' +
      '<' + level + (styleClass ? ' class="' + styleClass + '"' : '') + inlineStyleAttr(el) + '>' + text + '</' + level + '>' +
      '</div>';
  };

  renderers.text = function (el) {
    var content = el.content || {};
    var text = content.text != null ? content.text : '';
    text = withNativeTextSettings(content, text);
    return '<div class="content-type-text content-type-neo-text"' + inlineStyleAttr(el) + '>' + text + '</div>';
  };

  // Custom HTML block — render the author's raw markup verbatim (matches the React builder's
  // `html` element, which stores HTML untouched by the rich-text engine).
  renderers.html = function (el) {
    var content = el.content || {};
    var html = content.html != null ? content.html : '';
    return '<div class="content-type-html content-type-neo-html"' + inlineStyleAttr(el) + '>' + html + '</div>';
  };

  /**
   * Wrap a node in the element's authored link, if it has one.
   *
   * `linkUrl` + `openInNewTab` is what BOTH legacy converters write for a
   * linked image (the Alpine `image` with a link, the Grapes `link-image` and
   * `link-wrapper > link-image`). The backend body renderer grew the arm for it
   * (NeoLandingPageBodyRenderer::renderImage); this renderer never did, so the
   * picture arrived and the link around it did not — measured across the three
   * clubs' landing pages as 80 dead links on 37 pages, every one of them a
   * ticket shop, a sponsor site or a PDF the reader can no longer reach.
   * normalizeUrl already drops javascript:/vbscript:/non-image data: URLs.
   */
  function wrapInAuthoredLink(content, inner) {
    var url = normalizeUrl(trimStr(content && content.linkUrl));
    if (!url) return inner;
    var target = (content.openInNewTab === true) ? ' target="_blank" rel="noopener noreferrer"' : '';
    return '<a href="' + escapeAttr(url) + '"' + target + '>' + inner + '</a>';
  }

  // Image `content.wrap` (aico native settings contract v2, section 1): 'left'
  // or 'right' floats the picture to that side and the words of the elements
  // after it flow round it, as the legacy picture's own `float` did. The float
  // goes on the `<img>`, where the legacy page had it; the wrapper stays a
  // plain block of no height, so later siblings wrap round the float. No clear:
  // the legacy page had none. 'none', another value or none draws as before.
  function withImageWrap(content, imgTag) {
    var side = content && (content.wrap === 'left' || content.wrap === 'right') ? content.wrap : '';
    if (!side) return imgTag;
    if (/\sstyle="/.test(imgTag)) {
      return imgTag.replace(/\sstyle="([^"]*)"/, function (all, css) {
        var rest = css.replace(/(^|;)\s*float\s*:[^;]*/g, '$1').replace(/^;+|;+$/g, '');
        return ' style="' + (rest ? rest + ';' : '') + 'float:' + side + '"';
      });
    }
    return imgTag.replace(/^<img/, '<img style="float:' + side + '"');
  }

  // Image `content.onTextLine: true` (aico native settings contract v2, picture
  // line): the picture sits on a line of text, as a picture alone in a
  // paragraph did. The line keeps the space a line of words has below its
  // baseline, which follows the page's own font and line height, so it is
  // drawn by the browser, not written as a number. The picture is inline on
  // the line, a paragraph, so the page draws it with its paragraph font; the
  // element's margins move to that paragraph, and a picture centred or pushed
  // right by auto side margins is aligned on the line instead.
  // `content.emptyLinesBefore` (0-10) puts that many empty lines of the paragraph
  // before the picture's line, as line breaks before a legacy picture did.
  // `content.emptyLinesAfter` (0-10) adds that many empty lines to the paragraph
  // after the picture's line, as pictures without an address that the tab drew
  // a line wide once did. Null when the picture floats (content.wrap), sits in the text
  // next to it (content.inlineWith), or the setting is absent: then it draws
  // as before.
  var IMAGE_LINE_BOX_KEYS = ['margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft', 'display'];
  function imageTextLine(el) {
    var content = el && el.content || {};
    if (content.onTextLine !== true || content.wrap === 'left' || content.wrap === 'right' || content.inlineWith) return null;
    var styles = pickStyles(el);
    var box = {};
    var own = {};
    for (var key in styles) {
      if (!Object.prototype.hasOwnProperty.call(styles, key)) continue;
      if (IMAGE_LINE_BOX_KEYS.indexOf(key) >= 0) box[key] = styles[key];
      else own[key] = styles[key];
    }
    var autoLeft = trimStr(box.marginLeft) === 'auto';
    var autoRight = trimStr(box.marginRight) === 'auto';
    if (autoLeft) delete box.marginLeft;
    if (autoRight) delete box.marginRight;
    delete box.display;
    if (autoLeft) box.textAlign = autoRight ? 'center' : 'right';
    var attr = function (obj) {
      var css = stylesToInline(obj);
      if (css && LOOK_DEPTH > 0) css = importantize(css);
      return css ? ' style="' + escapeStyleValue(css) + '"' : '';
    };
    // Inline on the picture itself: a native card's body unwraps the image box, so a
    // rule keyed on it would not reach the picture there.
    own.display = 'inline';
    own.verticalAlign = 'baseline';
    var emptyLines = parseInt(content.emptyLinesAfter, 10);
    emptyLines = isFinite(emptyLines) && emptyLines > 0 ? Math.min(emptyLines, 10) : 0;
    var linesBefore = parseInt(content.emptyLinesBefore, 10);
    linesBefore = isFinite(linesBefore) && linesBefore > 0 ? Math.min(linesBefore, 10) : 0;
    return { box: attr(box), img: attr(own), emptyLines: emptyLines, linesBefore: linesBefore };
  }

  renderers.image = function (el) {
    var content = el.content || {};
    var src = content.src || '';
    if (!src) return '';
    var alt = escapeAttr(content.alt || '');
    var caption = content.caption || '';
    // content.onTextLine: the picture on a line of text (imageTextLine()).
    var line = imageTextLine(el);
    if (line) {
      var lineHtml = '<div class="content-type-neo-image content-type-neo-image--line"><p class="neo-image-line"' + line.box + '>';
      // content.emptyLinesBefore: that many empty lines of the paragraph before the picture's line.
      if (line.linesBefore > 0) lineHtml += new Array(line.linesBefore + 1).join('<br>');
      lineHtml += wrapInAuthoredLink(content,
        '<img src="' + escapeAttr(src) + '" alt="' + alt + '" loading="lazy"' + line.img + '>');
      // content.emptyLinesAfter: that many empty lines of the paragraph after the picture's line
      // (a break ends the picture's line, each further break one empty line).
      if (line.emptyLines > 0) lineHtml += new Array(line.emptyLines + 2).join('<br>');
      lineHtml += '</p>';
      if (caption) lineHtml += '<span class="caption">' + caption + '</span>';
      return lineHtml + '</div>';
    }
    // Inline styles go on the <img> — no legacy .image-container wrapper (forced max-width:75%).
    var html = '<div class="content-type-neo-image">';
    html += wrapInAuthoredLink(content,
      withImageWrap(content, '<img src="' + escapeAttr(src) + '" alt="' + alt + '" loading="lazy"' + inlineStyleAttr(el) + '>'));
    if (caption) {
      html += '<span class="caption">' + caption + '</span>';
    }
    html += '</div>';
    return html;
  };

  renderers.divider = function (el) {
    return '<div class="content-type-divider content-type-neo-divider"><hr' + inlineStyleAttr(el) + '></div>';
  };

  // ----- video helpers -----

  function youtubeId(url) {
    if (!url) return '';
    var m = String(url).match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : '';
  }

  function vimeoId(url) {
    if (!url) return '';
    var m = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : '';
  }

  // Wistia: the media id (ten lower-case letters and digits) from `mediaId`, else from a Wistia
  // URL in `src`. Played through Wistia's iframe player; no Wistia script is loaded into the page.
  function wistiaId(content) {
    var stored = String(content.mediaId || '');
    if (/^[a-z0-9]{10}$/.test(stored)) return stored;
    var m = String(content.src || '').match(/^https:\/\/[a-z0-9-]+\.wistia\.(?:com|net)\/(?:medias|embed\/medias|embed\/iframe)\/([a-z0-9]{10})(?:[\/?#.]|$)/);
    return m ? m[1] : '';
  }

  function parseAspectRatio(s, fallback) {
    if (!s) return fallback;
    var str = String(s).replace(':', '/');
    if (/^\d+(\.\d+)?(\/\d+(\.\d+)?)?$/.test(str)) return str;
    return fallback;
  }

  renderers.youtube = function (el) {
    var content = el.content || {};
    var id = content.videoId || youtubeId(content.src || '');
    if (!id) return '';
    var aspect = parseAspectRatio(content.aspectRatio, '16/9');
    var title = escapeAttr(content.videoTitle || 'YouTube video');
    var iframe = '<iframe title="' + title + '" src="https://www.youtube.com/embed/' + escapeAttr(id) +
      '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
    return '<div class="content-type-neo-youtube"' + inlineStyleAttr(el) + '>' +
      '<div class="neo-video__frame" style="aspect-ratio:' + aspect + ';">' + iframe + '</div>' +
      '</div>';
  };

  renderers.video = function (el) {
    var content = el.content || {};
    var src = content.src || '';
    var aspect = parseAspectRatio(content.aspectRatio, '16/9');
    var type = content.type || 'custom';
    // A Wistia media id is a video on its own, without a src, as neo-el-video.liquid and the PHP renderer draw it.
    if (!src && type !== 'wistia') return '';
    var inner;
    if (type === 'youtube') {
      var yid = youtubeId(src);
      if (!yid) return '';
      inner = '<iframe src="https://www.youtube.com/embed/' + escapeAttr(yid) +
        '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
    } else if (type === 'vimeo') {
      var vid = vimeoId(src);
      if (!vid) return '';
      inner = '<iframe src="https://player.vimeo.com/video/' + escapeAttr(vid) +
        '" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    } else if (type === 'wistia') {
      var wid = wistiaId(content);
      if (!wid) return '';
      inner = '<iframe src="https://fast.wistia.net/embed/iframe/' + escapeAttr(wid) +
        '?seo=true&amp;videoFoam=true" title="' + escapeAttr(content.title || 'Video') +
        '" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>';
    } else {
      inner = '<video src="' + escapeAttr(src) + '" controls></video>';
    }
    return '<div class="content-type-neo-video"' + inlineStyleAttr(el) + '>' +
      '<div class="neo-video__frame" style="aspect-ratio:' + aspect + ';">' + inner + '</div>' +
      '</div>';
  };

  renderers.gallery = function (el) {
    var content = el.content || {};
    var rawImages = Array.isArray(content.images) ? content.images : [];
    var images = [];
    for (var i = 0; i < rawImages.length; i++) {
      if (rawImages[i] && rawImages[i].src) images.push(rawImages[i]);
    }
    if (images.length === 0) return '';

    var cols = parseInt(content.columns, 10);
    if (!isFinite(cols) || cols < 1) cols = 3;
    if (cols > 6) cols = 6;

    var variant = content.layoutVariant
      ? ' content-type-neo-gallery--' + escapeAttr(String(content.layoutVariant))
      : '';

    var apiStyles = USE_API_INLINE_STYLES ? stylesToInline(pickStyles(el)) : '';
    var gridCols = 'grid-template-columns:repeat(' + cols + ',1fr)';
    var combined = apiStyles ? apiStyles + ';' + gridCols : gridCols;

    var html = '<div class="content-type-neo-gallery' + variant + '" style="' + escapeStyleValue(combined) + '">';
    for (var k = 0; k < images.length; k++) {
      var img = images[k];
      html += '<img src="' + escapeAttr(img.src) + '" alt="' + escapeAttr(img.alt || '') + '" loading="lazy">';
    }
    html += '</div>';
    return html;
  };

  function renderMediaGalleryItem(file, showCaptions) {
    var src = file.optimizedImageUrl || file.url;
    var t = pickTranslation(file.translations);
    var alt = escapeAttr(t.alternativeText || t.name || '');
    var caption = t.caption || '';
    var html = '<figure class="neo-media-gallery__item">';
    html += '<img src="' + escapeAttr(src) + '" alt="' + alt + '" loading="lazy">';
    if (showCaptions && caption) {
      html += '<figcaption class="neo-media-gallery__caption">' + escapeAttr(caption) + '</figcaption>';
    }
    html += '</figure>';
    return html;
  }

  renderers['media-gallery'] = function (el) {
    var content = el.content || {};
    var rawFiles = Array.isArray(content.files) ? content.files : [];
    if (rawFiles.length === 0) return '';

    var files = rawFiles.slice().sort(function (a, b) {
      var ao = (a && typeof a.order === 'number') ? a.order : 0;
      var bo = (b && typeof b.order === 'number') ? b.order : 0;
      return ao - bo;
    }).filter(function (f) {
      return f && (f.url || f.optimizedImageUrl);
    });
    if (files.length === 0) return '';

    var cols = parseInt(content.columns, 10);
    if (!isFinite(cols) || cols < 1) cols = 3;
    if (cols > 6) cols = 6;

    var layout = (content.layout || 'grid').toLowerCase();
    if (layout !== 'grid' && layout !== 'masonry' && layout !== 'carousel') layout = 'grid';

    var showCaptions = content.showCaptions !== false;
    var subsectionTitle = content.subsectionTitle || '';
    var variantClass = ' content-type-neo-media-gallery--' + layout;

    var inner = '';
    if (layout === 'masonry') {
      inner += '<div class="neo-media-gallery__masonry" style="column-count:' + cols + ';">';
      for (var i = 0; i < files.length; i++) inner += renderMediaGalleryItem(files[i], showCaptions);
      inner += '</div>';
    } else if (layout === 'carousel') {
      var hasOverflow = files.length > cols;
      inner += '<div class="neo-media-gallery__carousel" data-neo-mg-carousel="1" data-cols="' + cols + '">';
      inner += '<div class="neo-media-gallery__carousel-track" style="--neo-mg-cols:' + cols + ';">';
      for (var j = 0; j < files.length; j++) inner += renderMediaGalleryItem(files[j], showCaptions);
      inner += '</div>';
      if (hasOverflow) {
        inner += '<button type="button" class="neo-media-gallery__nav neo-media-gallery__nav--prev" aria-label="Previous">&#10094;</button>';
        inner += '<button type="button" class="neo-media-gallery__nav neo-media-gallery__nav--next" aria-label="Next">&#10095;</button>';
        inner += '<div class="neo-media-gallery__scrubber" style="position:relative;height:6px;background:#d0d0d0;border-radius:3px;margin-top:16px;cursor:pointer;touch-action:none;user-select:none;overflow:hidden;">';
        inner += '<div class="neo-media-gallery__scrubber-thumb" style="position:absolute;top:0;left:0;height:100%;width:60px;min-width:60px;background:#333;border-radius:3px;pointer-events:none;display:block;"></div>';
        inner += '</div>';
      }
      inner += '</div>';
    } else {
      inner += '<div class="neo-media-gallery__grid" style="grid-template-columns:repeat(' + cols + ',1fr);">';
      for (var k = 0; k < files.length; k++) inner += renderMediaGalleryItem(files[k], showCaptions);
      inner += '</div>';
    }

    var html = '<div class="content-type-neo-media-gallery' + variantClass + '"' + inlineStyleAttr(el) + '>';
    if (subsectionTitle) html += '<h3 class="neo-media-gallery__title">' + subsectionTitle + '</h3>';
    html += inner;
    html += '</div>';
    return html;
  };

  renderers.slideshow = function (el) {
    var content = el.content || {};
    var rawSlides = Array.isArray(content.slides) ? content.slides : [];
    var slides = [];
    for (var i = 0; i < rawSlides.length; i++) {
      if (rawSlides[i] && rawSlides[i].src) slides.push(rawSlides[i]);
    }
    if (slides.length === 0) return '';

    var autoplay = content.autoplay === true;
    var interval = parseInt(content.autoplayInterval, 10);
    if (!isFinite(interval) || interval < 1000) interval = 5000;
    var showDots = content.showDots !== false;
    var showArrows = content.showArrows !== false;
    var aspect = parseAspectRatio(content.aspectRatio, '16/9');

    var html = '<div class="content-type-text content-type-neo-slideshow"' +
      ' data-neo-slideshow="1"' +
      ' data-autoplay="' + (autoplay ? '1' : '0') + '"' +
      ' data-interval="' + interval + '"' +
      inlineStyleAttr(el) + '>';
    html += '<div class="neo-slideshow__viewport" style="aspect-ratio:' + aspect + ';">';
    html += '<div class="neo-slideshow__track">';
    for (var s = 0; s < slides.length; s++) {
      var slide = slides[s];
      html += '<div class="neo-slideshow__slide">';
      html += '<img src="' + escapeAttr(slide.src) + '" alt="' + escapeAttr(slide.alt || '') + '" loading="lazy">';
      if (slide.headline || (slide.buttonText && slide.buttonUrl)) {
        html += '<div class="neo-slideshow__caption">';
        if (slide.headline) html += '<h3 class="neo-slideshow__headline" style="color:#fff !important;font-size:28px !important;font-weight:600 !important;line-height:1.2 !important;margin:0 !important;">' + escapeAttr(slide.headline) + '</h3>';
        var btnUrl = normalizeUrl(slide.buttonUrl);
        if (slide.buttonText && btnUrl) {
          html += '<a class="neo-slideshow__btn" href="' + escapeAttr(btnUrl) + '">' + escapeAttr(slide.buttonText) + '</a>';
        }
        html += '</div>';
      }
      html += '</div>';
    }
    html += '</div>'; // track
    if (showArrows && slides.length > 1) {
      html += '<button type="button" class="neo-slideshow__nav neo-slideshow__nav--prev" aria-label="Previous slide">&#10094;</button>';
      html += '<button type="button" class="neo-slideshow__nav neo-slideshow__nav--next" aria-label="Next slide">&#10095;</button>';
    }
    html += '</div>'; // viewport
    if (showDots && slides.length > 1) {
      html += '<div class="neo-slideshow__dots">';
      for (var d = 0; d < slides.length; d++) {
        html += '<button type="button" class="neo-slideshow__dot" data-idx="' + d + '" aria-label="Go to slide ' + (d + 1) + '"></button>';
      }
      html += '</div>';
    }
    html += '</div>';
    return html;
  };

  // ============================================================
  // Text-level escape for author strings the renderer emits as TEXT.
  //
  // Mirrors the backend renderer's e() (htmlspecialchars, ENT_QUOTES): the
  // types below take their copy from plain-string content fields, so it must
  // not be able to open a tag. escapeAttr covers & " < > already; the extra
  // apostrophe rule keeps this identical to the PHP side, so the two
  // renderers cannot drift on the same input.
  // ============================================================

  function escapeHtml(s) {
    if (s == null) return '';
    return escapeAttr(s).replace(/'/g, '&#039;');
  }

  // Plain text of an HTML fragment — for <summary>/label slots, where the
  // backend applies strip_tags + html_entity_decode before escaping.
  function plainText(html) {
    if (html == null) return '';
    var s = String(html).replace(/<[^>]*>/g, ' ');
    // One decoding pass, as html_entity_decode on the server and decodeEntities() in the
    // aico engines: numeric entities (&#252;, &#xFC;) and the basic named ones.
    s = decodeEntities(s);
    // Any other named entity (&uuml;, &auml;, ...) through an inert <textarea>, when there
    // is a document; without one it stays as written.
    if (/&[a-zA-Z][a-zA-Z0-9]*;/.test(s) && typeof document !== 'undefined' && document.createElement) {
      try {
        var ta = document.createElement('textarea');
        s = s.replace(/&[a-zA-Z][a-zA-Z0-9]*;/g, function (entity) { ta.innerHTML = entity; return ta.value; });
      } catch (e) { /* keep as written */ }
    }
    return s.replace(/\s+/g, ' ').trim();
  }


  function trimStr(v) {
    return v == null ? '' : String(v).trim();
  }

  // `content.items`-style lists, defensively: anything that is not an array of
  // objects degrades to an empty list rather than throwing.
  function listOf(value) {
    if (!Array.isArray(value)) return [];
    var out = [];
    for (var i = 0; i < value.length; i++) {
      if (value[i] && typeof value[i] === 'object') out.push(value[i]);
    }
    return out;
  }

  /**
   * A field the builder treats as PLAIN TEXT but which production documents
   * sometimes hold HTML in.
   *
   * The legacy→neo converter copies the old CKEditor columns verbatim
   * (LegacyContentBuilderToNeoConverter::mapFaq → `'answer' => $answer`), so
   * FC St.Gallen's converted FAQ answers are full `<p>…</p>` fragments — while
   * aiconeo renders `{item.answer}` as a React text node and the Shopify body
   * renderer runs it through e(). All three therefore print the tags at the
   * reader: FCSG news 7488 shows a literal "<p>Wir verkaufen die Tickets…".
   *
   * This is a DELIBERATE, NARROW divergence from those two: a value whose
   * whole payload is a well-formed block-level fragment is emitted verbatim,
   * anything else is escaped exactly as before. It is the only place this file
   * knowingly renders differently from the backend, and it exists because the
   * alternative is shipping visible markup to a club's supporters.
   *
   * The real fix is upstream — the converter should emit `accordion`, whose
   * item `content` IS rich text on all three sides, or the field should be
   * HTML everywhere. Delete this helper when that lands.
   */
  function richTextOrEscaped(value) {
    var s = trimStr(value);
    if (!s) return '';
    // Conservative: must OPEN with a block tag and CLOSE with one, so a
    // sentence that merely mentions "<" or an angle bracket stays escaped.
    if (/^<(p|div|ul|ol|h[1-6]|table|blockquote|figure|section|span|strong|em|a)\b[^>]*>/i.test(s)
      && /<\/(p|div|ul|ol|h[1-6]|table|blockquote|figure|section|span|strong|em|a)>$/i.test(s)) {
      return s;
    }
    return escapeHtml(s);
  }

  // Shared <figure> for the gallery-family elements (mirrors the backend's
  // renderFigure).
  function renderFigure(src, alt, caption) {
    var source = normalizeUrl(trimStr(src));
    if (!source) return '';
    var html = '<figure><img src="' + escapeAttr(source) + '" alt="' + escapeAttr(alt || '') + '" loading="lazy">';
    if (trimStr(caption)) html += '<figcaption>' + escapeHtml(trimStr(caption)) + '</figcaption>';
    return html + '</figure>';
  }

  // ============================================================
  // Structural containers
  //
  // The engine handled `columns` at SECTION level only (renderSection) and had
  // no `container` at all, so an unknown wrapper returned '' — INCLUDING its
  // children. Both are handled here, which also makes a nested `columns`
  // reachable for the first time.
  // ============================================================

  function childrenOf(el) {
    // The builder accepts both keys; AI-generated documents use either.
    if (Array.isArray(el.children)) return el.children;
    if (Array.isArray(el.elements)) return el.elements;
    return [];
  }

  function renderChildren(el) {
    var kids = childrenOf(el);
    var out = '';
    for (var i = 0; i < kids.length; i++) out += renderElement(kids[i]);
    return out;
  }

  // `layout: 'grid'` (aico registry section 7.2): the children in an
  // equal-column grid of `gridColumns` that drops to `gridColumnsTablet` at
  // max-width 900px and `gridColumnsMobile` at max-width 640px. The desktop
  // grid is inline; the two narrower ones come from one <style> keyed on the
  // class `.neo-container--grid-<n>-<t>-<m>` (the same text for every
  // container with those counts, so repeating it is harmless). A container
  // without `layout: 'grid'` is drawn exactly as before.
  function gridCount(value, fallback) {
    var n = parseInt(value, 10);
    return isFinite(n) && n >= 1 && n <= 6 ? n : fallback;
  }

  function renderGridContainer(el, inner) {
    var content = el.content || {};
    var n = gridCount(content.gridColumns, 2);
    var t = gridCount(content.gridColumnsTablet, n);
    var m = gridCount(content.gridColumnsMobile, 1);
    var template = trimStr(content.gridTemplate);
    if (!/^[0-9a-z().,%\s-]+$/i.test(template)) template = '';
    var gap = lookLength(content.gridGap) || '16px';
    var cls = 'neo-container--grid-' + n + '-' + t + '-' + m;
    var repeat = function (count) { return 'repeat(' + count + ',minmax(0,1fr))'; };
    var picked = pickStyles(el);
    var ownStyles = {};
    for (var key in picked) {
      if (!Object.prototype.hasOwnProperty.call(picked, key)) continue;
      if (key === 'display' || key === 'gridTemplateColumns' || key === 'gap') continue;
      ownStyles[key] = picked[key];
    }
    var own = stylesToInline(ownStyles);
    // The grid itself is not marked !important: the narrower media rules have to win over it.
    var style = (own ? importantize(own) + ';' : '') + 'display:grid;grid-template-columns:' + (template || repeat(n)) + ';gap:' + gap;
    var media = '<style>@media (max-width:900px){.' + cls + '{grid-template-columns:' + repeat(t) + '!important}}' +
      '@media (max-width:640px){.' + cls + '{grid-template-columns:' + repeat(m) + '!important}}</style>';
    return media + '<div class="content-type-neo-container neo-look ' + cls + '" style="' + escapeStyleValue(style) + '">' + inner + '</div>';
  }

  // Container `content.linkUrl` + `openInNewTab` (native box link): the whole box is one link, as a
  // legacy `<a>` drawn around a box of pictures was (an app-store box: its QR code and its badge are
  // ONE link, not one per picture). The box is drawn as that `<a>`, a block unless its own styles lay
  // it out otherwise; normalizeUrl drops javascript:/vbscript: addresses. No link: drawn as before.
  function containerLinkAttrs(content) {
    var url = normalizeUrl(trimStr(content && content.linkUrl));
    if (!url) return '';
    return ' href="' + escapeAttr(url) + '"' + (content.openInNewTab === true ? ' target="_blank" rel="noopener noreferrer"' : '');
  }

  function containerStyle(own, isLink) {
    if (!isLink || /(^|;)\s*display\s*:/.test(own)) return own;
    return own ? own + ';display:block' : 'display:block';
  }

  renderers.container = function (el) {
    var content = el.content || {};
    var isIsland = content.drawsBox === true || content.layout === 'grid';
    var linkAttrs = containerLinkAttrs(content);
    var tag = linkAttrs ? 'a' : 'div';
    if (!isIsland) {
      var inner = renderChildren(el);
      if (!inner) return '';
      if (!linkAttrs) return '<div class="content-type-neo-container"' + inlineStyleAttr(el) + '>' + inner + '</div>';
      var plain = containerStyle(stylesToInline(pickStyles(el)), true);
      return '<a' + linkAttrs + ' class="content-type-neo-container" style="' + escapeStyleValue(plain) + '">' + inner + '</a>';
    }
    var islandInner;
    LOOK_DEPTH++;
    try { islandInner = renderChildren(el); } finally { LOOK_DEPTH--; }
    // An empty drawsBox container is a grid cell kept empty on purpose
    // (registry 8.4): drawn as its box so the cells after it keep their columns.
    if (!islandInner && content.drawsBox === true && childrenOf(el).length === 0) islandInner = '\n';
    if (!islandInner) return '';
    islandInner = importantizeTypographyAttributes(islandInner);
    if (content.layout === 'grid') return renderGridContainer(el, islandInner);
    var own = stylesToInline(pickStyles(el));
    var boxStyle = own ? importantize(own) : '';
    if (linkAttrs) boxStyle = containerStyle(boxStyle, true);
    return '<' + tag + linkAttrs + ' class="content-type-neo-container neo-look"' + (boxStyle ? ' style="' + escapeStyleValue(boxStyle) + '"' : '') + '>' + islandInner + '</' + tag + '>';
  };

  // Nested `columns` — same bucketing as renderColumnsRow, but emitting the
  // row/column classes directly instead of a section row (a nested columns is
  // not a section row and must not renumber them).
  // A row keeps its real column count. The theme lays out total-columns-2..4 side by side and has
  // no rule for 5 or more, so such a row stacks its columns one under the other: that is what the
  // legacy builder's `total-columns-5` rows show on the live product pages, and what this must
  // match. Folding a 5th column into the 4th (the old clamp) drew a 4-column grid with one column
  // twice as tall, which the live page never showed. The cap only bounds a malformed count.
  var MAX_COLUMNS = 12;

  function renderNestedColumns(el) {
    var content = el.content || {};
    var count = parseInt(content.columnCount, 10);
    if (!isFinite(count) || count < 1) count = 2;
    if (count > MAX_COLUMNS) count = MAX_COLUMNS;

    var children = childrenOf(el);
    var buckets = [];
    for (var c = 0; c < count; c++) buckets.push([]);

    var fallbackIdx = 0;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      var ci = (child && child.content && typeof child.content.columnIndex === 'number')
        ? child.content.columnIndex
        : fallbackIdx++;
      if (ci < 0) ci = 0;
      if (ci >= count) ci = count - 1;
      buckets[ci].push(child);
    }

    var columnsHtml = '';
    var any = false;
    for (var col = 0; col < count; col++) {
      var inner = '';
      for (var k = 0; k < buckets[col].length; k++) inner += renderElement(buckets[col][k]);
      if (inner) any = true;
      columnsHtml += '<div class="content-column column__' + (col + 1) + '">' + inner + '</div>';
    }
    if (!any) return '';

    // content.columnRatio: the row's own grid tracks (columnRatioTemplate()), before the
    // element's own styles so a template set in the Style tab still wins. A nested row's tracks
    // never grow past their share for a wide picture (minmax(0, …)), as the legacy row's
    // percentage columns did not.
    var template = columnRatioTemplate(content, count).replace(/minmax\(auto, /g, 'minmax(0, ');
    var styleAttr = inlineStyleAttr(el);
    if (template) {
      styleAttr = styleAttr
        ? styleAttr.replace(/^ style="/, ' style="grid-template-columns:' + template + ';')
        : ' style="grid-template-columns:' + template + '"';
    }
    // data-neo-columns: a nested `columns` element, not a legacy builder row. Inside a card the
    // product tab's stylesheet draws every `.content-row .content-column` as a category tile
    // (a quarter wide, framed, rounded, clipped); the rules below give a nested columns row its
    // own equal columns there instead (NEO_STYLES, "nested columns in a card").
    return '<div class="content-row total-columns-' + count + '" data-neo-columns' + styleAttr + '>' +
      columnsHtml + '</div>';
  }

  // ============================================================
  // Text-level types the backend body renderer also emits
  // (Modules/LandingPages/Service/ShopifySync/NeoLandingPageBodyRenderer.php).
  // Markup and classes mirror each PHP arm so an article looks the same
  // wherever it is rendered.
  // ============================================================

  // The Button tab's own keys (aico registry section 7.2). Only a button that
  // carries one of them is drawn through these; every other button is drawn as
  // before.
  var BUTTON_APPEARANCE_KEYS = ['buttonVariant', 'buttonShape', 'buttonBgColor', 'buttonTextColor', 'buttonBorderColor', 'buttonBorderWidth'];

  function hasButtonAppearance(content) {
    for (var i = 0; i < BUTTON_APPEARANCE_KEYS.length; i++) {
      var v = content[BUTTON_APPEARANCE_KEYS[i]];
      if (v !== undefined && v !== null && v !== '') return true;
    }
    return false;
  }

  /** Port of aico NeoLandingPageBodyRenderer::buttonAppearanceDeclarations(). */
  function buttonAppearanceDeclarations(content) {
    var out = '';
    // Own keys only: "constructor" or "toString" must not reach Object.prototype.
    var own = Object.prototype.hasOwnProperty;
    var radii = { square: '0px', rounded: '8px', pill: '9999px' };
    var shape = trimStr(content.buttonShape);
    if (own.call(radii, shape)) out += ';border-radius:' + radii[shape];
    var sizes = { sm: ['8px 16px', '13px'], md: ['12px 24px', '15px'], lg: ['16px 32px', '17px'], xl: ['20px 40px', '19px'] };
    var size = trimStr(content.buttonSize);
    if (own.call(sizes, size)) out += ';padding:' + sizes[size][0] + ';font-size:' + sizes[size][1];

    var variant = trimStr(content.buttonVariant) || 'filled';
    var background = lookColor(content.buttonBgColor);
    var text = lookColor(content.buttonTextColor);
    var borderColour = lookColor(content.buttonBorderColor);
    var rawWidth = content.buttonBorderWidth;
    var borderWidth = null;
    if (typeof rawWidth === 'number' && isFinite(rawWidth)) borderWidth = rawWidth;
    else if (typeof rawWidth === 'string' && /^\s*\d+(\.\d+)?\s*$/.test(rawWidth)) borderWidth = parseFloat(rawWidth);
    if (borderWidth !== null) borderWidth = Math.min(16, Math.max(0, Math.round(borderWidth)));

    if (variant === 'outline' || variant === 'link') out += ';background-color:transparent';
    else if (background) out += ';background-color:' + background;

    if (text) out += ';color:' + text;
    else if (variant === 'outline' && borderColour) out += ';color:' + borderColour;

    if (variant === 'outline') {
      var width = (borderWidth === null || borderWidth === 0) ? 2 : borderWidth;
      out += ';border:' + width + 'px solid ' + (borderColour || text || 'currentColor');
    } else if (borderWidth !== null) {
      out += borderWidth === 0 ? ';border:none' : ';border:' + borderWidth + 'px solid ' + (borderColour || 'currentColor');
    }
    if (content.buttonFullWidth === true) out += ';display:block;width:100%;text-align:center';
    return out;
  }

  renderers.button = function (el) {
    var content = el.content || {};
    // The label is rich text in the builder but a plain <a> label here, so it
    // is flattened the way the backend flattens it (strip_tags + decode).
    var text = plainText(content.text);
    var url = normalizeUrl(trimStr(content.url));
    if (!text || !url) return '';
    var target = content.openInNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
    if (hasButtonAppearance(content)) {
      // The Button tab's look and the element's own styles on the <a>, the box
      // the author styled (aico NeoLandingPageBodyRenderer::renderButton).
      var look = buttonAppearanceDeclarations(content) + ';' + stylesToInline(pickStyles(el)) + ';text-decoration:none';
      look = importantize(look.replace(/^;+|;+$/g, '').replace(/;;+/g, ';'));
      return '<div class="content-type-link content-type-neo-button neo-look">' +
        '<a href="' + escapeAttr(url) + '" class="button button--primary"' + target +
        (look ? ' style="' + escapeStyleValue(look) + '"' : '') + '>' + escapeHtml(text) + '</a>' +
        '</div>';
    }
    return '<div class="content-type-link content-type-neo-button"' + inlineStyleAttr(el) + '>' +
      '<a href="' + escapeAttr(url) + '" class="button button--primary"' + target + '>' + escapeHtml(text) + '</a>' +
      '</div>';
  };

  renderers.spacer = function (el) {
    // The builder stores the height in styles.desktop (content is empty by
    // design); the palette default is 48px for spacers never touched.
    var styles = pickStyles(el);
    var height = typeof styles.height === 'string' ? styles.height.trim() : '';
    if (!/^\d+(\.\d+)?(px|rem|em|vh|vw|%)$/.test(height)) height = '48px';
    return '<div class="content-type-neo-spacer" style="height:' + escapeAttr(height) + '" aria-hidden="true"></div>';
  };

  renderers.badge = function (el) {
    var content = el.content || {};
    var text = trimStr(content.text);
    if (!text) return '';
    return '<div class="content-type-neo-badge"' + inlineStyleAttr(el) + '>' +
      '<span class="badge">' + escapeHtml(text) + '</span></div>';
  };

  renderers.features = function (el) {
    var content = el.content || {};
    var items = '';
    var list = listOf(content.items);
    for (var i = 0; i < list.length; i++) {
      var title = trimStr(list[i].title);
      var description = trimStr(list[i].description);
      if (!title && !description) continue;
      var itemHtml = '<div class="feature">';
      var image = normalizeUrl(trimStr(list[i].image));
      if (image) itemHtml += '<img src="' + escapeAttr(image) + '" alt="' + escapeAttr(title) + '" loading="lazy">';
      if (title) itemHtml += '<h3>' + escapeHtml(title) + '</h3>';
      if (description) itemHtml += '<p>' + escapeHtml(description) + '</p>';
      items += itemHtml + '</div>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-features"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  renderers.testimonial = function (el) {
    var content = el.content || {};
    var list = listOf(content.items);
    if (list.length === 0) {
      // Legacy flat shape folded into a one-item list, like the builder does.
      list = [{
        quote: content.quote, author: content.author, role: content.role,
        avatar: content.avatar, rating: content.rating
      }];
    }
    var showRating = content.testimonialShowRating !== false;
    var showAvatar = content.testimonialShowAvatar !== false;

    var items = '';
    for (var i = 0; i < list.length; i++) {
      var quote = trimStr(list[i].quote);
      var author = trimStr(list[i].author);
      if (!quote && !author) continue;

      var itemHtml = '<blockquote class="testimonial">';
      var avatar = normalizeUrl(trimStr(list[i].avatar));
      if (showAvatar && avatar) {
        itemHtml += '<img class="avatar" src="' + escapeAttr(avatar) + '" alt="' + escapeAttr(author) + '" loading="lazy">';
      }
      if (quote) itemHtml += '<p>&ldquo;' + escapeHtml(quote) + '&rdquo;</p>';

      var rating = parseInt(list[i].rating, 10);
      if (showRating && isFinite(rating) && rating > 0) {
        var stars = '';
        for (var s = 0; s < Math.min(rating, 5); s++) stars += '&#9733;';
        itemHtml += '<span class="rating" aria-label="' + Math.min(rating, 5) + '/5">' + stars + '</span>';
      }
      if (author) {
        var role = trimStr(list[i].role);
        itemHtml += '<footer><cite>' + escapeHtml(author) + '</cite>' +
          (role ? '<span class="role">' + escapeHtml(role) + '</span>' : '') + '</footer>';
      }
      items += itemHtml + '</blockquote>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-testimonial"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  renderers['partner-logos'] = function (el) {
    var content = el.content || {};
    var items = '';
    var list = listOf(content.logos);
    for (var i = 0; i < list.length; i++) {
      var name = trimStr(list[i].name);
      var src = normalizeUrl(trimStr(list[i].src));
      var inner;
      if (src) inner = '<img src="' + escapeAttr(src) + '" alt="' + escapeAttr(name) + '" loading="lazy">';
      else if (name) inner = '<span>' + escapeHtml(name) + '</span>';
      else continue;

      var url = normalizeUrl(trimStr(list[i].url));
      if (/^https?:\/\//i.test(url)) {
        inner = '<a href="' + escapeAttr(url) + '" target="_blank" rel="noopener noreferrer">' + inner + '</a>';
      }
      items += '<div class="partner-logo">' + inner + '</div>';
    }
    if (!items) return '';
    var headline = trimStr(content.headline);
    return '<div class="content-type-neo-partner-logos"' + inlineStyleAttr(el) + '>' +
      (headline ? '<h2>' + escapeHtml(headline) + '</h2>' : '') +
      '<div class="logos">' + items + '</div></div>';
  };

  // <details>/<summary>: an accordion without script is exactly a details list,
  // which is what the backend body renderer emits too. Item bodies are
  // author-edited rich text and ship verbatim like the text element; titles go
  // inside <summary>, where block markup is flattened to its text.
  renderers.accordion = function (el) {
    var content = el.content || {};
    var items = '';
    var list = listOf(content.items);
    var pill = accordionPillLook(content);
    for (var i = 0; i < list.length; i++) {
      var title = plainText(list[i].title);
      var body = trimStr(list[i].content);
      if (!title && !body) continue;
      if (pill) {
        items += '<details class="neo-accordion--pill"><summary' + pill.summary + '>' + escapeHtml(title) + pill.badge + '</summary>' +
          '<div class="accordion-content"' + pill.answer + '>' + body + '</div></details>';
        continue;
      }
      items += '<details><summary>' + escapeHtml(title) + '</summary>' +
        '<div class="accordion-content">' + body + '</div></details>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-accordion"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  // Accordion `itemStyle: 'pill'` (aico native settings contract v2, accordion
  // pill look): each title drawn as a rounded pill that holds its marker, each
  // answer as a framed box below it, no divider line and no browser triangle.
  // Colours come from the element's own settings: questionColor,
  // questionBackground, questionBorderColor; iconStyle 'badge' draws the
  // marker as a ▾ in a filled circle (iconColor, iconBackground);
  // answerColor, answerBackground, answerBorderColor. Colours are written
  // with !important, as the product tab forces the colour of every box; the
  // sizes of the words are left to the page. Null for any other itemStyle,
  // so an accordion without it renders exactly as before.
  function accordionPillLook(content) {
    if (!content || content.itemStyle !== 'pill') return null;
    var style = function (pairs) {
      var css = [];
      for (var i = 0; i < pairs.length; i++) {
        var value = lookColor(pairs[i][1]);
        if (value) css.push(pairs[i][0] + ':' + value + '!important');
      }
      return css.length ? ' style="' + escapeStyleValue(css.join(';')) + '"' : '';
    };
    var questionColor = lookColor(content.questionColor);
    var summary = style([['color', questionColor], ['-webkit-text-fill-color', questionColor],
      ['background-color', content.questionBackground], ['border-color', content.questionBorderColor]]);
    var badge = '';
    if (content.iconStyle === 'badge') {
      var iconColor = lookColor(content.iconColor);
      badge = ' <span class="neo-accordion__badge" aria-hidden="true"' +
        style([['color', iconColor], ['-webkit-text-fill-color', iconColor], ['background-color', content.iconBackground]]) + '>&#9662;</span>';
    }
    var answerColor = lookColor(content.answerColor);
    var answer = style([['color', answerColor], ['-webkit-text-fill-color', answerColor],
      ['background-color', content.answerBackground], ['border-color', content.answerBorderColor]]);
    return { summary: summary, badge: badge, answer: answer };
  }

  renderers.faq = function (el) {
    var content = el.content || {};
    var items = '';
    var list = listOf(content.items);
    for (var i = 0; i < list.length; i++) {
      var q = trimStr(list[i].question);
      var a = trimStr(list[i].answer);
      if (!q && !a) continue;
      // The question is a text node everywhere and stays escaped; the answer
      // may be converted CKEditor HTML — see richTextOrEscaped.
      //
      // A question-less item is a DATA defect (FCSG article 7488 stores six
      // items whose `question` is ""), but a closed <details> with an empty
      // <summary> turns that into six bare arrows with the text hidden behind
      // them. Render it open instead: no label is invented, and nothing the
      // document holds is hidden from the reader. The flag is in the DOM so
      // the authoring side can find them.
      var noQuestion = !q;
      items += '<details' + (noQuestion ? ' open data-neo-no-question' : '') + '>' +
        '<summary>' + escapeHtml(q) + '</summary>' +
        '<div class="faq-answer">' + richTextOrEscaped(a) + '</div></details>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-faq"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  renderers['feature-accordion'] = function (el) {
    var content = el.content || {};
    var items = '';
    var list = listOf(content.items);
    for (var i = 0; i < list.length; i++) {
      var title = trimStr(list[i].title);
      var body = trimStr(list[i].content);
      if (!title && !body) continue;
      items += '<details><summary>' + escapeHtml(title) + '</summary>' +
        '<div class="accordion-content">' + richTextOrEscaped(body) + '</div></details>';
    }
    if (!items) return '';

    var heading = trimStr(content.heading);
    var html = '<div class="content-type-neo-feature-accordion"' + inlineStyleAttr(el) + '>';
    if (heading) html += '<h2>' + escapeHtml(heading) + '</h2>';
    html += '<div class="items">' + items + '</div>';

    var image = normalizeUrl(trimStr(content.image));
    if (content.featureAccordionShowImage !== false && image) {
      html += '<img src="' + escapeAttr(image) + '" alt="' + escapeAttr(heading) + '" loading="lazy">';
    }
    return html + '</div>';
  };

  renderers['before-after'] = function (el) {
    var content = el.content || {};
    // The drag handle is script; the content is the two labelled images. They
    // are shown side by side, the same degradation the backend body uses.
    var pairs = [['beforeSrc', 'beforeLabel'], ['afterSrc', 'afterLabel']];
    var panels = '';
    for (var i = 0; i < pairs.length; i++) {
      var src = normalizeUrl(trimStr(content[pairs[i][0]]));
      if (!src) continue;
      var label = trimStr(content[pairs[i][1]]);
      panels += '<figure><img src="' + escapeAttr(src) + '" alt="' + escapeAttr(label) + '" loading="lazy">' +
        (label ? '<figcaption>' + escapeHtml(label) + '</figcaption>' : '') + '</figure>';
    }
    if (!panels) return '';
    return '<div class="content-type-neo-before-after"' + inlineStyleAttr(el) + '>' + panels + '</div>';
  };

  // Google Maps as a static iframe. The src is only ever a Google Maps URL the
  // author supplied, or one BUILT from the address — never an arbitrary URL.
  renderers.map = function (el) {
    var content = el.content || {};
    var embedUrl = trimStr(content.embedUrl);
    var address = trimStr(content.address);

    var src = '';
    if (/^https:\/\/(www\.)?(google\.[a-z.]{2,6}\/maps|maps\.google\.[a-z.]{2,6})/i.test(embedUrl)) {
      src = embedUrl;
    } else if (address) {
      var zoom = parseInt(content.zoom, 10);
      if (!isFinite(zoom)) zoom = 13;
      zoom = Math.max(1, Math.min(zoom, 21));
      src = 'https://www.google.com/maps?q=' + encodeURIComponent(address) + '&z=' + zoom + '&output=embed';
    }
    if (!src) return '';

    var height = parseInt(content.height, 10);
    if (!isFinite(height) || height < 100 || height > 2000) height = 400;

    return '<div class="content-type-neo-map" style="height:' + height + 'px">' +
      '<iframe src="' + escapeAttr(src) + '" title="Google Maps" width="100%" height="100%"' +
      ' style="border:0" loading="lazy" allowfullscreen referrerpolicy="no-referrer-when-downgrade"></iframe>' +
      '</div>';
  };

  renderers['social-embed'] = function (el) {
    var content = el.content || {};
    // Author-pasted platform markup: the builder injects it verbatim
    // (dangerouslySetInnerHTML), the same trust level as the html element.
    // NOTE: <script> inside innerHTML does not execute, so a platform embed
    // that needs its SDK shows the markup's static part only.
    var embedCode = trimStr(content.embedCode);
    if (embedCode) {
      return '<div class="content-type-neo-social-embed"' + inlineStyleAttr(el) + '>' + embedCode + '</div>';
    }
    var embedUrl = trimStr(content.embedUrl);
    if (!/^https:\/\//i.test(embedUrl)) return '';
    return '<div class="content-type-neo-social-embed"' + inlineStyleAttr(el) + '>' +
      '<iframe src="' + escapeAttr(embedUrl) + '" title="Social Media" width="100%" height="480"' +
      ' style="border:0" loading="lazy" allowfullscreen></iframe></div>';
  };

  renderers['job-listings'] = function (el) {
    var content = el.content || {};
    var showLocation = content.showLocation !== false;
    var showType = content.showType !== false;
    var showDescription = content.showDescription !== false;

    var items = '';
    var list = listOf(content.jobs);
    for (var i = 0; i < list.length; i++) {
      var title = trimStr(list[i].title);
      if (!title) continue;
      var itemHtml = '<div class="job"><h3>' + escapeHtml(title) + '</h3>';

      var location = trimStr(list[i].location);
      var jobType = trimStr(list[i].type);
      if ((showLocation && location) || (showType && jobType)) {
        itemHtml += '<p class="job-meta">' +
          (showLocation && location ? '<span class="location">' + escapeHtml(location) + '</span>' : '') +
          (showType && jobType ? '<span class="type">' + escapeHtml(jobType) + '</span>' : '') +
          '</p>';
      }

      var description = trimStr(list[i].description);
      if (showDescription && description) itemHtml += '<p>' + escapeHtml(description) + '</p>';

      var link = normalizeUrl(trimStr(list[i].buttonLink));
      if (link) {
        var label = trimStr(list[i].buttonText) || trimStr(content.buttonText);
        // A dead button helps nobody: only emitted when the job carries a link.
        if (label) {
          itemHtml += '<a href="' + escapeAttr(link) + '" class="button button--primary">' + escapeHtml(label) + '</a>';
        }
      }
      items += itemHtml + '</div>';
    }
    if (!items) return '';

    var headline = trimStr(content.headline);
    return '<div class="content-type-neo-job-listings"' + inlineStyleAttr(el) + '>' +
      (headline ? '<h2>' + escapeHtml(headline) + '</h2>' : '') + items + '</div>';
  };

  renderers['site-header'] = function (el) {
    var content = el.content || {};
    var html = '';

    var logoUrl = normalizeUrl(trimStr(content.logoUrl));
    if (logoUrl) html += '<img src="' + escapeAttr(logoUrl) + '" alt="Logo" class="logo" loading="lazy">';

    var links = '';
    var list = listOf(content.links);
    for (var i = 0; i < list.length; i++) {
      var label = trimStr(list[i].label);
      var url = trimStr(list[i].url);
      // A link that only points at a builder section (targetSectionId) has no
      // anchor here and is skipped; a real URL is kept.
      if (!label || !/^(https?:\/\/|\/|#)/i.test(url)) continue;
      links += '<a href="' + escapeAttr(normalizeUrl(url)) + '">' + escapeHtml(label) + '</a>';
    }
    if (links) html += '<nav>' + links + '</nav>';

    var ctaLabel = trimStr(content.ctaLabel);
    var ctaUrl = normalizeUrl(trimStr(content.ctaUrl));
    if (content.ctaEnabled && ctaLabel && ctaUrl) {
      html += '<a href="' + escapeAttr(ctaUrl) + '" class="button button--primary">' + escapeHtml(ctaLabel) + '</a>';
    }
    if (!html) return '';
    return '<header class="content-type-neo-site-header"' + inlineStyleAttr(el) + '>' + html + '</header>';
  };

  renderers['site-footer'] = function (el) {
    var content = el.content || {};
    var html = '';

    var copyright = trimStr(content.copyrightText);
    if (copyright) html += '<span class="copyright">' + escapeHtml(copyright) + '</span>';

    var list = listOf(content.links);
    for (var i = 0; i < list.length; i++) {
      var label = trimStr(list[i].label);
      if (!label) continue;
      var url = normalizeUrl(trimStr(list[i].url));
      // A link without a URL still shows its label as text.
      html += url
        ? '<a href="' + escapeAttr(url) + '">' + escapeHtml(label) + '</a>'
        : '<span>' + escapeHtml(label) + '</span>';
    }
    if (!html) return '';
    return '<footer class="content-type-neo-site-footer"' + inlineStyleAttr(el) + '>' + html + '</footer>';
  };

  // An embedded landing page. When the importer resolved it to `embeddedHtml`
  // that HTML IS the content and ships verbatim. An embed carrying only a
  // `landingPageId` needs the AICO backend at view time — it cannot be
  // resolved here, so it degrades through the unknown-type path (visible
  // title + warning) instead of pretending to be empty.
  renderers['landing-page-embed'] = function (el) {
    var content = el.content || {};
    var embeddedHtml = trimStr(content.embeddedHtml);
    if (embeddedHtml) {
      return '<div class="content-type-neo-landing-page-embed"' + inlineStyleAttr(el) + '>' + embeddedHtml + '</div>';
    }
    var id = content.landingPageId;
    if (id === null || id === undefined || id === '') return '';
    return renderUnresolvable(el, 'landing-page-embed needs the AICO backend to resolve landingPageId ' + id);
  };

  // ============================================================
  // `table`
  //
  // FC St.Gallen's weekly "Trainingsplan" IS a table, and tables are most of
  // what their archive is. Until this arm existed the type fell through and
  // every one of them vanished.
  //
  // WHAT PRODUCTION ACTUALLY CONTAINS (full census of every neo document, news
  // and landing pages, both clubs, 2026-09-10):
  //
  //   FCSG   125 tables in 105 documents      FCB   10 tables in 6 documents
  //
  //   keys present : `rows` and `headerRow`. That is the whole list.
  //   NOT present  : caption, columns[], columnWidths, cell.colSpan,
  //                  cell.rowSpan, cell.align — ZERO instances, either club.
  //                  Same for every cosmetic key.
  //   ragged rows  : 0        legacy string[][] cells : 0
  //   empty cells  : 158, and they are LOAD-BEARING — the real Trainingsplan
  //                  header is ["DATUM", "", ""] and those empty <th>s are
  //                  what hold the columns open over the data below.
  //   inline tags  : a, br, strong. Nothing else, so the allowlist below is
  //                  not a guess about what authors write — it is a superset
  //                  of what they have written.
  //
  // So `colSpan`, `rowSpan`, `align` and `caption` are handled here WITHOUT a
  // single production instance to test against. That is deliberate, not
  // speculative gold-plating: the element supports all four (tableModel.ts),
  // the backend arm honours all four, and a hand-authored table — as opposed
  // to a converted one — will use them. Reading the diff and finding untested
  // paths is expected; the alternative is the next hand-built table silently
  // losing its merged header. Re-run the census before assuming they are
  // still unused.
  //
  // This mirrors the BACKEND arm
  // (NeoLandingPageBodyRenderer::renderTable / ::sanitizeTableCellHtml on aico
  // origin/main) rather than aiconeo's TableBlockRenderer, on purpose:
  //  - the backend's sanitiser is a dependency-free tokeniser, where the
  //    builder's is DOMPurify — which this file cannot have and must not fake;
  //  - matching the backend is what makes an article look the same in the
  //    Shopify page body and here.
  // Where the two upstreams already disagree, the divergence is called out at
  // the line.
  //
  // Content shape (tableModel.ts on aiconeo origin/main is the contract):
  //   rows        : TableCell[][]   — INCLUDES the header row as rows[0]
  //   headerRow   : boolean         — whether rows[0] renders as <th>
  //   columns?    : [{width?, align?}]   (legacy: columnWidths?: string[])
  //   caption?    : string
  //   cell        : {text, colSpan?, rowSpan?, align?}  (legacy: a bare string)
  //   text        : limited inline HTML — <a href target>, <strong>, <em>, <br>
  // Cosmetic keys (preset/headerStyle/rowStyle/border/typography/mobile) are
  // enum-ish theme names owned by the builder; translating them here would
  // drift the day one is renamed, so they are deliberately not honoured — same
  // decision the backend documents.
  // ============================================================

  // Inline styling travels WITH the markup: a news body can land in a theme
  // that never heard of .content-type-neo-table, so the borders and header
  // shading that make a table read as a table cannot live only in the CSS
  // block this file injects. Values copied from the backend constants.
  var TABLE_STYLE = 'width:100%;border-collapse:collapse;table-layout:auto;font-size:15px;line-height:1.45';
  var TABLE_CELL_STYLE = 'padding:8px 12px;border:1px solid #dcdcdc;vertical-align:top';
  // A fixed light background needs a fixed dark text colour: on a dark section the inherited
  // body colour is white and the header row reads as an empty bar (aico 37a89b19b26).
  var TABLE_HEADER_CELL_STYLE = TABLE_CELL_STYLE + ';background-color:#f4f4f5;color:#111;font-weight:700';

  // The inline tags a cell may carry, mapped onto the tag actually
  // emitted — <b>/<i> normalised onto their semantic twins, exactly like the
  // converter's serializeInline() and the backend's TABLE_CELL_INLINE_TAGS.
  var TABLE_CELL_INLINE_TAGS = {
    a: 'a', strong: 'strong', b: 'strong', em: 'em', i: 'em', br: 'br', img: 'img'
  };

  // A picture in a cell (a legacy table that showed a control's icon next to
  // its description) is drawn at most as wide as its cell, on the text line.
  var TABLE_CELL_IMAGE_STYLE = 'max-width:100%;height:auto;vertical-align:middle';

  var CSS_LENGTH_RE = /^\d+(\.\d+)?(px|rem|em|vh|vw|%)$/;

  // Entity decode for attribute values only. The converter escapes attributes,
  // and a URL has to be validated as the BROWSER will resolve it, so `&amp;`
  // must become `&` before the allowlist sees it.
  var NAMED_ENTITIES = {
    amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'"
  };

  function decodeEntities(s) {
    return String(s).replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g, function (whole, body) {
      if (body.charAt(0) === '#') {
        var code = body.charAt(1) === 'x' || body.charAt(1) === 'X'
          ? parseInt(body.slice(2), 16)
          : parseInt(body.slice(1), 10);
        if (!isFinite(code) || code < 1 || code > 0x10ffff) return whole;
        try { return String.fromCodePoint(code); } catch (e) { return whole; }
      }
      var named = NAMED_ENTITIES[body.toLowerCase()];
      return named === undefined ? whole : named;
    });
  }

  // The backend's isSafeUrl(), verbatim in behaviour: an explicit safe scheme,
  // or no scheme at all. Control characters and spaces are stripped first
  // because browsers ignore them while resolving a scheme.
  function isSafeTableUrl(url) {
    var normalized = String(url).replace(/[\x00-\x20\x7F]+/g, '');
    if (!normalized) return false;
    if (/^(?:https?|mailto|tel):/i.test(normalized)) return true;
    // A colon before the first "/", "?" or "#" is a scheme we did not list.
    return !/^[^/?#]*:/.test(normalized);
  }

  /** The `<a …>` opening tag rebuilt from href + target, or null when the href
   *  fails the allowlist (caller then unwraps, so the label survives as text). */
  function tableTagAttributes(tagMarkup) {
    var attrs = {};
    var re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/g;
    var m;
    while ((m = re.exec(tagMarkup)) !== null) {
      var value = m[2] !== undefined && m[2] !== '' ? m[2]
        : (m[3] !== undefined && m[3] !== '' ? m[3] : (m[4] || ''));
      attrs[m[1].toLowerCase()] = decodeEntities(value);
    }
    return attrs;
  }

  function tableAnchorOpenTag(tagMarkup) {
    var attrs = tableTagAttributes(tagMarkup);
    var href = (attrs.href || '').replace(/^\s+|\s+$/g, '');
    if (!href || !isSafeTableUrl(href)) return null;
    // target="_blank" forces the full rel: the converter emits the weaker
    // rel="noopener", which still leaks the referrer.
    var target = (attrs.target || '').toLowerCase().replace(/^\s+|\s+$/g, '') === '_blank'
      ? ' target="_blank" rel="noopener noreferrer"' : '';
    return '<a href="' + escapeHtml(href) + '"' + target + '>';
  }

  /**
   * The `<img>` of a cell rebuilt from exactly three attributes, or '' when the
   * src is not an absolute http(s) URL (the whole picture is dropped then:
   * unlike a link, a picture has no label to keep).
   *
   *   src    http:// or https:// only
   *   alt    text, escaped
   *   width  digits (CSS px, `60` -> `60px`) or digits plus `%`; anything else
   *          is ignored and the picture keeps its natural width
   *
   * Every other attribute (style, on*, srcset, class, ...) is not carried.
   */
  function tableImageTag(tagMarkup) {
    var attrs = tableTagAttributes(tagMarkup);
    var src = (attrs.src || '').replace(/^\s+|\s+$/g, '');
    // Re-checked as the browser will resolve it: control characters and
    // spaces inside a URL are dropped by the browser, so they must not be
    // able to hide a scheme from this test.
    if (/[\x00-\x20\x7F]/.test(src) || !/^https?:\/\/[^/?#]/i.test(src)) return '';
    // A picture in a table aico marks legacyCellPictures (--legacy-cell-picture-classes):
    // drawn as the legacy block drew it, its classes and size attributes and no
    // inline sizing, so the tab's stylesheet sizes, aligns and rounds it.
    if (LEGACY_CELL_PICTURES) {
      var legacyClass = typeof attrs['class'] === 'string' ? attrs['class'].replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '') : '';
      var legacy = '<img' + (legacyClass ? ' class="' + legacyClass + '"' : '') + ' src="' + escapeHtml(src) + '" alt="' + escapeHtml(attrs.alt || '') + '"';
      if (/^\d{1,5}%?$/.test(attrs.width || '')) legacy += ' width="' + attrs.width + '"';
      if (/^\d{1,5}$/.test(attrs.height || '')) legacy += ' height="' + attrs.height + '"';
      return legacy + '>';
    }
    // A cell picture of a table whose content.cellImageStyle is 'rounded': the theme's rounded,
    // responsive picture classes and the width attribute, as the legacy cell picture was drawn.
    if (ROUNDED_CELL_PICTURES) {
      var rounded = '<img class="img-responsive img-rounded" src="' + escapeHtml(src) + '" alt="' + escapeHtml(attrs.alt || '') + '"';
      if (/^\d{1,5}%?$/.test(attrs.width || '')) rounded += ' width="' + attrs.width + '"';
      return rounded + '>';
    }
    var style = TABLE_CELL_IMAGE_STYLE;
    var width = (attrs.width || '').replace(/^\s+|\s+$/g, '');
    var w = /^(\d{1,4})(%?)$/.exec(width);
    if (w && parseInt(w[1], 10) > 0) style += ';width:' + w[1] + (w[2] ? '%' : 'px');
    return '<img src="' + escapeHtml(src) + '" alt="' + escapeHtml(attrs.alt || '') +
      '" style="' + style + '">';
  }

  /** Close the innermost open tag with this name, plus everything still open
   *  inside it. A closing tag matching nothing open is dropped. */
  function closeTableInlineTag(name, open) {
    var tag = TABLE_CELL_INLINE_TAGS[name];
    if (!tag || tag === 'br' || tag === 'img') return '';
    var depth = -1;
    for (var i = 0; i < open.length; i++) if (open[i].tag === tag) depth = i;
    if (depth < 0) return '';
    var html = '';
    while (open.length > depth) {
      var entry = open.pop();
      if (entry && entry.emitted) html += '</' + entry.tag + '>';
    }
    return html;
  }

  /**
   * A cell's text reduced to the inline tags the table element renders (the
   * four text tags, plus a picture rebuilt by tableImageTag()),
   * everything else UNWRAPPED to its content.
   *
   * A sanitiser, not a pass-through — and the one place in this file where
   * markup from the document is NOT trusted verbatim. The html/text elements
   * ship author-pasted CMS markup by long-standing contract; a table cell's
   * markup is machine-built from scraped GrapesJS nodes, so it is re-checked
   * here rather than trusted because an earlier pass was careful.
   *
   * Text nodes are NOT re-escaped: the converter already escaped them, so
   * `&amp;` must stay `&amp;` and not become `&amp;amp;`. Only a stray `<`/`>`
   * that survived the tag split is escaped.
   */
  function sanitizeTableCellHtml(html) {
    var s = html == null ? '' : String(html);
    if (!s.replace(/^\s+|\s+$/g, '')) return '';

    // Tag AND body: for these two the content is code, not text. A comment goes
    // whole — a browser shows none of it, so none of it is content.
    s = s.replace(/<(script|style)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/gi, '');
    s = s.replace(/<!--[\s\S]*?(?:-->|$)/g, '');

    var parts = s.split(/(<[^>]*>)/);
    var out = '';
    var open = [];

    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      if (!part) continue;

      // The HTML tokenizer's rule, not a looser one: only `<` directly
      // followed by a letter opens a tag. `<!doctype>` and `<?xml` are
      // swallowed; `< b >` and `<3` are TEXT a browser shows — treating
      // `< b >` as a tag turned "a < b > c" into a <strong>.
      if (part.charAt(0) !== '<' || !/^<\/?[a-zA-Z]/.test(part)) {
        if (part.charAt(0) === '<' && /^<[!?/]/.test(part)) continue;
        out += part.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        continue;
      }

      var closing = /^<\/([a-zA-Z][^\s/>]*)/.exec(part);
      if (closing) { out += closeTableInlineTag(closing[1].toLowerCase(), open); continue; }

      // A tag name runs to the first whitespace, `/` or `>`: `<a-x>` is a
      // custom element, not an anchor, and must not be rebuilt into one.
      var opening = /^<([a-zA-Z][^\s/>]*)/.exec(part);
      if (!opening) continue;

      var tag = TABLE_CELL_INLINE_TAGS[opening[1].toLowerCase()];
      if (!tag) continue;

      if (tag === 'br') { out += '<br>'; continue; }

      if (tag === 'img') { out += tableImageTag(part); continue; }

      if (tag === 'a') {
        var anchor = tableAnchorOpenTag(part);
        if (anchor === null) { open.push({ tag: 'a', emitted: false }); continue; }
        out += anchor;
        open.push({ tag: 'a', emitted: true });
        continue;
      }

      out += '<' + tag + '>';
      // `<strong/>`: self-closed in the source, so never pushed.
      if (part.replace(/[>\s]+$/, '').slice(-1) === '/') { out += '</' + tag + '>'; continue; }
      open.push({ tag: tag, emitted: true });
    }

    while (open.length) {
      var last = open.pop();
      if (last && last.emitted) out += '</' + last.tag + '>';
    }
    return out;
  }

  function tableAlignment(align) {
    var a = String(align == null ? '' : align).toLowerCase().replace(/^\s+|\s+$/g, '');
    return (a === 'left' || a === 'center' || a === 'right') ? a : null;
  }

  /**
   * `content.columns`, falling back to the legacy `content.columnWidths`.
   *
   * DELIBERATE DIVERGENCE FROM THE BACKEND — do not "fix" this to match it.
   * NeoLandingPageBodyRenderer::renderTableColumnGroup reads `columns` only,
   * so a pre-migration document keeps its column widths here and loses them in
   * the Shopify body. aiconeo's tableModel.ts::normalizeTableColumns reads the
   * legacy key, the builder therefore shows those widths, and matching the
   * builder is the right side to be on when the two upstreams disagree about
   * whether authored data exists. The backend is the one with the gap.
   */
  function tableColumns(content) {
    var raw = content.columns;
    var out = [];
    var i;
    if (Array.isArray(raw)) {
      for (i = 0; i < raw.length; i++) {
        var entry = raw[i];
        if (typeof entry === 'string') { out.push({ width: entry }); continue; }
        out.push(entry && typeof entry === 'object' ? entry : {});
      }
      return out;
    }
    if (Array.isArray(content.columnWidths)) {
      for (i = 0; i < content.columnWidths.length; i++) out.push({ width: content.columnWidths[i] });
    }
    return out;
  }

  /** `<colgroup>` for the per-column widths, or '' when no column asks for one.
   *  A bare number is the HTML width="120" attribute's meaning — pixels. */
  function renderTableColgroup(columns) {
    var cols = '';
    var hasWidth = false;
    for (var i = 0; i < columns.length; i++) {
      var width = String((columns[i] && columns[i].width) || '').replace(/^\s+|\s+$/g, '');
      if (width && /^\d+(\.\d+)?$/.test(width)) width += 'px';
      if (width && CSS_LENGTH_RE.test(width)) { cols += '<col style="width:' + escapeAttr(width) + '">'; hasWidth = true; }
      else cols += '<col>';
    }
    return hasWidth ? '<colgroup>' + cols + '</colgroup>' : '';
  }

  /** One `<tr>`. A row with no cells renders nothing; a row whose cells are all
   *  empty strings still renders — an empty header row is what holds the
   *  columns open, and an empty spacer row is authored content. */
  function renderTableRow(row, isHeader, columns) {
    if (!Array.isArray(row) || row.length === 0) return '';

    var tag = isHeader ? 'th' : 'td';
    var baseStyle = isHeader ? TABLE_HEADER_CELL_STYLE : TABLE_CELL_STYLE;
    var html = '';

    for (var i = 0; i < row.length; i++) {
      var raw = row[i];
      // A legacy `string[][]` document stores a bare string per cell.
      //
      // DELIBERATE DIVERGENCE FROM THE BACKEND — do not "fix" this to match
      // it. NeoLandingPageBodyRenderer::renderTableRow runs the row through
      // listOfArrays(), which keeps only ARRAY entries, so a bare-string cell
      // is dropped there. aiconeo's normalizeTableCell coerces it to {text},
      // and so does this. A dropped cell is the exact failure this whole file
      // exists to prevent, and no reading of "match the backend" is worth
      // reintroducing it. The census found no legacy cells in FCSG or FCB
      // today, so this costs nothing now and catches an older document that
      // has not been re-saved since.
      var cell = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw
        : { text: raw == null ? '' : String(raw) };

      // Column fallback is indexed by the cell's position in its ROW ARRAY,
      // not by its grid column — same as the backend. With a rowSpan above it
      // the two differ, but no production table spans (see the census), and
      // matching the backend keeps the two renderings identical.
      var align = tableAlignment(cell.align)
        || tableAlignment(columns[i] && columns[i].align)
        || 'left';

      var attributes = ' style="' + baseStyle + ';text-align:' + align + '"';
      if (isHeader) attributes += ' scope="col"';

      var spans = [['colSpan', 'colspan'], ['rowSpan', 'rowspan']];
      for (var s = 0; s < spans.length; s++) {
        var value = cell[spans[s][0]];
        var span = null;
        if (typeof value === 'number' && isFinite(value)) span = Math.floor(value);
        else if (typeof value === 'string' && /^\d+$/.test(value)) span = parseInt(value, 10);
        // Capped: a span wider than any real table cannot help the layout, and
        // a huge one would blow the row out of the page. (The backend caps at
        // 100, tableModel's MAX_TABLE_SPAN at 64; the backend wins here so the
        // Shopify body and this render the same markup.)
        if (span !== null && span > 1) attributes += ' ' + spans[s][1] + '="' + Math.min(span, 100) + '"';
      }

      html += '<' + tag + attributes + '>' + sanitizeTableCellHtml(cell.text) + '</' + tag + '>';
    }

    return html ? '<tr>' + html + '</tr>' : '';
  }

  // ============================================================
  // `table` with row headers and the builder's look (opt-in)
  //
  // Drawn only for a table whose content says `rendersLook: true` or
  // `rowHeaders: true` (aico registry section 7.2). Every other table takes
  // the arm above, byte for byte as before. The look keys are the ones the
  // builder's table panel stores (headerStyle, border, typography, mobile)
  // plus rowHeaderStyle, cellStyle, a per-cell `background`, tableBackground,
  // tableMinWidth and captionVisuallyHidden. Every value is validated (a
  // colour, a length, a weight); anything else is dropped, never printed.
  //
  // mobile.behaviour 'columns': below `mobile.breakpoint` px (default 768)
  // the table is hidden and one card per DATA column is shown instead, the
  // transposed table the legacy comparison widgets drew. The switch is CSS
  // only: the cards carry an inline display:none, and one <style> keyed on
  // the breakpoint class `.neo-table--bp-<px>` (identical text for every
  // table with that breakpoint, so repeating it is harmless) flips the two
  // with !important under the media rule. No theme stylesheet is needed.
  // ============================================================

  var LOOK_COLOR_RE = /^(#[0-9a-f]{3,8}|(rgb|rgba|hsl|hsla)\([0-9.,%\s\/]+\)|[a-z]{3,20})$/i;
  var LOOK_LENGTH_RE = /^(0|\d+(\.\d+)?(px|rem|em|%))$/;

  function lookObject(v) { return v && typeof v === 'object' && !Array.isArray(v) ? v : {}; }
  function lookColor(v) { v = trimStr(v); return LOOK_COLOR_RE.test(v) ? v : ''; }
  function lookLength(v) {
    if (typeof v === 'number' && isFinite(v) && v >= 0) v = v + 'px';
    v = trimStr(v);
    return LOOK_LENGTH_RE.test(v) ? v : '';
  }
  function lookBox(v) {
    v = trimStr(v);
    if (!v) return '';
    var parts = v.split(/\s+/);
    if (parts.length > 4) return '';
    for (var i = 0; i < parts.length; i++) if (!LOOK_LENGTH_RE.test(parts[i])) return '';
    return parts.join(' ');
  }
  function lookWeight(group) {
    var w = trimStr(group.fontWeight);
    if (/^[1-9]00$/.test(w)) return w;
    if (group.bold === true) return '700';
    if (group.bold === false) return '400';
    return '';
  }
  function lookDecl(list, property, value) { if (value) list.push(property + ':' + value); }

  /** The look a table asks for, resolved and validated once. */
  function resolveTableLook(content) {
    var drawsLook = content.rendersLook === true;
    var header = drawsLook ? lookObject(content.headerStyle) : {};
    var rowHeader = drawsLook ? lookObject(content.rowHeaderStyle) : {};
    var cell = drawsLook ? lookObject(content.cellStyle) : {};
    var border = drawsLook ? lookObject(content.border) : {};
    var typography = drawsLook ? lookObject(content.typography) : {};
    var mobile = drawsLook ? lookObject(content.mobile) : {};

    var style = trimStr(border.style);
    var hasFlags = typeof border.outer === 'boolean' || typeof border.rows === 'boolean' || typeof border.columns === 'boolean';
    var outer = hasFlags ? border.outer === true : style === 'all';
    var rows = hasFlags ? border.rows === true : (style === 'all' || style === 'horizontal');
    var cols = hasFlags ? border.columns === true : style === 'all';
    var breakpoint = parseInt(mobile.breakpoint, 10);
    if (!isFinite(breakpoint) || breakpoint < 200 || breakpoint > 4000) breakpoint = 768;

    return {
      drawsLook: drawsLook,
      header: {
        background: lookColor(header.background), color: lookColor(header.color), weight: lookWeight(header),
        padding: lookBox(header.padding), accentColor: lookColor(header.accentColor),
        accentWidth: lookLength(header.accentWidth) || '3px'
      },
      rowHeader: {
        background: lookColor(rowHeader.background), color: lookColor(rowHeader.color), weight: lookWeight(rowHeader),
        padding: lookBox(rowHeader.padding)
      },
      cell: { color: lookColor(cell.color), background: lookColor(cell.background) },
      // A look without any border key keeps the engine's own cell borders.
      borderKeys: drawsLook && (style !== '' || hasFlags || border.color != null || border.width != null || border.radius != null),
      border: {
        color: lookColor(border.color) || '#dcdcdc', width: lookLength(border.width) || '1px',
        radius: lookLength(border.radius), outer: outer, rows: rows, columns: cols
      },
      fontSize: lookLength(typography.fontSize),
      cellPadding: lookBox(typography.cellPadding),
      background: drawsLook ? lookColor(content.tableBackground) : '',
      minWidth: drawsLook ? lookLength(content.tableMinWidth) : '',
      mobileColumns: drawsLook && trimStr(mobile.behaviour) === 'columns',
      // Registry 8.4: below the breakpoint every row header on a line of its own.
      rowLabels: drawsLook && trimStr(mobile.behaviour) === 'rowLabels',
      rowLooks: drawsLook ? resolveRowLooks(content.rowLooks) : {},
      breakpoint: breakpoint,
      mobileAccentColor: lookColor(mobile.accentColor),
      mobileAccentWidth: lookLength(mobile.accentWidth) || '3px'
    };
  }

  /** The looks of the body rows that carry one (rowLooks, registry 8.4), keyed by body row index. */
  function resolveRowLooks(raw) {
    var looks = {};
    var entries = Array.isArray(raw) ? raw : [];
    for (var i = 0; i < entries.length; i++) {
      var entry = lookObject(entries[i]);
      if (typeof entry.row !== 'number' || entry.row % 1 !== 0 || entry.row < 0) continue;
      var weight = trimStr(entry.fontWeight);
      var rowLook = {
        background: lookColor(entry.background), color: lookColor(entry.color),
        fontSize: lookLength(entry.fontSize), weight: /^[1-9]00$/.test(weight) ? weight : ''
      };
      if (rowLook.background + rowLook.color + rowLook.fontSize + rowLook.weight !== '') looks[entry.row] = rowLook;
    }
    return looks;
  }

  /** A cell's lines (registry 8.4): block lines with their own look, one of them a pill badge. */
  function renderCellLines(raw) {
    var html = '';
    var lines = Array.isArray(raw) ? raw : [];
    for (var i = 0; i < lines.length; i++) {
      var line = lookObject(lines[i]);
      if (typeof line.text !== 'string') continue;
      var text = sanitizeTableCellHtml(line.text);
      if (String(text).replace(/<[^>]*>/g, '').replace(/\s+/g, '') === '') continue;
      var decl = [];
      lookDecl(decl, 'font-size', lookLength(line.fontSize));
      var weight = trimStr(line.fontWeight);
      if (/^[1-9]00$/.test(weight)) decl.push('font-weight:' + weight);
      var marginTop = lookLength(line.marginTop);
      var badge = line.badge && typeof line.badge === 'object' ? line.badge : null;
      if (badge) {
        var pill = ['display:inline-block', 'padding:3px 10px', 'border-radius:999px', 'line-height:16px', 'white-space:nowrap'];
        lookDecl(pill, 'background-color', lookColor(badge.background));
        lookDecl(pill, 'color', lookColor(badge.color));
        html += '<span style="display:block' + (marginTop ? ';margin-top:' + marginTop : '') + '"><span style="' +
          escapeStyleValue(pill.concat(decl).join(';')) + '">' + text + '</span></span>';
        continue;
      }
      lookDecl(decl, 'margin-top', marginTop);
      lookDecl(decl, 'color', lookColor(line.color));
      html += '<span style="' + escapeStyleValue(['display:block'].concat(decl).join(';')) + '">' + text + '</span>';
    }
    return html;
  }

  /** Inline style of one cell under a look. `kind`: 'head' | 'rowhead' | 'cell'. */
  function lookCellStyle(look, kind, align, isFirstRow, isFirstInRow, ownBackground, rowLook) {
    var d = [];
    var base = look.borderKeys ? 'vertical-align:top' : TABLE_CELL_STYLE;
    if (LEGACY_TAB_LOOK && look.borderKeys) base = 'border:0;vertical-align:' + LEGACY_TAB_LOOK.verticalAlign;
    if (!look.drawsLook) {
      // rowHeaders alone: the engine's own cell look, row headers like header cells.
      return (kind === 'cell' ? TABLE_CELL_STYLE : TABLE_HEADER_CELL_STYLE) + ';text-align:' + align;
    }
    d.push(base);
    if (look.borderKeys && LEGACY_TAB_LOOK && LEGACY_TAB_LOOK.cellFrame) {
      // Each legacy cell drew its own frame; the tab's rules took the left and right ones off body
      // cells (aico legacyTabLook.cellFrame).
      var frame = look.border.width + ' solid ' + look.border.color;
      d.push('border:' + frame);
      if (kind === 'cell') d.push('border-left:0', 'border-right:0');
    } else if (look.borderKeys) {
      var line = look.border.width + ' solid ' + look.border.color;
      if (look.border.rows && !isFirstRow) d.push('border-top:' + line);
      if (look.border.columns && !isFirstInRow) d.push('border-left:' + line);
    }
    var group = kind === 'head' ? look.header : kind === 'rowhead' ? look.rowHeader : null;
    var padding = (group && group.padding) || look.cellPadding;
    if (padding) d.push('padding:' + padding);
    if (look.borderKeys && LEGACY_TAB_LOOK && LEGACY_TAB_LOOK.headerRule && kind === 'head') d.push('border-bottom:' + look.border.width + ' solid ' + look.border.color);
    if (look.fontSize && !LEGACY_TAB_LOOK) d.push('font-size:' + look.fontSize);
    if (rowLook && rowLook.fontSize && !LEGACY_TAB_LOOK) d.push('font-size:' + rowLook.fontSize);
    var background = ownBackground || (rowLook && rowLook.background) || (group ? group.background : look.cell.background);
    if (!background && !look.borderKeys && kind !== 'cell') background = '#f4f4f5';
    lookDecl(d, 'background-color', background);
    if (!LEGACY_TAB_LOOK) lookDecl(d, 'color', (rowLook && rowLook.color) || (group ? group.color : look.cell.color));
    var weight = (rowLook && rowLook.weight) || (group ? (group.weight || '700') : '');
    if (!LEGACY_TAB_LOOK) lookDecl(d, 'font-weight', weight);
    d.push('text-align:' + align);
    return d.join(';');
  }

  function tableCellOf(raw) {
    return (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : { text: raw == null ? '' : String(raw) };
  }

  function tableCellSpans(cell) {
    var out = '';
    var spans = [['colSpan', 'colspan'], ['rowSpan', 'rowspan']];
    for (var s = 0; s < spans.length; s++) {
      var value = cell[spans[s][0]];
      var span = null;
      if (typeof value === 'number' && isFinite(value)) span = Math.floor(value);
      else if (typeof value === 'string' && /^\d+$/.test(value)) span = parseInt(value, 10);
      if (span !== null && span > 1) out += ' ' + spans[s][1] + '="' + Math.min(span, 100) + '"';
    }
    return out;
  }

  function renderLookTableRow(row, isHeader, columns, look, rowHeaders, isFirstRow, rowLook) {
    if (!Array.isArray(row) || row.length === 0) return '';
    var html = '';
    for (var i = 0; i < row.length; i++) {
      var cell = tableCellOf(row[i]);
      var isRowHeader = !isHeader && rowHeaders && i === 0;
      var align = tableAlignment(cell.align) || tableAlignment(columns[i] && columns[i].align) || 'left';
      var kind = isHeader ? 'head' : isRowHeader ? 'rowhead' : 'cell';
      var own = look.drawsLook ? lookColor(cell.background) : '';
      var tag = kind === 'cell' ? 'td' : 'th';
      var attributes = ' style="' + escapeStyleValue(lookCellStyle(look, kind, align, isFirstRow, i === 0, own, kind === 'cell' ? rowLook : null)) + '"';
      if (isHeader) attributes += ' scope="col"';
      else if (isRowHeader) attributes += ' scope="row"';
      attributes += tableCellSpans(cell);
      var lines = look.drawsLook ? renderCellLines(cell.lines) : '';
      html += '<' + tag + attributes + '>' + (lines || sanitizeTableCellHtml(cell.text)) + '</' + tag + '>';
    }
    if (!html) return '';
    var trStyle = '';
    if (isHeader && look.header.accentColor) {
      // A rule above the header row: drawn on the cells, the only box a
      // separate-model row can paint.
      html = html.replace(/<th style="/g, '<th style="box-shadow:inset 0 ' + look.header.accentWidth + ' 0 ' + look.header.accentColor + ';');
    }
    return '<tr' + trStyle + '>' + html + '</tr>';
  }

  /** The transposed table: one card per data column, for narrow screens. */
  function renderTableColumnCards(rows, hasHeaderRow, rowHeaders, look) {
    var header = hasHeaderRow && Array.isArray(rows[0]) ? rows[0] : null;
    var body = hasHeaderRow ? rows.slice(1) : rows;
    var width = 0;
    for (var r = 0; r < rows.length; r++) if (Array.isArray(rows[r]) && rows[r].length > width) width = rows[r].length;
    var first = rowHeaders ? 1 : 0;
    var line = look.border.width + ' solid ' + look.border.color;
    var cards = '';
    for (var c = first; c < width; c++) {
      var head = header && header[c] !== undefined ? sanitizeTableCellHtml(tableCellOf(header[c]).text) : '';
      var headStyle = LEGACY_TAB_LOOK ? ['padding:12px 12px'] : ['padding:12px 12px', 'font-weight:' + (look.header.weight || '700')];
      if (!LEGACY_TAB_LOOK) lookDecl(headStyle, 'color', look.header.color);
      lookDecl(headStyle, 'background-color', look.header.background);
      if (look.mobileAccentColor) headStyle.push('border-top:' + look.mobileAccentWidth + ' solid ' + look.mobileAccentColor);
      var items = '';
      for (var b = 0; b < body.length; b++) {
        if (!Array.isArray(body[b]) || body[b].length === 0) continue;
        var label = rowHeaders && body[b][0] !== undefined ? sanitizeTableCellHtml(tableCellOf(body[b][0]).text) : '';
        var value = body[b][c] !== undefined ? sanitizeTableCellHtml(tableCellOf(body[b][c]).text) : '';
        var rowStyle = ['display:flex', 'align-items:flex-start', 'justify-content:space-between', 'gap:12px', 'padding:8px 0'];
        if (items) rowStyle.push('border-top:' + line);
        var dtStyle = LEGACY_TAB_LOOK ? ['margin:0'] : ['margin:0', 'font-weight:' + (look.rowHeader.weight || '700')];
        if (!LEGACY_TAB_LOOK) lookDecl(dtStyle, 'color', look.rowHeader.color);
        if (!LEGACY_TAB_LOOK) lookDecl(dtStyle, 'font-size', look.fontSize);
        var ddStyle = ['margin:0', 'text-align:right'];
        if (!LEGACY_TAB_LOOK) lookDecl(ddStyle, 'color', look.cell.color);
        if (!LEGACY_TAB_LOOK) lookDecl(ddStyle, 'font-size', look.fontSize);
        items += '<div style="' + escapeStyleValue(rowStyle.join(';')) + '">' +
          '<dt style="' + escapeStyleValue(dtStyle.join(';')) + '">' + label + '</dt>' +
          '<dd style="' + escapeStyleValue(ddStyle.join(';')) + '">' + value + '</dd></div>';
      }
      var cardStyle = ['border:' + line, 'overflow:hidden'];
      lookDecl(cardStyle, 'border-radius', look.border.radius);
      lookDecl(cardStyle, 'background-color', look.background);
      cards += '<div class="neo-table__card" style="' + escapeStyleValue(cardStyle.join(';')) + '">' +
        '<div class="neo-table__card-head" style="' + escapeStyleValue(headStyle.join(';')) + '">' + head + '</div>' +
        '<dl style="margin:0;padding:10px 12px 12px 12px">' + items + '</dl></div>';
    }
    if (!cards) return '';
    // The cards' own `display:none` stays a normal declaration: the breakpoint
    // rule has to be able to turn it into a grid.
    return '<div class="neo-table__cards" style="display:none;grid-template-columns:minmax(0,1fr);gap:12px">' + importantizeStyleAttributes(cards) + '</div>';
  }

  // A look table aico marked legacyTabLook (import switch --legacy-tab-look-tables) inside a legacy
  // card: drawn as the legacy product tab drew the raw-markup table it came from. The tab's text rules
  // outranked the block's text colours, font sizes, weights and line heights there, so the table sets none of them
  // and stays outside the neo-look island (whose reset would undo the tab's rules); it keeps the
  // block's table layout, its cell alignment and its header rule. Set while such a table draws.
  var LEGACY_TAB_LOOK = null;

  function renderLookTable(el) {
    var previousTabLook = LEGACY_TAB_LOOK;
    var mark = el.content && el.content.legacyCard && el.content.legacyTabLook;
    // The native tab look (aico native settings contract v2, section 5): a look
    // table whose content.shopTextStyle is true takes the shop's text style and
    // is drawn exactly as a legacyTabLook table, its cell alignment
    // (content.cellVerticalAlign), layout (content.tableLayout) and header rule
    // (content.headerRule) read from its own settings.
    if (!mark && el.content && el.content.shopTextStyle === true) {
      mark = {
        verticalAlign: el.content.cellVerticalAlign,
        headerRule: el.content.headerRule === true,
        tableLayout: el.content.tableLayout,
        classless: false,
        borderCollapse: 'separate',
        cellFrame: false
      };
    }
    LEGACY_TAB_LOOK = mark && typeof mark === 'object'
      ? {
        verticalAlign: /^(top|middle|bottom)$/.test(mark.verticalAlign) ? mark.verticalAlign : 'top',
        headerRule: mark.headerRule === true,
        tableLayout: mark.tableLayout === 'fixed' ? 'fixed' : 'auto',
        classless: mark.classless === true,
        borderCollapse: mark.borderCollapse === 'collapse' ? 'collapse' : 'separate',
        cellFrame: mark.cellFrame === true,
        containerBreakpoint: /^\d{2,4}$/.test(String(mark.containerBreakpoint || '')) ? parseInt(mark.containerBreakpoint, 10) : 0
      }
      : null;
    try {
      return renderLookTableInner(el);
    } finally {
      LEGACY_TAB_LOOK = previousTabLook;
    }
  }

  function renderLookTableInner(el) {
    var content = el.content || {};
    var rows = Array.isArray(content.rows) ? content.rows : [];
    if (rows.length === 0) return '';
    var columns = tableColumns(content);
    var hasHeaderRow = content.headerRow === true;
    var rowHeaders = content.rowHeaders === true;
    var look = resolveTableLook(content);

    var head = '';
    var body = '';
    var drawnRows = 0;
    var bodyIndex = -1;
    var valueCount = 1;
    for (var r = 0; r < rows.length; r++) {
      var isHeader = hasHeaderRow && r === 0;
      if (!isHeader) bodyIndex++;
      if (Array.isArray(rows[r])) valueCount = Math.max(valueCount, rows[r].length - 1);
      var tr = renderLookTableRow(rows[r], isHeader, columns, look, rowHeaders, drawnRows === 0, isHeader ? null : (look.rowLooks[bodyIndex] || null));
      if (!tr) continue;
      drawnRows++;
      if (isHeader) head += tr; else body += tr;
    }
    if (!head && !body) return '';

    var tableStyle;
    if (look.drawsLook) {
      // A legacy table printed without a class and without a layout of its own was laid out by the
      // theme's `table:not([class])` rule (aico legacyTabLook.classless): no layout set here either.
      tableStyle = LEGACY_TAB_LOOK
        ? (LEGACY_TAB_LOOK.classless && LEGACY_TAB_LOOK.tableLayout !== 'fixed' ? ['width:100%'] : ['width:100%', 'table-layout:' + LEGACY_TAB_LOOK.tableLayout])
        : ['width:100%', 'table-layout:auto', 'line-height:1.45'];
      if (LEGACY_TAB_LOOK && LEGACY_TAB_LOOK.borderCollapse === 'collapse') tableStyle.push('border-collapse:collapse');
      else if (look.borderKeys) tableStyle.push('border-collapse:separate', 'border-spacing:0');
      else tableStyle.push('border-collapse:collapse');
      if (!LEGACY_TAB_LOOK) tableStyle.push('font-size:' + (look.fontSize || '15px'));
      if (look.borderKeys && look.border.outer && !(LEGACY_TAB_LOOK && LEGACY_TAB_LOOK.cellFrame)) tableStyle.push('border:' + look.border.width + ' solid ' + look.border.color);
      if (look.border.radius) tableStyle.push('border-radius:' + look.border.radius, 'overflow:hidden');
      lookDecl(tableStyle, 'background-color', look.background);
      lookDecl(tableStyle, 'min-width', look.minWidth);
      tableStyle = tableStyle.join(';');
    } else {
      tableStyle = TABLE_STYLE;
    }

    var table = '<table' + (LEGACY_TAB_LOOK && LEGACY_TAB_LOOK.classless ? '' : ' class="neo-table__table"') + ' style="' + escapeStyleValue(tableStyle) + '">';
    var caption = trimStr(content.caption);
    if (caption) {
      var captionStyle = look.drawsLook && content.captionVisuallyHidden === true
        ? 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0'
        : 'caption-side:top;text-align:left;padding:0 0 8px;font-weight:600';
      table += '<caption style="' + captionStyle + '">' + escapeHtml(caption) + '</caption>';
    }
    table += renderTableColgroup(columns);
    if (head) table += '<thead>' + head + '</thead>';
    if (body) table += '<tbody>' + body + '</tbody>';
    table += '</table>';

    var ownStyles = stylesToInline(pickStyles(el));
    if (!look.drawsLook) {
      // rowHeaders alone changes only the first cell of each body row into a
      // <th scope="row">: the wrapper and the table are the engine's plain
      // table, not a styled island.
      return '<div class="content-type-neo-table" style="' +
        escapeStyleValue('overflow-x:auto;-webkit-overflow-scrolling:touch' + (ownStyles ? ';' + ownStyles : '')) + '">' +
        table.replace('<table class="neo-table__table" ', '<table ') + '</div>';
    }

    var classes = LEGACY_TAB_LOOK ? 'content-type-neo-legacy-look-table' : 'content-type-neo-table neo-look';
    var scroller = '<div class="neo-table__scroll" style="overflow-x:auto;-webkit-overflow-scrolling:touch">' + table + '</div>';
    var cards = '';
    var media = '';
    if (look.mobileColumns) {
      cards = renderTableColumnCards(rows, hasHeaderRow, rowHeaders, look);
      if (cards) {
        var bp = 'neo-table--bp-' + look.breakpoint;
        classes += ' neo-table--mobile-columns ' + bp;
        // A legacy tab look table whose widget switched by a container query on its frame: the
        // same switch, measured on this frame (container-type below) instead of the viewport.
        // Native content.mobile.measure 'container' (contract v2): the same switch on the table's own
        // frame width at the table's breakpoint.
        var containerBp = LEGACY_TAB_LOOK && LEGACY_TAB_LOOK.containerBreakpoint ? LEGACY_TAB_LOOK.containerBreakpoint
          : (content.mobile && content.mobile.measure === 'container' ? look.breakpoint : 0);
        var query = containerBp ? '@container (max-width:' + containerBp + 'px)' : '@media (max-width:' + look.breakpoint + 'px)';
        media = '<style>' + query + '{.' + bp + ' .neo-table__scroll{display:none!important}.' +
          bp + ' .neo-table__cards{display:grid!important}}</style>';
      }
    }

    if (!cards && look.rowLabels && rowHeaders) {
      // Registry 8.4: below the breakpoint the corner cell is hidden and every
      // row header spans its row above the values. CSS only.
      var rl = 'neo-table--rl-' + look.breakpoint + '-' + valueCount;
      classes += ' neo-table--row-labels ' + rl;
      media = '<style>@media (max-width:' + look.breakpoint + 'px){.' + rl + ' table,.' + rl + ' thead,.' + rl + ' tbody{display:block!important;width:100%!important;min-width:0!important}.' +
        rl + ' colgroup{display:none!important}.' + rl + ' tr{display:grid!important;grid-template-columns:repeat(' + valueCount + ',minmax(0,1fr))!important}.' +
        rl + ' thead th:first-child{display:none!important}.' + rl + ' tbody th[scope=row]{grid-column:1/-1!important}}</style>';
    }

    var wrapperStyle = cards ? ownStyles : 'overflow-x:auto;-webkit-overflow-scrolling:touch' + (ownStyles ? ';' + ownStyles : '');
    if (cards && ((LEGACY_TAB_LOOK && LEGACY_TAB_LOOK.containerBreakpoint) || (content.mobile && content.mobile.measure === 'container'))) {
      wrapperStyle = 'container-type:inline-size' + (wrapperStyle ? ';' + wrapperStyle : '');
    }
    return media + '<div class="' + classes + '"' + (wrapperStyle ? ' style="' + escapeStyleValue(importantize(wrapperStyle)) + '"' : '') + '>' +
      (cards ? importantizeStyleAttributes(scroller) + cards : importantizeStyleAttributes(table)) + '</div>';
  }

  // ============================================================
  // Plain `table`, drawn for the host theme's table styles
  //
  // render(data, {themeTables: true}). A host page whose own stylesheet
  // already styles content tables (the Ecofort product tab: green header,
  // row lines, no column lines, 160px cells and a horizontal scroller below
  // 750px) gets the plain table as the legacy builder wrote it,
  // `.card-body > figure.table > table` with no inline look, so the theme's
  // rules decide the look exactly as they do for the legacy table. Only what
  // the document authored is kept: per-cell or per-column alignment, spans,
  // column widths and the caption. Look tables (rendersLook / rowHeaders)
  // carry an authored look and never take this arm. Off by default, so news
  // and every other caller keep the self-styled table above.
  // ============================================================

  var THEME_TABLES = false;
  // Set while a theme table that aico marks legacyCellPictures draws its rows.
  var LEGACY_CELL_PICTURES = false;
  // Set while a theme table whose content.cellImageStyle is 'rounded' draws its rows.
  var ROUNDED_CELL_PICTURES = false;

  // ============================================================
  // Native table settings (aico native settings contract v2, section 5)
  //
  //   table  content.themeStyle      'default' | 'lined'
  //   table  content.rowStyle.hover  boolean (the builder's row hover switch)
  //   table  content.equalColumns    boolean
  //   table  content.cellImageStyle  'default' | 'rounded'
  //
  // Drawn with the product tab's own table rules, so a native table looks the
  // way the legacy classed table did: 'lined' is `<table class="table">`
  // (product-content.css `.table`: full width, a rule above every row, the
  // green header) printed bare, without the `figure.table` box, as the legacy
  // block printed it; with the hover switch it is also `table-hover` (row tint
  // `whitesmoke`). A table that is not 'lined' but hovers gets the
  // `neo-row-hover` hook and the injected NATIVE_ROW_HOVER_RULES (#f5f5f5).
  // `equalColumns` puts the table back in the old shop's `div.rte div.contents`
  // box (base.css `.rte table{table-layout:fixed}` and its 1.2rem cell padding
  // from 750px). `cellImageStyle: 'rounded'` prints every cell picture with the
  // theme's `img-responsive img-rounded` classes and its width attribute, as
  // the legacy cell pictures were. A table without these keys draws as before.
  // ============================================================

  var NATIVE_ROW_HOVER_CLASS = 'neo-row-hover';
  var NATIVE_ROW_HOVER_RULES = '.' + NATIVE_ROW_HOVER_CLASS + '>tbody>tr:hover>td,.' + NATIVE_ROW_HOVER_CLASS + '>tbody>tr:hover>th{background-color:#f5f5f5}';
  var nativeRowHoverInjected = false;

  function injectNativeRowHoverStyles() {
    if (nativeRowHoverInjected) return;
    try {
      if (typeof document === 'undefined' || !document.createElement) return;
      if (document.querySelector && document.querySelector('style[data-neo-row-hover]')) {
        nativeRowHoverInjected = true;
        return;
      }
      var style = document.createElement('style');
      style.setAttribute('data-neo-row-hover', '1');
      style.appendChild(document.createTextNode(NATIVE_ROW_HOVER_RULES));
      (document.head || document.documentElement).appendChild(style);
      nativeRowHoverInjected = true;
    } catch (e) { /* no-op */ }
  }

  function tableRowHover(content) {
    return !!(content && content.rowStyle && typeof content.rowStyle === 'object' && content.rowStyle.hover === true);
  }

  // The class the table itself wears for its native settings ('' for none).
  function nativeTableClass(content) {
    if (content && content.themeStyle === 'lined') return 'table' + (tableRowHover(content) ? ' table-hover' : '');
    if (tableRowHover(content)) {
      injectNativeRowHoverStyles();
      return NATIVE_ROW_HOVER_CLASS;
    }
    return '';
  }

  function hasNativeTableSettings(content) {
    return !!content && (content.themeStyle === 'lined' || tableRowHover(content) || content.equalColumns === true || content.cellImageStyle === 'rounded');
  }

  function renderThemeTableRow(row, isHeader, columns) {
    if (!Array.isArray(row) || row.length === 0) return '';
    var tag = isHeader ? 'th' : 'td';
    var html = '';
    for (var i = 0; i < row.length; i++) {
      var raw = row[i];
      var cell = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw
        : { text: raw == null ? '' : String(raw) };
      var align = tableAlignment(cell.align) || tableAlignment(columns[i] && columns[i].align);
      var cellStyle = align ? 'text-align:' + align : '';
      // A column whose width is `auto` (aico --legacy-cell-auto-width): the legacy body cells said
      // width:auto inline, which beat the tab's `td { width: 50% }`; a <col> cannot outrank it.
      if (!isHeader && columns[i] && columns[i].width === 'auto') cellStyle += (cellStyle ? ';' : '') + 'width:auto';
      var attributes = cellStyle ? ' style="' + cellStyle + '"' : '';
      var spans = [['colSpan', 'colspan'], ['rowSpan', 'rowspan']];
      for (var s = 0; s < spans.length; s++) {
        var value = cell[spans[s][0]];
        var span = null;
        if (typeof value === 'number' && isFinite(value)) span = Math.floor(value);
        else if (typeof value === 'string' && /^\d+$/.test(value)) span = parseInt(value, 10);
        if (span !== null && span > 1) attributes += ' ' + spans[s][1] + '="' + Math.min(span, 100) + '"';
      }
      html += '<' + tag + attributes + '>' + sanitizeTableCellHtml(cell.text) + '</' + tag + '>';
    }
    return html ? '<tr>' + html + '</tr>' : '';
  }

  function renderThemeTable(el, rows) {
    var previousLegacyPictures = LEGACY_CELL_PICTURES;
    var previousRoundedPictures = ROUNDED_CELL_PICTURES;
    LEGACY_CELL_PICTURES = !!(el.content && el.content.legacyCellPictures === true);
    ROUNDED_CELL_PICTURES = !!(el.content && el.content.cellImageStyle === 'rounded');
    try {
      return renderThemeTableInner(el, rows);
    } finally {
      LEGACY_CELL_PICTURES = previousLegacyPictures;
      ROUNDED_CELL_PICTURES = previousRoundedPictures;
    }
  }

  function renderThemeTableInner(el, rows) {
    var content = el.content || {};
    var columns = tableColumns(content);
    var hasHeaderRow = content.headerRow === true;
    var head = '';
    var body = '';
    for (var r = 0; r < rows.length; r++) {
      var isHeader = hasHeaderRow && r === 0;
      var tr = renderThemeTableRow(rows[r], isHeader, columns);
      if (!tr) continue;
      if (isHeader) head += tr; else body += tr;
    }
    if (!head && !body) return '';
    // Native table settings (see nativeTableClass()).
    var tableClass = nativeTableClass(content);
    var table = '<table' + (tableClass ? ' class="' + tableClass + '"' : '') + '>';
    var caption = trimStr(content.caption);
    if (caption) table += '<caption>' + escapeHtml(caption) + '</caption>';
    table += renderTableColgroup(columns);
    if (head) table += '<thead>' + head + '</thead>';
    if (body) table += '<tbody>' + body + '</tbody>';
    table += '</table>';
    var boxed = content.themeStyle === 'lined' ? table : '<figure class="table">' + table + '</figure>';
    if (content.equalColumns === true) boxed = '<div class="rte"><div class="contents">' + boxed + '</div></div>';
    return '<div class="content-type-neo-theme-table card-body"' + inlineStyleAttr(el) + '>' +
      boxed + '</div>';
  }

  renderers.table = function (el) {
    var content = el.content || {};
    if (content.rendersLook === true || content.rowHeaders === true) return renderLookTable(el);
    var rows = Array.isArray(content.rows) ? content.rows : [];
    if (rows.length === 0) return '';
    if (THEME_TABLES) return renderThemeTable(el, rows);

    var columns = tableColumns(content);
    var hasHeaderRow = content.headerRow === true;

    var head = '';
    var body = '';
    for (var r = 0; r < rows.length; r++) {
      var isHeader = hasHeaderRow && r === 0;
      // Ragged rows stay ragged — the builder renders them that way too;
      // nothing is padded out.
      var tr = renderTableRow(rows[r], isHeader, columns);
      if (!tr) continue;
      if (isHeader) head += tr; else body += tr;
    }
    if (!head && !body) return '';

    // Native table settings outside the shop-theme arm: the row hover tint and,
    // for equalColumns, a fixed layout. themeStyle / cellImageStyle name the
    // shop theme's own table rules, which only the theme arm above draws.
    var ownTableStyle = content.equalColumns === true ? TABLE_STYLE.replace('table-layout:auto', 'table-layout:fixed') : TABLE_STYLE;
    var hoverClass = tableRowHover(content) ? (injectNativeRowHoverStyles(), ' class="' + NATIVE_ROW_HOVER_CLASS + '"') : '';
    var table = '<table' + hoverClass + ' style="' + ownTableStyle + '">';

    var caption = trimStr(content.caption);
    if (caption) {
      table += '<caption style="caption-side:top;text-align:left;padding:0 0 8px;font-weight:600">' +
        escapeHtml(caption) + '</caption>';
    }

    table += renderTableColgroup(columns);
    if (head) table += '<thead>' + head + '</thead>';
    if (body) table += '<tbody>' + body + '</tbody>';
    table += '</table>';

    // The horizontal scroller is what keeps a five-column Trainingsplan
    // readable on a phone: without it the table either overflows the article
    // column or squeezes every date onto four lines. The element's own
    // styles.desktop go on the wrapper, never replacing the overflow rule.
    var ownStyles = stylesToInline(pickStyles(el));
    var wrapperStyle = 'overflow-x:auto;-webkit-overflow-scrolling:touch' +
      (ownStyles ? ';' + ownStyles : '');
    return '<div class="content-type-neo-table" style="' + escapeStyleValue(wrapperStyle) + '">' +
      table + '</div>';
  };

  // ============================================================
  // `quote`
  //
  // A census of all 11,929 stored neo documents (FCSG 19929, FCB 19839,
  // FCZ 19838) found 999 <blockquote>s in 632 Custom-HTML blobs across 516
  // FCB news articles — and the quote element is the SOLE thing blocking all
  // 516 from being editable in the builder.
  //
  // WHAT PRODUCTION ACTUALLY CONTAINS:
  //
  //   935 of 999 (93.6%) carry an attribution, in TWO shapes:
  //     <blockquote><h3>…</h3><footer><cite>Fabian Frei</cite></footer>   620
  //     <blockquote><p>…</p><footer>— Ludovic Magnin</footer>             319
  //   64 are genuinely anonymous (52 with no footer, 12 with an EMPTY one).
  //   217 distinct attributions; the longest carries a role in the same line
  //   ("Bernhard Burgener, anlässlich der Vereins-GV 2019 …"), so `author` is
  //   ONE field and is not split.
  //
  // THE EM DASH IS DRAWN HERE, NOT STORED. 319 of the 935 are stored with a
  // leading dash and 616 without. quoteAuthor() strips it so both shapes
  // render identically; storing it would render the 319 as "— — Ludovic
  // Magnin" once this arm draws its own.
  //
  // THE MARKUP IS figure > blockquote + figcaption, NOT the source's
  // blockquote > footer > cite. The spec puts attribution OUTSIDE the
  // quotation; inside it, a screen reader reads the speaker's name as the
  // quote's last sentence.
  //
  // This mirrors the BACKEND arm (NeoLandingPageBodyRenderer::renderQuote),
  // same decision and same reason as the table arm above: the backend's
  // sanitiser is a dependency-free tokeniser where the builder's is DOMPurify,
  // and matching the backend is what makes an article look the same in the
  // Shopify page body and here.
  //
  // Content shape (quoteModel.ts on aiconeo is the contract):
  //   text     : the quotation. Limited inline HTML — <a>, <strong>, <em>, <br>
  //   author?  : the attribution, plain text, no leading dash
  //   variant? : plain | bordered | card | large        (default bordered)
  //   align?   : left | center                          (default left)
  //   size?    : normal | large                (default: large iff variant large)
  //   accentColor? : colour of the rule; empty inherits the text colour
  //   quoteMark?   : draw the decorative “            (default false)
  // ============================================================

  // Inline styling travels WITH the markup, for the same reason the table's
  // does: a news body can land in a theme that never loaded the CSS this file
  // injects, or in an email client that drops <style> blocks outright, and a
  // pull-quote with no rule and no size is just a paragraph. Values copied
  // from the backend constants.
  var QUOTE_FIGURE_STYLE = 'margin:24px 0;padding:0';
  var QUOTE_TEXT_STYLE = 'margin:0;font-weight:500;font-style:normal';
  var QUOTE_CAPTION_STYLE = 'margin-top:12px;font-size:0.875rem;opacity:0.75';
  var QUOTE_CITE_STYLE = 'font-style:normal;font-weight:600';

  var QUOTE_VARIANTS = { plain: 1, bordered: 1, card: 1, large: 1 };

  // Colours accepted as an accent: a hex triplet, an rgb()/rgba()/hsl()/hsla()
  // function, or a bare CSS keyword. The value is CONCATENATED into a style="…"
  // string below, and escapeStyleValue only neutralises the quote — so
  // `red;background:url(//evil.test)` would be a CSS injection, not a colour.
  // Identical rule in the backend (CSS_COLOUR_PATTERN) and in aiconeo's
  // quoteModel.ts, so the three cannot drift on what an author may store.
  var CSS_COLOUR_RE = /^(?:#[0-9a-f]{3,8}|(?:rgb|hsl)a?\([0-9%.,\s/deg]+\)|[a-z]{3,24})$/i;

  /**
   * The attribution as a NAME.
   *
   * plainText() strips the <br>s and the stray <cite>/<unserem> the legacy
   * footers carry (7 and 1 of the 999 respectively) and collapses the
   * whitespace; the leading dash comes off after that, because it is the
   * renderer's punctuation and not part of anybody's name. Em dash, en dash
   * and plain hyphen all appear in the corpus.
   */
  function quoteAuthor(raw) {
    return plainText(raw).replace(/^[—–-]\s*/, '').replace(/^\s+|\s+$/g, '');
  }

  /** The look, with every key optional — the converter emits only text+author. */
  function quoteStyleOf(content) {
    var variant = trimStr(content.variant).toLowerCase();
    if (!QUOTE_VARIANTS[variant]) variant = 'bordered';
    var align = trimStr(content.align).toLowerCase() === 'center' ? 'center' : 'left';
    var size = trimStr(content.size).toLowerCase();
    if (size !== 'normal' && size !== 'large') size = variant === 'large' ? 'large' : 'normal';
    // `currentColor` rather than a fixed grey: it inherits the article's text
    // colour, so the rule reads correctly on a light AND a dark theme without
    // this file knowing either. A hardcoded hex is what a static body cannot
    // get right.
    var accent = trimStr(content.accentColor);
    if (!CSS_COLOUR_RE.test(accent)) accent = 'currentColor';
    return { variant: variant, align: align, size: size, accent: accent, mark: content.quoteMark === true };
  }

  renderers.quote = function (el) {
    var content = el.content || {};
    // The same tokeniser the table cells use — one allowlist (<a>, <strong>,
    // <em>, <br>), one set of rules, no second copy to drift. A quotation is
    // machine-built from scraped GrapesJS nodes exactly like a table cell is,
    // so it gets the same treatment and not the html element's verbatim trust.
    // The legacy body's <h3>/<p> wrapper unwraps to its text, which is what a
    // pull-quote wants: an <h3> here would be a heading the page never asked for.
    var text = sanitizeTableCellHtml(content.text);
    var author = quoteAuthor(content.author);
    if (!text && !author) return '';

    var s = quoteStyleOf(content);

    var figureStyle = QUOTE_FIGURE_STYLE;
    if (s.variant === 'bordered') {
      // Centred, the rule runs along the top: a leading edge has no meaning
      // without a leading side.
      figureStyle += s.align === 'center'
        ? ';border-top:3px solid ' + s.accent + ';padding-top:16px'
        : ';border-left:3px solid ' + s.accent + ';padding-left:20px';
    } else if (s.variant === 'card') {
      figureStyle += ';border-left:4px solid ' + s.accent +
        ';background:rgba(0,0,0,0.04);border-radius:8px;padding:20px';
    }
    if (s.align === 'center') figureStyle += ';text-align:center';

    var ownStyles = stylesToInline(pickStyles(el));
    if (ownStyles) figureStyle += ';' + ownStyles;

    var textStyle = QUOTE_TEXT_STYLE + (s.size === 'large'
      ? ';font-size:1.5rem;line-height:1.3'
      : ';font-size:1.125rem;line-height:1.55');

    var mark = s.mark
      ? '<span aria-hidden="true" style="opacity:0.3;padding-right:4px">&#8220;</span>'
      : '';

    var html = '<figure class="content-type-neo-quote" style="' + escapeStyleValue(figureStyle) + '">' +
      '<blockquote style="' + escapeStyleValue(textStyle) + '">' + mark + text + '</blockquote>';

    if (author) {
      // The dash is punctuation this arm owns — see the note above.
      html += '<figcaption style="' + escapeStyleValue(QUOTE_CAPTION_STYLE) + '">&#8212; ' +
        '<cite style="' + escapeStyleValue(QUOTE_CITE_STYLE) + '">' + escapeHtml(author) + '</cite>' +
        '</figcaption>';
    }

    return html + '</figure>';
  };

  // ============================================================
  // `audio`
  //
  // 157 documents, 193 Custom-HTML blobs, across the same census. Every one of
  // the 193 carries a resolvable provider and id:
  //
  //   letscast.fm  145   <script id="letscast-player-918b0437" src="…/player.js?size=m">
  //                      (128 EPISODE players + 17 SHOW players — see the arm)
  //   soundcloud    63   <iframe src="https://w.soundcloud.com/player/?url=…/tracks/843879019&…">
  //   simplecast     1   <iframe src=https://player.simplecast.com/{uuid}?dark=false>   ← UNQUOTED src
  //   libsyn         1   <iframe src="//html5-player.libsyn.com/embed/episode/id/16426175/…">
  //   radioking      1   <iframe src="https://player.radioking.io/fcsg-fm/?…">
  //   srf            1   <iframe src="//tp.srgssr.ch/p/srf/embed?urn=urn:srf:audio:{uuid}&start=">
  //
  // THE URL IS THE PAYLOAD, so `provider` is a hint the URL overrules, and the
  // embed URL is REBUILT from the recognised id rather than emitted verbatim —
  // the treatment the youtube and video arms already give their embeds. The
  // host allowlist is the sanitising step and not decoration: the letscast
  // branch below appends an EXECUTING <script>, and a document round-trips
  // through the converter, the database and the page-builder API and can be
  // hand-edited.
  //
  // `content.url` is expected already entity-DECODED — that is the converter's
  // job when it lifts the attribute off the source node, the same as it does
  // for an anchor's href. Nothing here decodes it a second time.
  //
  // THE ONE DIVERGENCE FROM THE BACKEND, and it is a platform constraint
  // rather than a choice. letscast has no iframe embed (`/embed` is a 404,
  // `/player` redirects to a login); its only embed is a <script> that mounts
  // a Podlove player beside its own tag and finds itself by an id baked into
  // the response. The backend can emit that <script> straight into body_html,
  // where the parser runs it. This file cannot: everything here is assembled
  // as a string and injected with innerHTML, and a <script> inside innerHTML
  // NEVER executes (the same trap social-embed documents above). So this arm
  // emits the identical wrapper carrying the src and the id as data
  // attributes, and hydrateAudio() below creates the real tag — landing on the
  // same final DOM as the backend's markup by a different route.
  // ============================================================

  var AUDIO_HEIGHTS = {
    letscast: 0, soundcloud: 166, simplecast: 200, libsyn: 90, radioking: 150, srf: 175, file: 54
  };

  var AUDIO_HOSTS = {
    letscast: ['letscast.fm'],
    soundcloud: ['soundcloud.com', 'w.soundcloud.com', 'api.soundcloud.com', 'on.soundcloud.com'],
    simplecast: ['player.simplecast.com', 'simplecast.com'],
    libsyn: ['html5-player.libsyn.com', 'libsyn.com'],
    radioking: ['player.radioking.io', 'radioking.com'],
    srf: ['tp.srgssr.ch', 'srgssr.ch']
  };

  var AUDIO_PROVIDER_ORDER = ['letscast', 'soundcloud', 'simplecast', 'libsyn', 'radioking', 'srf'];

  /** The id letscast's own snippet uses. Anything else and its script throws. */
  var LETSCAST_ID_RE = /^letscast-player-[A-Za-z0-9]{4,64}$/;

  /**
   * An absolute https URL, or ''. Protocol-relative is how libsyn and srf are
   * stored; the scheme is pinned rather than inherited, so a plain-http render
   * can never downgrade an embed. A relative path is rejected outright instead
   * of resolving against the visitor's origin.
   */
  function normalizeAudioUrl(raw) {
    var s = trimStr(raw);
    if (!s) return '';
    if (s.indexOf('//') === 0) s = 'https:' + s;
    else if (/^http:\/\//i.test(s)) s = 'https://' + s.slice(7);
    return /^https:\/\/[^/?#]+/i.test(s) ? s : '';
  }

  function audioHost(url) {
    var m = /^https:\/\/([^/?#:]+)/i.exec(url);
    return m ? m[1].toLowerCase() : '';
  }

  function audioHostMatches(host, allowed) {
    for (var i = 0; i < allowed.length; i++) {
      if (host === allowed[i] || host.slice(-(allowed[i].length + 1)) === '.' + allowed[i]) return true;
    }
    return false;
  }

  function audioQuery(url) {
    var i = url.indexOf('?');
    return i < 0 ? '' : url.slice(i);
  }

  function audioParam(url, name) {
    var m = new RegExp('[?&]' + name + '=([^&#]*)').exec(url);
    if (!m) return '';
    try { return decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch (e) { return m[1]; }
  }

  function detectAudioProvider(url) {
    var host = audioHost(url);
    if (!host) return '';
    for (var i = 0; i < AUDIO_PROVIDER_ORDER.length; i++) {
      if (audioHostMatches(host, AUDIO_HOSTS[AUDIO_PROVIDER_ORDER[i]])) return AUDIO_PROVIDER_ORDER[i];
    }
    // A direct media file is played by a native <audio>, which executes
    // nothing — so it needs no allowlist.
    return /\.(mp3|m4a|aac|ogg|oga|opus|wav|flac)(?:$|[?#])/i.test(url) ? 'file' : '';
  }

  /**
   * What to render, or null. Every branch returns an absolute https URL on an
   * allow-listed host, so callers may put the result straight into a src.
   */
  function audioEmbed(content) {
    var url = normalizeAudioUrl(content.url);
    if (!url) return null;
    var provider = detectAudioProvider(url);
    if (!provider) return null;

    var height = (typeof content.height === 'number' && isFinite(content.height) && content.height > 0)
      ? Math.round(content.height) : AUDIO_HEIGHTS[provider];

    var m, id;

    if (provider === 'letscast') {
      // TWO shapes, both in production. The census's "145 letscast embeds" is 128 EPISODE
      // players in 101 documents PLUS 17 SHOW players in 17 documents:
      //   …/podcasts/{show}/episodes/{episode}/player.js?size=m
      //   …/podcasts/{show}/player.js?size=l&hidePlaylist=true
      // Reading only the first drops the other 17 silently, which is the exact
      // failure this file exists to prevent. All 17 share one anchor id — letscast
      // has only one show player to identify.
      m = /^https:\/\/letscast\.fm\/(?:podcasts|sites)\/([^/?#]+)(?:\/(?:episodes|episode)\/([^/?#]+))?/i.exec(url);
      if (!m) return null;
      var base = 'https://letscast.fm/podcasts/' + m[1] + (m[2] ? '/episodes/' + m[2] : '');
      // The PUBLIC page. `/podcasts/…` is letscast's ADMIN path and 404s for a
      // visitor (measured); `/sites/…` is the link letscast itself puts in its
      // player payload. A fallback that lands on a 404 is worse than none.
      var page = 'https://letscast.fm/sites/' + m[1] + (m[2] ? '/episode/' + m[2] : '');
      // `size` and `hidePlaylist` are the author's sizing, carried through: a
      // playlist needs the room, which is why every show player asks for size=l.
      // VALIDATED, not trusted: one stored URL in the corpus reads `size=l«`
      // (FCSG news 7975 — a stray guillemet somebody typed), and forwarding that
      // asks letscast for a size it does not have. Same rule as the backend, so
      // the two renderers do not disagree about one article.
      var size = audioParam(url, 'size');
      if (!/^[a-z]{1,4}$/i.test(size)) size = 'm';
      var query = '?size=' + encodeURIComponent(size) +
        (audioParam(url, 'hidePlaylist') === 'true' ? '&hidePlaylist=true' : '');
      var domId = trimStr(content.embedId);
      // No id means letscast's script would call .parentNode on null and take
      // the page's other scripts down with it. A link is a worse player and a
      // much better failure.
      if (!LETSCAST_ID_RE.test(domId)) return { kind: 'link', href: page, provider: provider };
      return { kind: 'script', src: base + '/player.js' + query, domId: domId, provider: provider };
    }

    if (provider === 'soundcloud') {
      var host = audioHost(url);
      var target = '';
      if (host === 'w.soundcloud.com') {
        target = audioParam(url, 'url');
        if (target.indexOf('//') === 0) target = 'https:' + target;
      } else if (host === 'api.soundcloud.com') {
        target = url;
      } else {
        // SoundCloud's own caption markup puts the ARTIST's profile link beside
        // every embed — 126 such links against 63 real players in the corpus.
        // A profile is not a track; embedding one plays the artist's latest.
        if (url.replace(/^https:\/\/[^/]+\//i, '').replace(/[?#].*$/, '').split('/').length < 2) return null;
        target = url;
      }
      var inner = normalizeAudioUrl(target);
      if (!inner || !audioHostMatches(audioHost(inner), AUDIO_HOSTS.soundcloud)) return null;
      return {
        kind: 'iframe', height: height, provider: provider,
        src: 'https://w.soundcloud.com/player/?url=' + encodeURIComponent(inner) +
          '&auto_play=false&hide_related=true&show_comments=false&show_teaser=false&visual=false'
      };
    }

    if (provider === 'simplecast') {
      id = url.replace(/^https:\/\/[^/]+\/?/i, '').replace(/[?#].*$/, '').split('/')[0];
      if (!/^[A-Za-z0-9-]{8,}$/.test(id)) return null;
      return { kind: 'iframe', src: 'https://player.simplecast.com/' + id + '?dark=false', height: height, provider: provider };
    }

    if (provider === 'libsyn') {
      m = /\/id\/(\d+)/.exec(url);
      if (!m) return null;
      return {
        kind: 'iframe', height: height, provider: provider,
        src: 'https://html5-player.libsyn.com/embed/episode/id/' + m[1] + '/height/' + height +
          '/theme/standard/direction/forward/render-playlist/no/'
      };
    }

    if (provider === 'radioking') {
      id = url.replace(/^https:\/\/[^/]+\/?/i, '').replace(/[?#].*$/, '').split('/')[0];
      if (!/^[A-Za-z0-9_-]{1,64}$/.test(id)) return null;
      // The query carries the station's own colours — the club's, so it stays.
      return { kind: 'iframe', src: 'https://player.radioking.io/' + id + '/' + audioQuery(url), height: height, provider: provider };
    }

    if (provider === 'srf') {
      var urn = audioParam(url, 'urn');
      if (!/^urn:srf:audio:[A-Za-z0-9-]+$/.test(urn)) return null;
      return { kind: 'iframe', src: 'https://tp.srgssr.ch/p/srf/embed?urn=' + encodeURIComponent(urn), height: height, provider: provider };
    }

    return { kind: 'file', src: url, provider: 'file' };
  }

  renderers.audio = function (el) {
    var content = el.content || {};
    var embed = audioEmbed(content);
    var title = plainText(content.title);
    var open = '<div class="content-type-neo-audio"' + inlineStyleAttr(el) + '>';
    var caption = title
      ? '<p class="neo-audio__caption" style="margin:8px 0 0;font-size:0.875rem;opacity:0.75">' + escapeHtml(title) + '</p>'
      : '';

    if (!embed) {
      // An unresolvable address is not a reason to drop the block: the caption
      // is authored copy, and a silent '' is the failure this whole file exists
      // to prevent. With no caption either there is genuinely nothing to draw.
      if (!title) return '';
      warnType('audio', 'no supported provider for "' + trimStr(content.url) + '"; rendered its caption only');
      return open + caption + '</div>';
    }

    if (embed.kind === 'script') {
      // See the divergence note above: the tag itself cannot be written here.
      return open +
        '<div class="neo-audio__mount" data-neo-audio="1" data-neo-audio-src="' + escapeAttr(embed.src) +
        '" data-neo-audio-id="' + escapeAttr(embed.domId) + '"></div>' +
        caption + '</div>';
    }

    if (embed.kind === 'link') {
      // The link already carries the caption as its label; repeating it below
      // would print the episode title twice.
      return open + '<a href="' + escapeAttr(embed.href) + '" target="_blank" rel="noopener noreferrer">' +
        escapeHtml(title || 'Podcast-Folge anhören') + '</a></div>';
    }

    if (embed.kind === 'file') {
      return open + '<audio src="' + escapeAttr(embed.src) +
        '" controls preload="none" style="width:100%;display:block"></audio>' + caption + '</div>';
    }

    return open + '<iframe src="' + escapeAttr(embed.src) +
      '" title="' + escapeAttr(title || 'Audio / Podcast') + '"' +
      ' width="100%" height="' + embed.height + '"' +
      ' style="border:0;display:block;width:100%;height:' + embed.height + 'px"' +
      ' loading="lazy" allow="autoplay; encrypted-media; clipboard-write"></iframe>' +
      caption + '</div>';
  };

  /**
   * Create the provider's real <script> for one mounted audio block.
   *
   * The ONLY place in this engine that injects executing third-party code, and
   * the reason it can be done safely is that `src` was already rebuilt from an
   * allow-listed host by audioEmbed() — this function never sees a raw document
   * value. Idempotent on the DOM rather than on a flag, so a re-render or a
   * second hydrate() pass does not stack players: the provider looks ITSELF up
   * by `id`, so a duplicate id anywhere in the document would mount both
   * players onto the first one found.
   */
  function hydrateAudio(host) {
    if (!host || host.getAttribute('data-neo-audio-mounted') === '1') return;
    var src = host.getAttribute('data-neo-audio-src');
    var id = host.getAttribute('data-neo-audio-id');
    if (!src || !id || !LETSCAST_ID_RE.test(id)) return;
    if (document.getElementById(id)) return;
    host.setAttribute('data-neo-audio-mounted', '1');
    try {
      var script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;
      host.appendChild(script);
    } catch (e) { /* no-op: a blocked script must not take the article with it */ }
  }

  // ============================================================
  // Types the builder renders from AUTHORED content but the Shopify body
  // renderer has no arm for. Their copy lives in the document, so a client
  // renderer can show all of it; only editor-side affordances (drag handles,
  // upload zones, scroll-spy) are left out. Each deviation is noted inline.
  // ============================================================

  renderers['file-list'] = function (el) {
    var content = el.content || {};
    var list = listOf(content.items);
    var items = '';
    for (var i = 0; i < list.length; i++) {
      var name = trimStr(list[i].name);
      var href = normalizeUrl(trimStr(list[i].fileUrl));
      if (!name && !href) continue;
      var fileType = trimStr(list[i].fileType);
      var meta = [fileType, trimStr(list[i].fileSize)].filter(Boolean).join(' · ');
      // Deviation: the editor's drop zone / progress bar are editor-only; the
      // published view is a list of download links.
      items += '<li class="neo-file-list__item">' +
        (href ? '<a class="neo-file-list__link" href="' + escapeAttr(href) + '" download>' : '<span class="neo-file-list__link">') +
        '<span class="neo-file-list__icon" aria-hidden="true">' + escapeHtml((fileType || 'file').toUpperCase().slice(0, 4)) + '</span>' +
        '<span class="neo-file-list__name">' + escapeHtml(name) + '</span>' +
        (meta ? '<span class="neo-file-list__meta">' + escapeHtml(meta) + '</span>' : '') +
        (href ? '</a>' : '</span>') + '</li>';
    }
    var title = trimStr(content.title);
    var subtitle = trimStr(content.subtitle);
    if (!items && !title && !subtitle) return '';

    var html = '<div class="content-type-neo-file-list"' + inlineStyleAttr(el) + '>';
    if (title || subtitle) {
      html += '<div class="neo-file-list__header">';
      if (title) html += '<h3 class="neo-file-list__title">' + escapeHtml(title) + '</h3>';
      if (subtitle) html += '<p class="neo-file-list__subtitle">' + escapeHtml(subtitle) + '</p>';
      html += '</div>';
    }
    if (items) html += '<ul class="neo-file-list__items">' + items + '</ul>';
    return html + '</div>';
  };

  renderers.cta = function (el) {
    var content = el.content || {};
    var headline = trimStr(content.headline);
    var description = trimStr(content.description);
    var buttonText = trimStr(content.buttonText);
    if (!headline && !description && !buttonText) return '';
    var url = normalizeUrl(trimStr(content.buttonUrl));
    var html = '<div class="content-type-neo-cta"' + inlineStyleAttr(el) + '>';
    if (headline) html += '<h2 class="neo-cta__headline">' + escapeHtml(headline) + '</h2>';
    if (description) html += '<p class="neo-cta__description">' + escapeHtml(description) + '</p>';
    if (buttonText) {
      html += '<a class="neo-cta__button button button--primary"' +
        (url ? ' href="' + escapeAttr(url) + '"' : '') + '>' + escapeHtml(buttonText) + '</a>';
    }
    return html + '</div>';
  };

  var HERO_POSITIONS = {
    'top-left': ['flex-start', 'flex-start'], 'top-center': ['flex-start', 'center'], 'top-right': ['flex-start', 'flex-end'],
    'center-left': ['center', 'flex-start'], 'center': ['center', 'center'], 'center-right': ['center', 'flex-end'],
    'bottom-left': ['flex-end', 'flex-start'], 'bottom-center': ['flex-end', 'center'], 'bottom-right': ['flex-end', 'flex-end']
  };

  renderers.hero = function (el) {
    var content = el.content || {};
    var headline = trimStr(content.headline);
    var subheadline = trimStr(content.subheadline);
    var buttonText = trimStr(content.buttonText);
    var background = normalizeUrl(trimStr(content.backgroundImage));
    if (!headline && !subheadline && !buttonText && !background) return '';

    var pos = HERO_POSITIONS[content.contentPosition || 'center'] || HERO_POSITIONS.center;
    var own = {
      position: 'relative', display: 'flex', flexDirection: 'column',
      justifyContent: pos[0], alignItems: pos[1], width: '100%'
    };
    if (background) {
      // Parenthesis/quote strip so a url() payload cannot close the declaration.
      own.backgroundImage = 'url(' + background.replace(/[()"']/g, '') + ')';
      own.backgroundSize = 'cover';
      own.backgroundPosition = content.backgroundPosition || 'center';
    }
    if (trimStr(content.heroHeight)) own.minHeight = trimStr(content.heroHeight);

    var merged = stylesToInline(pickStyles(el));
    var mine = stylesToInline(own);
    var style = merged ? merged + ';' + mine : mine;

    // Deviation: the builder's `sectionBlocks` ordering model is not carried
    // over — blocks render in its default headline → subheadline → button order.
    var html = '<div class="content-type-neo-hero" style="' + escapeStyleValue(style) + '">';
    var overlay = Number(content.overlayOpacity || 0);
    if (overlay > 0) {
      html += '<div class="neo-hero__overlay" style="position:absolute;inset:0;background:rgba(0,0,0,' +
        Math.max(0, Math.min(overlay > 1 ? overlay / 100 : overlay, 1)) + ');"></div>';
    }
    html += '<div class="neo-hero__content" style="position:relative;">';
    if (headline) html += '<h1 class="neo-hero__headline">' + escapeHtml(headline) + '</h1>';
    if (subheadline) html += '<p class="neo-hero__subheadline">' + escapeHtml(subheadline) + '</p>';
    if (buttonText) {
      var url = normalizeUrl(trimStr(content.buttonUrl));
      html += '<a class="neo-hero__button button button--primary"' +
        (url ? ' href="' + escapeAttr(url) + '"' : '') + '>' + escapeHtml(buttonText) + '</a>';
    }
    return html + '</div></div>';
  };

  renderers['anchor-nav'] = function (el) {
    var content = el.content || {};
    var tabs = listOf(content.tabs);
    var links = '';
    for (var i = 0; i < tabs.length; i++) {
      var label = trimStr(tabs[i].label);
      if (!label) continue;
      // Only an id-shaped target becomes an anchor; a builder section id that
      // has no counterpart in the rendered DOM degrades to plain text.
      var target = trimStr(tabs[i].targetSectionId);
      var safe = /^[A-Za-z0-9_-]+$/.test(target) ? target : '';
      links += '<a class="neo-anchor-nav__tab"' +
        (safe ? ' href="#section-' + escapeAttr(safe) + '"' : '') + '>' + escapeHtml(label) + '</a>';
    }
    if (!links) return '';
    // Deviation: `mode: overlay` (a scroll-spy overlay bar) renders inline.
    return '<div class="content-type-neo-anchor-nav"' + inlineStyleAttr(el) + '>' +
      '<nav class="neo-anchor-nav' + (content.layout === 'vertical' ? ' neo-anchor-nav--vertical' : '') + '">' +
      links + '</nav></div>';
  };

  renderers.marquee = function (el) {
    var content = el.content || {};
    var list = listOf(content.items);
    var items = '';
    for (var i = 0; i < list.length; i++) {
      var text = trimStr(list[i].text);
      var src = normalizeUrl(trimStr(list[i].src));
      if (!text && !src) continue;
      items += '<div class="neo-marquee__item">' + (src
        ? '<img src="' + escapeAttr(src) + '" alt="' + escapeAttr(text) + '" loading="lazy">'
        : '<span>' + escapeHtml(text) + '</span>') + '</div>';
    }
    if (!items) return '';
    var speed = Number(content.speed);
    if (!isFinite(speed) || speed <= 0) speed = 30;
    return '<div class="content-type-neo-marquee"' + inlineStyleAttr(el) + '>' +
      '<div class="neo-marquee' + (content.showBorder !== false ? ' neo-marquee--bordered' : '') + '">' +
      '<div class="neo-marquee__track' + (content.pauseOnHover !== false ? ' neo-marquee__track--pausable' : '') +
      '" style="animation-duration:' + speed + 's;animation-direction:' +
      (content.direction === 'right' ? 'reverse' : 'normal') + ';">' +
      items + '</div></div></div>';
  };

  renderers.multicolumn = function (el) {
    var content = el.content || {};
    // `columns` here is a list of {icon, title, description} cards, NOT the
    // `columns` ELEMENT type — the name collision is the builder's.
    var list = listOf(content.columns);
    var items = '';
    for (var i = 0; i < list.length; i++) {
      var title = trimStr(list[i].title);
      var description = trimStr(list[i].description);
      if (!title && !description) continue;
      // Icons are lucide component names in the builder bundle; no static
      // counterpart, so the text is the content.
      items += '<div class="neo-multicolumn__item">' +
        (title ? '<h3>' + escapeHtml(title) + '</h3>' : '') +
        (description ? '<p>' + escapeHtml(description) + '</p>' : '') + '</div>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-multicolumn' +
      (content.layout === 'vertical' ? ' content-type-neo-multicolumn--vertical' : '') + '"' +
      inlineStyleAttr(el) + '>' + items + '</div>';
  };

  // A live countdown needs a ticking clock, which is script — so it renders the
  // target date as readable text and the hydrator upgrades it to a live timer.
  renderers.countdown = function (el) {
    var content = el.content || {};
    var target = trimStr(content.targetDate);
    var ms = Date.parse(target);
    if (!target || isNaN(ms)) return '';
    return '<div class="content-type-neo-countdown"' + inlineStyleAttr(el) +
      ' data-neo-countdown="' + escapeAttr(new Date(ms).toISOString()) + '"' +
      ' data-show-days="' + (content.showDays !== false ? '1' : '0') + '"' +
      ' data-show-hours="' + (content.showHours !== false ? '1' : '0') + '"' +
      ' data-show-minutes="' + (content.showMinutes !== false ? '1' : '0') + '"' +
      ' data-show-seconds="' + (content.showSeconds !== false ? '1' : '0') + '">' +
      '<time class="neo-countdown__target" datetime="' + escapeAttr(new Date(ms).toISOString()) + '">' +
      escapeHtml(new Date(ms).toISOString().slice(0, 16).replace('T', ' ')) + '</time></div>';
  };

  // Tabs degrade to <details> panels: no script, every panel reachable, and no
  // content hidden behind a control that never gets wired.
  renderers.tabs = function (el) {
    var content = el.content || {};
    var list = listOf(content.tabs);
    var items = '';
    for (var i = 0; i < list.length; i++) {
      var title = plainText(list[i].title);
      var body = trimStr(list[i].content);
      if (!title && !body) continue;
      items += '<details class="neo-tabs__panel"' + (i === 0 ? ' open' : '') + '>' +
        '<summary>' + escapeHtml(title) + '</summary>' +
        '<div class="neo-tabs__content">' + body + '</div></details>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-tabs"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  renderers['announcement-bar'] = function (el) {
    var content = el.content || {};
    var text = trimStr(content.text);
    var linkText = trimStr(content.linkText);
    if (!text && !linkText) return '';
    var url = normalizeUrl(trimStr(content.linkUrl));
    // Deviation: `dismissible` needs per-visitor state the renderer does not
    // own, so the bar is always shown.
    return '<div class="content-type-neo-announcement-bar content-type-neo-announcement-bar--' +
      escapeAttr(trimStr(content.style) || 'primary') + '"' + inlineStyleAttr(el) + '>' +
      (text ? '<span class="neo-announcement__text">' + escapeHtml(text) + '</span>' : '') +
      (linkText ? '<a class="neo-announcement__link"' + (url ? ' href="' + escapeAttr(url) + '"' : '') + '>' +
        escapeHtml(linkText) + '</a>' : '') + '</div>';
  };

  // A card rail. Reuses the media-gallery carousel hydrator's markup contract
  // so arrows/scrubber come for free instead of a second implementation.
  renderers.carousel = function (el) {
    var content = el.content || {};
    var list = listOf(content.items);
    var cards = '';
    for (var i = 0; i < list.length; i++) {
      var image = normalizeUrl(trimStr(list[i].image));
      var title = trimStr(list[i].title);
      var description = trimStr(list[i].description);
      if (!image && !title && !description) continue;
      cards += '<figure class="neo-media-gallery__item neo-carousel__card">';
      if (image) cards += '<img src="' + escapeAttr(image) + '" alt="' + escapeAttr(title) + '" loading="lazy">';
      if ((content.showTitle !== false && title) || (content.showDescription !== false && description)) {
        cards += '<figcaption class="neo-carousel__caption">' +
          (content.showTitle !== false && title ? '<h3>' + escapeHtml(title) + '</h3>' : '') +
          (content.showDescription !== false && description ? '<p>' + escapeHtml(description) + '</p>' : '') +
          '</figcaption>';
      }
      cards += '</figure>';
    }
    if (!cards) return '';
    var width = parseInt(content.cardWidth, 10);
    if (!isFinite(width) || width < 80) width = 300;
    return '<div class="content-type-neo-carousel"' + inlineStyleAttr(el) + '>' +
      '<div class="neo-media-gallery__carousel" data-neo-mg-carousel="1" data-cols="3">' +
      '<div class="neo-media-gallery__carousel-track" style="--neo-carousel-card:' + width + 'px;">' +
      cards + '</div>' +
      '<button type="button" class="neo-media-gallery__nav neo-media-gallery__nav--prev" aria-label="Previous">&#10094;</button>' +
      '<button type="button" class="neo-media-gallery__nav neo-media-gallery__nav--next" aria-label="Next">&#10095;</button>' +
      '</div></div>';
  };

  renderers.pricing = function (el) {
    var content = el.content || {};
    var list = listOf(content.plans);
    var plans = '';
    for (var i = 0; i < list.length; i++) {
      var name = trimStr(list[i].name);
      var price = trimStr(list[i].price);
      if (!name && !price) continue;
      var features = '';
      var featureList = Array.isArray(list[i].features) ? list[i].features : [];
      for (var f = 0; f < featureList.length; f++) {
        var label = trimStr(featureList[f]);
        if (label) features += '<li>' + escapeHtml(label) + '</li>';
      }
      var period = trimStr(list[i].period);
      plans += '<div class="neo-pricing__plan' + (list[i].highlighted ? ' is-highlighted' : '') + '">' +
        (name ? '<h3>' + escapeHtml(name) + '</h3>' : '') +
        (price ? '<p class="neo-pricing__price">' + escapeHtml(price) +
          (period ? '<span class="neo-pricing__period"> / ' + escapeHtml(period) + '</span>' : '') + '</p>' : '') +
        (features ? '<ul class="neo-pricing__features">' + features + '</ul>' : '') +
        '</div>';
    }
    if (!plans) return '';
    return '<div class="content-type-neo-pricing"' + inlineStyleAttr(el) + '>' + plans + '</div>';
  };

  renderers.team = function (el) {
    var content = el.content || {};
    // The palette default is source-driven (`source: 'aico-staff'`) but the
    // builder renders `content.members`, which IS authored — so an authored
    // roster renders, and a source-only element falls through to the
    // unknown-type path where its absence is at least visible.
    var list = listOf(content.members);
    if (list.length === 0) {
      return renderUnresolvable(el, 'team has no authored `members`; source "' +
        (trimStr(content.source) || 'unset') + '" needs the AICO backend');
    }
    var items = '';
    for (var i = 0; i < list.length; i++) {
      var name = trimStr(list[i].name);
      var role = trimStr(list[i].role);
      var bio = trimStr(list[i].bio);
      if (!name && !role && !bio) continue;
      var avatar = normalizeUrl(trimStr(list[i].avatar));
      items += '<div class="neo-team__member">' +
        (content.showAvatar !== false && avatar
          ? '<img src="' + escapeAttr(avatar) + '" alt="' + escapeAttr(name) + '" loading="lazy">' : '') +
        (name ? '<h3>' + escapeHtml(name) + '</h3>' : '') +
        (content.showRole !== false && role ? '<p class="neo-team__role">' + escapeHtml(role) + '</p>' : '') +
        (content.showBio !== false && bio ? '<p class="neo-team__bio">' + escapeHtml(bio) + '</p>' : '') +
        '</div>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-team"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  renderers.reviews = function (el) {
    var content = el.content || {};
    var list = listOf(content.items).length ? listOf(content.items) : listOf(content.reviews);
    if (list.length === 0) {
      return renderUnresolvable(el, 'reviews carries no authored items; source "' +
        (trimStr(content.source) || 'unset') + '" is resolved by the AICO backend');
    }
    var items = '';
    for (var i = 0; i < list.length; i++) {
      var body = trimStr(list[i].text) || trimStr(list[i].quote) || trimStr(list[i].body);
      var author = trimStr(list[i].author) || trimStr(list[i].name);
      if (!body && !author) continue;
      var rating = parseInt(list[i].rating, 10);
      var stars = '';
      if (content.showRating !== false && isFinite(rating) && rating > 0) {
        for (var s = 0; s < Math.min(rating, 5); s++) stars += '&#9733;';
      }
      items += '<blockquote class="neo-reviews__item">' +
        (stars ? '<span class="rating" aria-label="' + Math.min(rating, 5) + '/5">' + stars + '</span>' : '') +
        (body ? '<p>' + escapeHtml(body) + '</p>' : '') +
        (author ? '<footer><cite>' + escapeHtml(author) + '</cite></footer>' : '') +
        '</blockquote>';
    }
    if (!items) return '';
    return '<div class="content-type-neo-reviews"' + inlineStyleAttr(el) + '>' + items + '</div>';
  };

  // ============================================================
  // Forms
  //
  // `form` is a DESIGN element: the builder stores the field list but no
  // destination, so the fields render as a disabled preview rather than a form
  // that silently drops what a visitor types.
  //
  // `linked-form` IS submittable: the element carries `submitEndpoint`,
  // stamped precisely so external/headless renderers can POST it, and the
  // field list comes from the PUBLIC read endpoint derived from that URL — no
  // per-theme constant, so this belongs in the shared engine.
  // ============================================================

  function formControlFor(type) {
    switch (String(type || '').toLowerCase()) {
      case 'textarea':
      case 'text_area': return { kind: 'textarea' };
      case 'dropdown':
      case 'select': return { kind: 'select' };
      case 'radio': return { kind: 'radio' };
      case 'checkbox': return { kind: 'checkbox' };
      case 'file_upload':
      case 'multiple_file_upload':
      case 'file': return { kind: 'input', inputType: 'file' };
      case 'email': return { kind: 'input', inputType: 'email' };
      case 'phone':
      case 'tel': return { kind: 'input', inputType: 'tel' };
      case 'number': return { kind: 'input', inputType: 'number' };
      case 'date': return { kind: 'input', inputType: 'date' };
      default: return { kind: 'input', inputType: 'text' };
    }
  }

  renderers.form = function (el) {
    var content = el.content || {};
    var list = listOf(content.fields);
    var fields = '';
    for (var i = 0; i < list.length; i++) {
      var label = trimStr(list[i].label);
      var placeholder = trimStr(list[i].placeholder);
      var control = formControlFor(list[i].type);
      var required = list[i].required ? ' <span class="aico_label__required" aria-hidden="true">*</span>' : '';
      var body;
      if (control.kind === 'textarea') {
        body = '<textarea class="aico_input" rows="5" placeholder="' + escapeAttr(placeholder) + '" disabled></textarea>';
      } else if (control.kind === 'select') {
        body = '<select class="aico_input" disabled><option value=""></option></select>';
      } else {
        body = '<input class="aico_input" type="' + control.inputType +
          '" placeholder="' + escapeAttr(placeholder) + '" disabled>';
      }
      fields += '<div class="form-item">' +
        (content.showLabels !== false && label
          ? '<label class="aico_label">' + escapeHtml(label) + required + '</label>' : '') +
        body + '</div>';
    }
    if (!fields) return '';
    var submitText = trimStr(content.submitText);
    // Deliberately inert: a `form` element names no destination, and a form
    // that looks live but posts nowhere loses the visitor's message.
    return '<div class="content-type-neo-form"' + inlineStyleAttr(el) + ' data-neo-form-inert="1">' +
      '<div class="aico-custom-form__fields">' + fields + '</div>' +
      (submitText ? '<button type="button" class="aico-custom-form__submit" disabled>' +
        escapeHtml(submitText) + '</button>' : '') +
      '</div>';
  };

  renderers['linked-form'] = function (el) {
    var content = el.content || {};
    var formId = content.formId || content.linkedFormId;
    var endpoint = trimStr(content.submitEndpoint);
    if (!formId || !endpoint) {
      return renderUnresolvable(el, 'linked-form is missing ' +
        (!formId ? 'formId' : 'submitEndpoint') + ', so its fields cannot be resolved');
    }
    return '<div class="content-type-neo-linked-form aico-custom-form-wrap"' + inlineStyleAttr(el) +
      ' data-neo-form="1"' +
      ' data-neo-form-id="' + escapeAttr(formId) + '"' +
      ' data-neo-form-endpoint="' + escapeAttr(endpoint) + '"' +
      ' data-neo-form-title="' + (content.showTitle === true ? '1' : '') + '">' +
      '<div class="aico-form-banner aico-form-banner--success" hidden role="status"></div>' +
      '<div class="aico-form-banner aico-form-banner--error" hidden role="alert"></div>' +
      '<div class="aico-form-slot"></div></div>';
  };

  renderers['booking-embed'] = function (el) {
    var content = el.content || {};
    var embedCode = trimStr(content.embedCode);
    var embedUrl = trimStr(content.embedUrl);
    var title = trimStr(content.title);
    var description = trimStr(content.description);

    var head = (title ? '<h3>' + escapeHtml(title) + '</h3>' : '') +
      (description ? '<p>' + escapeHtml(description) + '</p>' : '');

    if (embedCode) {
      return '<div class="content-type-neo-booking-embed"' + inlineStyleAttr(el) + '>' + head + embedCode + '</div>';
    }
    if (/^https:\/\//i.test(embedUrl)) {
      return '<div class="content-type-neo-booking-embed"' + inlineStyleAttr(el) + '>' + head +
        '<iframe src="' + escapeAttr(embedUrl) + '" title="' + escapeAttr(title || 'Booking') + '"' +
        ' width="100%" height="640" style="border:0" loading="lazy"></iframe></div>';
    }
    if (head) return '<div class="content-type-neo-booking-embed"' + inlineStyleAttr(el) + '>' + head + '</div>';
    return '';
  };

  // ============================================================
  // Server-hydrated storefront types
  //
  // product-card / product-grid / product-carousel / brand-news /
  // brand-gender-links carry NO data of their own: the loader attaches
  // `content.aico_hydrated` (see Modules/Storefront/Services/NeoContentHydrator).
  // When it is there, it renders. When it is not — and on the NEWS payload it
  // never is, because NewsResource does not run the hydrator — there is nothing
  // to render faithfully, so the element takes the unknown-type path and says
  // so out loud rather than emitting an empty grid.
  // ============================================================

  function renderProductList(el, products) {
    var content = el.content || {};
    var cards = '';
    for (var i = 0; i < products.length; i++) {
      var p = products[i] || {};
      var title = trimStr(p.title);
      var url = normalizeUrl(trimStr(p.url));
      var image = normalizeUrl(trimStr(p.image_url));
      if (!title && !image) continue;
      var inner =
        (image ? '<img src="' + escapeAttr(image) + '" alt="' + escapeAttr(title) + '" loading="lazy">' : '') +
        (title ? '<h3 class="neo-product__title">' + escapeHtml(title) + '</h3>' : '') +
        (content.showPrice !== false && trimStr(p.price_label)
          ? '<p class="neo-product__price">' + escapeHtml(trimStr(p.price_label)) +
            (trimStr(p.compare_at_price_label)
              ? ' <s class="neo-product__compare">' + escapeHtml(trimStr(p.compare_at_price_label)) + '</s>' : '') +
            '</p>' : '');
      var badge = p.badge && typeof p.badge === 'object' ? trimStr(p.badge.label) : '';
      if (badge) {
        inner = '<span class="neo-product__badge neo-product__badge--' +
          escapeAttr(trimStr(p.badge.kind) || 'default') + '">' + escapeHtml(badge) + '</span>' + inner;
      }
      cards += '<div class="neo-product">' +
        (url ? '<a href="' + escapeAttr(url) + '">' + inner + '</a>' : inner) + '</div>';
    }
    if (!cards) return '';
    var cols = parseInt(content.columns, 10);
    if (!isFinite(cols) || cols < 1) cols = 3;
    if (cols > 6) cols = 6;
    return '<div class="content-type-neo-products" style="grid-template-columns:repeat(' + cols + ',1fr)">' +
      cards + '</div>';
  }

  function productRenderer(el) {
    var content = el.content || {};
    var hydrated = content.aico_hydrated;
    var products = (hydrated && Array.isArray(hydrated.products)) ? hydrated.products : null;
    if (!products) {
      return renderUnresolvable(el, el.type + ' has no content.aico_hydrated.products; ' +
        'the shop payload must be hydrated server-side before it can render');
    }
    var list = renderProductList(el, products);
    if (!list) return '';
    return '<div class="content-type-neo-' + escapeAttr(el.type) + '"' + inlineStyleAttr(el) + '>' + list + '</div>';
  }

  renderers['product-card'] = productRenderer;
  renderers['product-grid'] = productRenderer;
  renderers['product-carousel'] = productRenderer;

  renderers['brand-news'] = function (el) {
    var content = el.content || {};
    var hydrated = content.aico_hydrated;
    var news = (hydrated && Array.isArray(hydrated.news)) ? hydrated.news : null;
    var heading = trimStr(content.heading);
    if (!news) {
      return renderUnresolvable(el, 'brand-news has no content.aico_hydrated.news ' +
        '(brand-scoped, resolved server-side)');
    }
    var items = '';
    for (var i = 0; i < news.length; i++) {
      var title = trimStr(news[i].title);
      var url = normalizeUrl(trimStr(news[i].url));
      var image = normalizeUrl(trimStr(news[i].image));
      if (!title && !image) continue;
      var inner = (image ? '<img src="' + escapeAttr(image) + '" alt="' + escapeAttr(title) + '" loading="lazy"' +
        (trimStr(news[i].object_position) ? ' style="object-position:' +
          escapeStyleValue(trimStr(news[i].object_position)) + '"' : '') + '>' : '') +
        (title ? '<h3>' + escapeHtml(title) + '</h3>' : '');
      items += '<div class="neo-brand-news__item">' +
        (url ? '<a href="' + escapeAttr(url) + '">' + inner + '</a>' : inner) + '</div>';
    }
    if (!items && !heading) return '';
    return '<div class="content-type-neo-brand-news"' + inlineStyleAttr(el) + '>' +
      (heading ? '<h2>' + escapeHtml(heading) + '</h2>' : '') + items + '</div>';
  };

  renderers['brand-gender-links'] = function (el) {
    var content = el.content || {};
    var hydrated = content.aico_hydrated || {};
    var pairs = [[trimStr(content.womenLabel), trimStr(hydrated.women_url)],
                 [trimStr(content.menLabel), trimStr(hydrated.men_url)]];
    var links = '';
    var resolved = 0;
    for (var i = 0; i < pairs.length; i++) {
      if (!pairs[i][0]) continue;
      var url = normalizeUrl(pairs[i][1]);
      if (url) resolved++;
      // The label is authored, so it shows either way; only the target needs
      // the server.
      links += url
        ? '<a class="neo-gender-link" href="' + escapeAttr(url) + '">' + escapeHtml(pairs[i][0]) + '</a>'
        : '<span class="neo-gender-link">' + escapeHtml(pairs[i][0]) + '</span>';
    }
    if (!links) return '';
    if (resolved === 0) {
      warnType(el.type, 'brand-gender-links has no content.aico_hydrated URLs; labels rendered without targets');
    }
    return '<div class="content-type-neo-brand-gender-links"' + inlineStyleAttr(el) + '>' + links + '</div>';
  };


  // ============================================================
  // Backend-resolved types: an EXPLICIT decision each, not a fall-through.
  //
  // These nine name a source the AICO backend resolves at view time
  // (a collection, a CRM list, a navigation tree, a booking calendar) and the
  // document carries none of the resulting data. There is no faithful
  // client-side rendering, and an approximation — an empty grid, a dead form,
  // a fake calendar — would be worse than the truth, so each shows the copy it
  // DOES carry and reports precisely what is missing.
  //
  // They are registered rather than left to renderUnknown on purpose: that
  // keeps the unknown path a net for element types that do not exist yet,
  // which is the only thing it should ever catch. Moving one of these to a
  // real renderer means the payload started carrying its data — check that
  // first, then delete its arm here.
  // ============================================================

  function unresolvableType(reason) {
    return function (el) { return renderUnresolvable(el, reason); };
  }

  // blog-feed and dynamic-list LEFT this list in 2026-09 — for the two sources AICO now
  // answers at display time, and for those only. See section "Runtime lists" below.
  renderers['blog-feed'] = runtimeListRenderer(
    'blog-feed',
    'aico-news',
    'blog-feed lists posts resolved server-side (content.source, no items in the document)');
  renderers['dynamic-list'] = runtimeListRenderer(
    'dynamic-list',
    'collection:matches',
    'dynamic-list resolves content.source (e.g. "collection:posts") through the AICO backend');
  renderers['crm-list'] = unresolvableType(
    'crm-list resolves its roster live from the crm-lists API; the document holds only the (entityType, group, tag) triple');
  renderers['dealer-map'] = unresolvableType(
    'dealer-map needs the live dealer feed and a Maps SDK, neither of which is in the document');
  renderers['navigation'] = unresolvableType(
    'navigation renders the menu tree behind content.navigationId, resolved server-side');
  renderers.booking = unresolvableType(
    'booking needs the AICO booking backend for availability; only its headline/description are authored');
  renderers.chatbot = unresolvableType(
    'chatbot needs a live AICO session; only its headline/welcome message are authored');
  renderers.newsletter = unresolvableType(
    'newsletter has no submit endpoint in the document, and a signup box that posts nowhere loses the address');

  // category-products carries no ids of its own (just categoryId), but the
  // hydrator fills the SAME aico_hydrated.products contract as the other
  // product elements — so it renders wherever that ran, and reports otherwise.
  renderers['category-products'] = productRenderer;

  // ============================================================
  // Runtime lists — the two element types AICO resolves WHEN THE PAGE IS SHOWN
  //
  // `dynamic-list` (source "collection:matches") is a team's agenda or its
  // call-up list; `blog-feed` (source "aico-news") is a filtered news strip.
  // Both are backend-resolved, like the eight types above — but unlike those,
  // AICO now serves them through a public, read-only, CORS-enabled endpoint,
  // and the element carries its absolute URL in `content.aicoEndpoint`
  // (Modules/LandingPages/Service/PublicNeo/NeoRuntimeEndpointStamper). Same
  // contract `linked-form` already uses for its field list: the document holds
  // a reference, the renderer fetches it.
  //
  // Why a fetch and not a payload baked into the document: these lists move on
  // their own. A page synced today would still be advertising this week's
  // training next month. The URL does not go stale; the rows behind it do, so
  // the rows are read per view.
  //
  // Deliberately NARROW. Only those two exact sources are fetched, and only
  // when the server stamped an endpoint. Anything else — another source, an
  // element with no binding behind it (an imported block whose legacy detail
  // row is gone) — keeps the honest "resolved server-side" notice it had
  // before, because a widget that fetches a URL that will only ever 404 is
  // worse than one that says what is missing.
  // ============================================================

  // The labels the AICO-rendered page prints, per language. They live here and
  // not in AICO_NEO_I18N because that object is written by the page's Liquid
  // for the form strings only, and this file must stay identical across the
  // four club themes — a per-theme label would break that.
  var RUNTIME_LIST_LABELS = {
    de: {
      agenda: 'Agenda', asking: 'Aufgebote', time: 'Zeit', team: 'Team',
      empty: 'Derzeit keine Informationen verfügbar', more: 'Mehr Informationen',
      place: 'Ort', status: 'Status', note: 'Notiz', adversary: 'Gegnerin',
      mainFocus: 'Schwerpunkt TE/TA', confirmed: 'Bestätigt', notConfirmed: 'Nicht bestätigt',
      description: 'Beschreibung', gathering: 'Besammlung', address: 'Ort (Adresse)',
      askingFor: 'Aufgebot an', clothing: 'Bekleidung / Ausrüstung', catering: 'Verpflegung',
      coaching: 'Coaching',
      monthsLong: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      // Carbon's `M` for this locale — the exact strings the AICO-rendered page prints
      // ("11.Sep.2026", "Sep.2026"), so the separators live in the format, not in the name.
      monthsShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    },
    fr: {
      agenda: 'Agenda', asking: 'Demande', time: "L'heure", team: "L'équipe",
      empty: 'Aucune information disponible pour le moment', more: "Plus d'informations",
      place: 'Lieu', status: 'Statut', note: 'Note', adversary: 'Adversaire',
      mainFocus: "Principaux points d'attention", confirmed: 'Confirmé', notConfirmed: 'Non confirmé',
      description: 'Description', gathering: 'Rassemblement', address: 'Lieu (adresse)',
      askingFor: 'Demander', clothing: 'Vêtements / équipements', catering: 'Dispositions',
      coaching: 'Coaching',
      monthsLong: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
      monthsShort: ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc']
    },
    en: {
      agenda: 'Agenda', asking: 'Asking', time: 'Time', team: 'Team',
      empty: 'No information available for the moment', more: 'More Information',
      place: 'Place', status: 'Status', note: 'Note', adversary: 'Adversary',
      mainFocus: 'Main Focus', confirmed: 'Confirmed', notConfirmed: 'Not confirmed',
      description: 'Description', gathering: 'Gathering', address: 'Place (Address)',
      askingFor: 'Asking for', clothing: 'Clothing / Equipment', catering: 'Provisions',
      coaching: 'Coaching',
      monthsLong: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    }
  };

  function runtimeLabels() {
    var lang = '';
    try {
      lang = String((document.documentElement && document.documentElement.lang) || '').slice(0, 2).toLowerCase();
    } catch (e) { lang = ''; }
    return RUNTIME_LIST_LABELS[lang] || RUNTIME_LIST_LABELS.de;
  }

  /** An http(s) endpoint the server stamped, or '' — never an author-typed URL. */
  function runtimeEndpoint(content) {
    var url = String((content && content.aicoEndpoint) || '').trim();
    return /^https?:\/\//i.test(url) ? url : '';
  }

  /**
   * The element's mount. Empty on purpose: the title, the month headers and the
   * rows all come from the same fetch, so painting a heading here would flash a
   * label above a list that may turn out to be empty.
   */
  function runtimeListRenderer(type, source, unresolvedReason) {
    return function (el) {
      var content = (el && el.content) || {};
      var endpoint = runtimeEndpoint(content);
      if (!endpoint || String(content.source || '') !== source) {
        return renderUnresolvable(el, unresolvedReason);
      }

      return '<div class="content-type-neo-' + escapeAttr(type) + ' neo-runtime-list"' +
        inlineStyleAttr(el) +
        ' data-neo-runtime-list="' + escapeAttr(type) + '"' +
        ' data-neo-runtime-source="' + escapeAttr(source) + '"' +
        ' data-neo-runtime-endpoint="' + escapeAttr(endpoint) + '"' +
        ' data-neo-runtime-columns="' + escapeAttr(String(content.columns || '')) + '"' +
        ' data-neo-runtime-excerpt="' + (content.showExcerpt === false ? '0' : '1') + '"' +
        ' data-neo-runtime-image="' + (content.showImage === false ? '0' : '1') + '"' +
        '></div>';
    };
  }

  /** "2026-09-11" → "11.Sep.2026" — Carbon's `d.M.Y`, zero-padded day included. */
  function runtimeDay(iso, labels) {
    var parts = String(iso || '').split('-');
    if (parts.length !== 3) return '';
    var month = labels.monthsShort[parseInt(parts[1], 10) - 1] || parts[1];
    return parts[2] + '.' + month + '.' + parts[0];
  }

  /** The month bucket a row belongs to, in the format that list prints it. */
  function runtimeMonth(iso, labels, long) {
    var parts = String(iso || '').split('-');
    if (parts.length !== 3) return '';
    var index = parseInt(parts[1], 10) - 1;
    return long
      ? (labels.monthsLong[index] || parts[1]) + ' ' + parts[0]
      : (labels.monthsShort[index] || parts[1]) + '.' + parts[0];
  }

  /** "17:30" + "19:15" → "17:30 - 19:15"; a missing end time prints the start alone. */
  function runtimeTimeRange(start, end) {
    if (!start) return end ? escapeHtml(end) : '';
    return escapeHtml(end ? start + ' - ' + end : start);
  }

  /**
   * One `<tr><th>label</th><td>value</td></tr>`.
   *
   * Omitted for a value the source has not got — EXCEPT when `always` is set, which is how the
   * agenda's Ort and Status rows behave: the AICO-rendered page prints those two unconditionally,
   * empty cell and all, so skipping them would leave the migrated page two cells short per event.
   */
  function runtimeDetailRow(label, value, opts) {
    var options = opts || {};
    var empty = value === null || value === undefined || String(value) === '';
    if (empty && !options.always) return '';
    return '<tr><th>' + escapeHtml(label) + '</th><td' +
      (options.pre ? ' class="ask-pre"' : '') + '>' + escapeHtml(empty ? '' : value) + '</td></tr>';
  }

  /**
   * The expandable detail table shared by the agenda and the asking rows.
   *
   * Emitted even when it has no rows, because the AICO-rendered page emits it that way: every
   * summary row carries a toggle, and an entry with nothing to show still opens an empty panel.
   * Dropping it would leave a visible control pointing at nothing.
   */
  function runtimeDetails(id, rows) {
    return '<tr class="neo-runtime-list__details" id="' + escapeAttr(id) + '" hidden>' +
      '<td colspan="4" class="ask-details-cell">' +
      '<table class="more-details-table"><tbody>' + rows + '</tbody></table>' +
      '</td></tr>';
  }

  function runtimeAgendaHtml(payload, labels, uid) {
    var items = (payload && payload.items) || [];
    var html = '<h1 class="neo-runtime-list__heading">' + escapeHtml(labels.agenda) + '</h1>';
    if (!items.length) return html + '<h2>' + escapeHtml(labels.empty) + '</h2>';

    var month = null;
    for (var i = 0; i < items.length; i++) {
      var item = items[i] || {};
      var itemMonth = runtimeMonth(item.date, labels, true);
      if (itemMonth !== month) {
        month = itemMonth;
        html += '<div class="split-row-in-columns"><h2 class="neo-runtime-list__month">' + escapeHtml(month) + '</h2></div>' +
          '<div class="split-row-in-columns neo-runtime-list__colhead"><h3>' + escapeHtml(labels.time) + '</h3></div>' +
          '<div class="split-row-in-columns neo-runtime-list__colhead"><h3>' + escapeHtml(labels.team) + '</h3></div>';
      }

      var detailsId = uid + '-agenda-' + i;
      html += '<table class="team-table"><tbody class="ask-tbody">' +
        '<tr class="agenda-event">' +
        '<td class="neo-runtime-list__date"><h3>' + escapeHtml(runtimeDay(item.date, labels)) + '</h3></td>' +
        '<td class="neo-runtime-list__time"><h3>' + runtimeTimeRange(item.startTime, item.endTime) + '</h3></td>' +
        '<td class="neo-runtime-list__what"><h3>' + escapeHtml(item.title || '') + '</h3></td>' +
        '<td class="more-info-agenda" role="button" tabindex="0" aria-expanded="false"' +
        ' aria-controls="' + escapeAttr(detailsId) + '" data-neo-runtime-toggle="' + escapeAttr(detailsId) + '">&#8594;</td>' +
        '</tr>' +
        runtimeDetails(detailsId,
          runtimeDetailRow(labels.place, item.location, { always: true }) +
          runtimeDetailRow(labels.status, item.confirmed ? labels.confirmed : labels.notConfirmed, { always: true }) +
          runtimeDetailRow(labels.note, item.notes) +
          runtimeDetailRow(labels.adversary, item.adversary) +
          runtimeDetailRow(labels.mainFocus, item.mainFocus)) +
        '</tbody></table>';
    }
    return html;
  }

  function runtimeAskingHtml(payload, labels, uid) {
    var items = (payload && payload.items) || [];
    var html = '<h1 class="neo-runtime-list__heading">' + escapeHtml(labels.asking) + '</h1>';
    if (!items.length) return html + '<h2>' + escapeHtml(labels.empty) + '</h2>';

    var month = null;
    for (var i = 0; i < items.length; i++) {
      var item = items[i] || {};
      var itemMonth = runtimeMonth(item.date, labels, false);
      if (itemMonth !== month) {
        month = itemMonth;
        html += '<h3 class="ask-bold neo-runtime-list__month">' + escapeHtml(month) + '</h3>';
      }

      var detailsId = uid + '-asking-' + i;
      html += '<table class="team-table"><tbody class="ask-tbody">' +
        '<tr class="agenda-event">' +
        '<td class="ask-col-date"><h3 class="ask-bold">' + escapeHtml(runtimeDay(item.date, labels)) + '</h3></td>' +
        '<td class="ask-col-time">' + runtimeTimeRange(item.startTime, item.endTime) + '</td>' +
        '<td class="ask-col-title">' + escapeHtml(item.title || '') + '</td>' +
        '<td class="more-info" role="button" tabindex="0" aria-expanded="false"' +
        ' aria-controls="' + escapeAttr(detailsId) + '" data-neo-runtime-toggle="' + escapeAttr(detailsId) + '">' +
        escapeHtml(labels.more) + '</td>' +
        '</tr>' +
        runtimeDetails(detailsId,
          runtimeDetailRow(labels.description, item.description) +
          runtimeDetailRow(labels.gathering, item.meetingPoint) +
          runtimeDetailRow(labels.address, item.location) +
          // Absent unless the club turned the roster on server-side
          // (landingpages.public_neo_roster_fields) — these two name people.
          runtimeDetailRow(labels.askingFor, item.recipients, { pre: true }) +
          runtimeDetailRow(labels.clothing, item.clothing, { pre: true }) +
          runtimeDetailRow(labels.catering, item.catering) +
          runtimeDetailRow(labels.coaching, item.coaching)) +
        '</tbody></table>';
    }
    return html;
  }

  function runtimeNewsHtml(payload, host) {
    var items = (payload && payload.items) || [];
    if (!items.length) return '';

    // The old block's own column count wins over the element's, which the
    // importer set to 3 on every page; the live pages draw 2 and 3.
    var columns = parseInt(payload.columns, 10) ||
      parseInt(host.getAttribute('data-neo-runtime-columns'), 10) || 3;
    var width = (86 / Math.max(1, columns)).toFixed(2);
    var showImage = host.getAttribute('data-neo-runtime-image') !== '0';
    var showExcerpt = host.getAttribute('data-neo-runtime-excerpt') !== '0';
    var textColor = payload.textColor || '#ffffff';
    var background = payload.backgroundColor || 'royalblue';

    var html = '';
    for (var i = 0; i < items.length; i++) {
      var item = items[i] || {};
      var image = normalizeUrl(item.imageUrl || '');
      html += '<div class="neo-news-card">' +
        '<a href="' + escapeAttr(normalizeUrl(item.url || '#')) + '">' +
        '<div class="image-group text-center neo-news-card__body" style="width:' + escapeAttr(width) + '%">' +
        (showImage && image ? '<img class="neo-news-card__image" src="' + escapeAttr(image) + '" alt="" loading="lazy">' : '') +
        '<div class="text-block" style="background-color:' + escapeAttr(background) + '">' +
        '<h6 class="mb-0" style="color:' + escapeAttr(textColor) + '">' + escapeHtml(item.title || '') + '</h6>' +
        (showExcerpt ? '<p style="color:' + escapeAttr(textColor) + '">' + escapeHtml(item.excerpt || '') + '</p>' : '') +
        '</div></div></a></div>';
    }
    return html;
  }

  var RUNTIME_LIST_SEQUENCE = 0;

  function hydrateRuntimeList(host) {
    if (host.getAttribute('data-neo-runtime-hydrated') === '1') return;
    host.setAttribute('data-neo-runtime-hydrated', '1');

    var endpoint = host.getAttribute('data-neo-runtime-endpoint');
    var type = host.getAttribute('data-neo-runtime-list');
    if (!endpoint || typeof fetch !== 'function') {
      warnType(type, 'no usable runtime endpoint');
      return;
    }

    fetch(endpoint, { credentials: 'omit', headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (body) {
        var payload = (body && body.data) ? body.data : body;
        var labels = runtimeLabels();
        var uid = 'neo-rt-' + (++RUNTIME_LIST_SEQUENCE);
        var html;

        if (type === 'blog-feed') {
          html = runtimeNewsHtml(payload, host);
        } else if (payload && payload.kind === 'asking') {
          html = runtimeAskingHtml(payload, labels, uid);
        } else {
          html = runtimeAgendaHtml(payload, labels, uid);
        }

        host.innerHTML = html;
        host.setAttribute('data-neo-runtime-kind', String((payload && payload.kind) || type));
      })
      .catch(function (error) {
        if (window.console && console.warn) {
          console.warn('content-builder-neo: could not load the ' + type + ' list', error);
        }
        // Left un-hydrated so a later hydrate() call can retry, and empty rather
        // than half-drawn — an empty block is what the page showed before this
        // renderer existed.
        host.setAttribute('data-neo-runtime-hydrated', '0');
      });
  }

  var RUNTIME_TOGGLES_BOUND = false;

  /**
   * One delegated pair of listeners for every expandable detail row on the page.
   * Bound on `document`, once: the rows are written into the mount AFTER the
   * fetch resolves, so a listener attached to the rows themselves would have to
   * be re-attached on every refresh, and one attached per mount would fire N
   * times on a page with N lists.
   */
  function bindRuntimeListToggles() {
    if (RUNTIME_TOGGLES_BOUND) return;
    RUNTIME_TOGGLES_BOUND = true;

    function toggle(event) {
      var trigger = event.target && event.target.closest
        ? event.target.closest('[data-neo-runtime-toggle]')
        : null;
      if (!trigger) return;
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      var details = document.getElementById(trigger.getAttribute('data-neo-runtime-toggle'));
      if (!details) return;
      details.hidden = !details.hidden;
      trigger.setAttribute('aria-expanded', details.hidden ? 'false' : 'true');
    }

    document.addEventListener('click', toggle);
    document.addEventListener('keydown', toggle);
  }

  // ============================================================
  // Unknown / unresolvable types — the anti-silence layer
  //
  // 2026-09-08: FC Zürich's 17,421 converted articles are `html`-shaped and the
  // news renderer had no `html` arm. An unregistered type returned '' —
  // including, for a wrapper, its entire subtree — so every article showed its
  // images and no text, live, until it was rolled back. The conversion had been
  // verified against the BACKEND renderer, which does have that arm: a correct
  // measurement of the wrong code path.
  //
  // The rule that replaces it: nothing renders to nothing. A type with no
  // renderer still emits its children, then whatever copy its content carries,
  // then a source-visible marker — and always a console warning naming the
  // type. A future element type degrades to something a reader can see and an
  // author can report, never to a hole.
  // ============================================================

  // Content keys holding author-edited markup, which ships verbatim (same
  // trust level as the html element).
  var COPY_HTML_KEYS = ['html', 'text', 'embedCode', 'embeddedHtml', 'content', 'body'];

  // Content keys holding plain strings, which are escaped.
  var COPY_TEXT_KEYS = [
    'headline', 'heading', 'title', 'subheadline', 'subtitle', 'description',
    'quote', 'author', 'role', 'name', 'label', 'question', 'answer',
    'welcomeMessage', 'successMessage', 'copyrightText', 'placeholder',
    'buttonText', 'linkText', 'ctaLabel', 'submitText', 'searchPlaceholder',
    'contactButtonLabel', 'womenLabel', 'menLabel', 'price', 'period', 'location'
  ];

  // Content keys whose value is a list of sub-items carrying their own copy.
  var COPY_LIST_KEYS = [
    'items', 'jobs', 'plans', 'tabs', 'logos', 'slides', 'columns', 'links',
    'fields', 'members', 'reviews', 'news', 'products', 'files', 'images',
    'categories', 'options'
  ];

  /**
   * Every scrap of human-visible copy an arbitrary content object carries, as
   * HTML. Recurses one level into the known list keys (and into `aico_hydrated`),
   * which is where the builder keeps sub-item copy. Depth-capped so a
   * pathological document cannot spin here.
   */
  function visibleCopy(content, depth) {
    if (!content || typeof content !== 'object') return '';
    if (depth == null) depth = 0;
    if (depth > 3) return '';

    var out = '';
    var i;
    for (i = 0; i < COPY_HTML_KEYS.length; i++) {
      var raw = content[COPY_HTML_KEYS[i]];
      // Verbatim only for real markup strings; an object under `content` is a
      // nested payload, not copy.
      if (typeof raw === 'string' && raw.trim() !== '') {
        out += '<div class="neo-unknown__copy">' + raw + '</div>';
      }
    }
    for (i = 0; i < COPY_TEXT_KEYS.length; i++) {
      var text = content[COPY_TEXT_KEYS[i]];
      if (typeof text === 'string' && text.trim() !== '') {
        out += '<p class="neo-unknown__copy">' + escapeHtml(text.trim()) + '</p>';
      } else if (typeof text === 'number') {
        out += '<p class="neo-unknown__copy">' + escapeHtml(String(text)) + '</p>';
      }
    }
    for (i = 0; i < COPY_LIST_KEYS.length; i++) {
      var list = listOf(content[COPY_LIST_KEYS[i]]);
      for (var j = 0; j < list.length; j++) out += visibleCopy(list[j], depth + 1);
    }
    if (content.aico_hydrated && typeof content.aico_hydrated === 'object') {
      out += visibleCopy(content.aico_hydrated, depth + 1);
    }
    return out;
  }

  /**
   * One warning per type per render — a document with 300 unrenderable
   * elements should not emit 300 lines, but every distinct type must be named.
   */
  var warnedTypes = {};

  function warnType(type, detail) {
    var key = String(type || '(no type)');
    if (warnedTypes[key]) return;
    warnedTypes[key] = true;
    if (window.console && console.warn) {
      console.warn('NewsNeo: element type "' + key + '" — ' + (detail || 'no renderer registered') +
        '. Rendered as degraded copy; see content-builder-neo.js.');
    }
  }

  /**
   * A type this renderer KNOWS about but cannot resolve here (it needs the AICO
   * backend at view time). Distinct from renderUnknown only in the reason it
   * reports — the visible outcome is the same: the element's copy, never a hole.
   */
  function renderUnresolvable(el, reason) {
    var type = (el && el.type) || '(no type)';
    warnType(type, reason);
    var copy = visibleCopy((el && el.content) || {});
    return '<div class="content-type-neo-unresolved" data-neo-unresolved-type="' + escapeAttr(type) + '">' +
      '<!-- neo: ' + escapeAttr(String(reason || '').replace(/--+/g, '-')) + ' -->' +
      copy + '</div>';
  }

  function renderUnknown(el) {
    var type = (el && el.type) || '(no type)';

    // A wrapper-ish unknown must never delete its subtree: children first, and
    // if it has any, they ARE the content.
    var children = el ? renderChildren(el) : '';
    var copy = visibleCopy((el && el.content) || {});

    warnType(type, children
      ? 'no renderer registered; rendered its ' + childrenOf(el).length + ' child element(s) instead'
      : 'no renderer registered; rendered its text content instead');

    return '<div class="content-type-neo-unknown" data-neo-unknown-type="' + escapeAttr(type) + '">' +
      '<!-- neo: no renderer for element type "' + escapeAttr(type) + '" -->' +
      children + copy + '</div>';
  }

  // ============================================================
  // Dispatch (single element, NOT a column container)
  // ============================================================

  function renderElement(el) {
    if (!el || typeof el !== 'object') return '';
    if (el.localeHidden === true) return '';
    return withTierVisibility(el, renderElementMarkup(el));
  }

  function renderElementMarkup(el) {
    if (el.type === 'columns') {
      // Section-level `columns` is consumed by renderSection; reaching here
      // means a NESTED columns (inside a container, or inside another
      // columns), which used to render nothing at all.
      return renderNestedColumns(el);
    }
    var fn = renderers[el.type];
    if (typeof fn !== 'function') return renderUnknown(el);
    try {
      return fn(el);
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('NewsNeo: renderer for "' + el.type + '" threw', e);
      }
      // Degrade to the element's own copy rather than dropping it: a bug in one
      // arm must not blank the article the way the missing `html` arm did.
      return renderUnresolvable(el, 'its renderer threw: ' + (e && e.message ? e.message : e));
    }
  }

  // ============================================================
  // Row / column wrappers
  // Mirrors the legacy structure produced inside news.js so news.css
  // styles the result identically.
  // ============================================================

  // The legacy subsection title of an element, wherever the converter put it:
  // `content.subsectionTitle`, or one level down inside a legacy frame object
  // (`legacyHtmlFrame`, `legacyContentCard`, ...). Empty string when none.
  function subsectionTitleOf(el) {
    var content = el && el.content;
    if (!content || typeof content !== 'object') return '';
    if (typeof content.subsectionTitle === 'string' && content.subsectionTitle.trim() !== '') {
      return content.subsectionTitle.trim();
    }
    for (var key in content) {
      if (!Object.prototype.hasOwnProperty.call(content, key)) continue;
      var v = content[key];
      if (v && typeof v === 'object' && !Array.isArray(v) &&
          typeof v.subsectionTitle === 'string' && v.subsectionTitle.trim() !== '') {
        return v.subsectionTitle.trim();
      }
    }
    return '';
  }

  // `data-subsection-title` mirrors the legacy product card attribute that
  // theme scripts (Ecofort main-product.js) read to build in-page navigation.
  // Inert everywhere else; emitted only when the element carries a title.
  function wrapContent(innerHtml, contentIndex, el) {
    if (!innerHtml) return '';
    var subsection = subsectionTitleOf(el);
    var attr = subsection ? ' data-subsection-title="' + escapeAttr(subsection) + '"' : '';
    return '<div class="content content__' + contentIndex + '"' + attr + '>' + innerHtml + '</div>';
  }

  function wrapColumn(contentHtmlList, columnIndex) {
    var inner = contentHtmlList.join('');
    return '<div class="content-column column__' + columnIndex + '">' + inner + '</div>';
  }

  function wrapRow(columnsHtml, rowIndex, totalColumns, isFullWidth, gridTemplate) {
    var classes = ['content-row', 'row__' + rowIndex, 'total-columns-' + totalColumns];
    if (isFullWidth) classes.push('fullwidth');
    return '<div class="' + classes.join(' ') + '"' + (gridTemplate ? ' style="grid-template-columns:' + gridTemplate + '"' : '') + '>' + columnsHtml + '</div>';
  }

  // ============================================================
  // Columns ratio (aico native settings contract v2, section 4)
  //
  //   columns  content.columnRatio  '7:5', '1:2:1', '60:40', ...
  //
  // The product tab lays a `content-row total-columns-N` out as a CSS grid of
  // N equal tracks where the columns sit side by side (section-main-product
  // .css, `#product-info .total-columns-N {display:grid;
  // grid-template-columns:repeat(N, minmax(auto, 1fr))}` from 768px). The
  // ratio becomes the row's own inline `grid-template-columns` with one track
  // per part, the same `minmax(auto, <part>fr)` tracks: an inline declaration
  // outranks the stylesheet's, and it is inert below 768px, where the row is
  // not a grid and the columns stack as before. Without a ratio, with equal
  // parts, or with one that does not have exactly one positive part per column,
  // nothing changes. Inside a card a ratio in whole twelfths is drawn as the
  // legacy Bootstrap row instead (renderRatioGrid()). Outside #product-info the
  // row is a flex row (base.css `.product_submenu .tab-content .total-columns-N`)
  // whose column widths stay as they were.
  // ============================================================

  function columnRatioParts(content, count) {
    var raw = content && typeof content.columnRatio === 'string' ? content.columnRatio : '';
    if (!raw) return null;
    var parts = raw.split(':');
    if (parts.length !== count || count < 2) return null;
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].replace(/^\s+|\s+$/g, '');
      if (!/^\d{1,4}(\.\d{1,4})?$/.test(p) || parseFloat(p) <= 0) return null;
      out.push(parseFloat(p));
    }
    // Equal parts are the equal columns the theme draws anyway: drawn as before.
    for (var e = 1; e < out.length; e++) if (out[e] !== out[0]) return out;
    return null;
  }

  function columnRatioTemplate(content, count) {
    var parts = columnRatioParts(content, count);
    if (!parts) return '';
    var tracks = [];
    for (var i = 0; i < parts.length; i++) tracks.push('minmax(auto, ' + parts[i] + 'fr)');
    return tracks.join(' ');
  }

  // The ratio in twelfths (Bootstrap column spans), or null when a part does
  // not come out as a whole twelfth.
  function columnRatioTwelfths(content, count) {
    // A single column whose ratio is a whole number of twelfths below twelve (aico writes the legacy
    // row of one `col-sm-7` as columnCount 1, columnRatio '7', --legacy-single-column-grids): the
    // Bootstrap column the legacy block printed, the rest of the row left empty.
    if (count === 1) {
      var single = content && typeof content.columnRatio === 'string' ? content.columnRatio.replace(/^\s+|\s+$/g, '') : '';
      return /^([2-9]|1[01])$/.test(single) ? [parseInt(single, 10)] : null;
    }
    var parts = columnRatioParts(content, count);
    if (!parts) return null;
    var sum = 0;
    for (var i = 0; i < parts.length; i++) sum += parts[i];
    var spans = [];
    for (var j = 0; j < parts.length; j++) {
      var span = parts[j] * 12 / sum;
      if (Math.abs(span - Math.round(span)) > 1e-6 || Math.round(span) < 1) return null;
      spans.push(Math.round(span));
    }
    return spans;
  }

  // Render a `columns` element as one multi-column row.
  function renderColumnsRow(columnsEl, rowIndex, isFullWidth) {
    if (columnsEl && columnsEl.localeHidden === true) return '';
    var content = columnsEl.content || {};
    var count = parseInt(content.columnCount, 10);
    if (isNaN(count) || count < 1) count = 2;
    if (count > MAX_COLUMNS) count = MAX_COLUMNS;

    var children = Array.isArray(columnsEl.children) ? columnsEl.children : [];

    var sorted = children.slice().sort(function (a, b) {
      var ai = (a && a.content && typeof a.content.columnIndex === 'number') ? a.content.columnIndex : 0;
      var bi = (b && b.content && typeof b.content.columnIndex === 'number') ? b.content.columnIndex : 0;
      return ai - bi;
    });

    var buckets = [];
    for (var c = 0; c < count; c++) buckets.push([]);
    var fallbackIdx = 0;
    for (var j = 0; j < sorted.length; j++) {
      var child = sorted[j];
      var ci = (child && child.content && typeof child.content.columnIndex === 'number')
        ? child.content.columnIndex
        : fallbackIdx++;
      if (ci < 0) ci = 0;
      if (ci >= count) ci = count - 1;
      buckets[ci].push(child);
    }

    var columnsHtml = '';
    for (var col = 0; col < count; col++) {
      var contentsHtml = [];
      // drawSiblings: the column's elements, native inline pictures put into their text.
      var drawnList = drawSiblings(buckets[col]);
      for (var k = 0; k < drawnList.length; k++) {
        contentsHtml.push(wrapContent(drawnList[k].html, drawnList[k].index + 1, drawnList[k].el));
      }
      columnsHtml += wrapColumn(contentsHtml, col + 1);
    }

    return wrapRow(columnsHtml, rowIndex, count, isFullWidth, columnRatioTemplate(content, count));
  }

  // ============================================================
  // Legacy product-tab cards
  //
  // An element imported with aico's --legacy-block-cards carries
  // `content.legacyCard = {block, blockType}`: the legacy block it was
  // converted from. The legacy product tab drew every block in a card of its
  // own (`div.card.content-card > div.card-body > div.content-type-<type>`)
  // and printed the block's markup bare inside it, and the theme's stylesheet
  // and scripts are written against exactly that structure (the card is a flex
  // box, so a block's outer margins stay inside it; picture and table rules
  // are scoped to `.card-body .content-type-html`; the category tiles are
  // built by a script from `.content-html-text .row`). A section whose
  // elements carry the mark is drawn the same way: one legacy row, its
  // columns, and per column one card per run of elements of the same block,
  // each element without the engine's own wrapper where that wrapper draws
  // nothing. A document without the mark renders exactly as before.
  // ============================================================

  function legacyCardOf(el) {
    var content = el && el.content;
    var mark = content && typeof content === 'object' ? content.legacyCard : null;
    if (!mark || typeof mark !== 'object') return null;
    if (mark.block === undefined || mark.block === null || mark.block === '') return null;
    return mark;
  }

  function sectionHasLegacyCards(section) {
    var elements = section && Array.isArray(section.elements) ? section.elements : [];
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el) continue;
      if (legacyCardOf(el) || el.type === 'card') return true;
      if (el.type === 'columns' && Array.isArray(el.children)) {
        for (var j = 0; j < el.children.length; j++) {
          if (legacyCardOf(el.children[j]) || (el.children[j] && el.children[j].type === 'card')) return true;
        }
      }
    }
    return false;
  }

  // The markup of an element without the engine's outer wrapper div, when
  // that wrapper is a bare class box (no style of its own) around the whole
  // markup: inside a legacy card the inner tag is what the block printed.
  function unwrapPlainBox(html) {
    var m = /^<div class="(content-type-[^"]*)">/.exec(html);
    if (!m || !/<\/div>$/.test(html)) return html;
    var re = /<div\b|<\/div>/g;
    re.lastIndex = m[0].length;
    var depth = 1, t;
    while ((t = re.exec(html))) {
      depth += t[0] === '</div>' ? -1 : 1;
      if (depth === 0) break;
    }
    if (!t || t.index !== html.length - 6) return html;
    return html.slice(m[0].length, html.length - 6);
  }

  // The data sheet table of a legacy `attribute` block, as the product tab
  // printed it: `div.attribute-comparison-table`, the product title over both
  // columns in a bold header cell, one row per attribute category.
  function renderLegacyAttributeTable(el) {
    var content = el.content || {};
    var rows = Array.isArray(content.rows) ? content.rows : [];
    if (rows.length === 0) return '';
    var cellText = function (cell) {
      if (cell && typeof cell === 'object') return sanitizeTableCellHtml(cell.text);
      return sanitizeTableCellHtml(cell == null ? '' : String(cell));
    };
    var head = '';
    var start = 0;
    if (content.headerRow === true) {
      var first = Array.isArray(rows[0]) ? rows[0] : [];
      head = '<thead><tr><th colspan="2"><strong>' + (first.length ? cellText(first[0]) : '') + '</strong></th></tr></thead>';
      start = 1;
    }
    var body = '';
    for (var r = start; r < rows.length; r++) {
      var row = Array.isArray(rows[r]) ? rows[r] : [];
      var tds = '';
      for (var c = 0; c < row.length; c++) tds += '<td>' + cellText(row[c]) + '</td>';
      body += '<tr>' + tds + '</tr>';
    }
    return '<div class="attribute-comparison-table"><table>' + head + '<tbody>' + body + '</tbody></table></div>';
  }

  // A legacy `sub_sub_category` block as the product tab printed it: the
  // heading, then per category a Bootstrap row with the picture in one half
  // and the linked name in the other, all inside `.content-html-text` (the
  // theme script turns those rows into the tile grid).
  function renderLegacyCategoryList(elements) {
    var html = '<div class="content-html-text" style="padding-top: 3rem;">';
    var titled = false;
    var row = function (image, title) {
      var name = title ? stripOuterParagraph(title.content.text != null ? title.content.text : '') : '';
      // A category without a picture: the tab drew the shop's logo icon in its place, named after the
      // category (content_builders_json.liquid, `ecofort-logo-icon.png | file_img_url: 'master'`).
      var picture = image ? '<img src="' + escapeAttr(image.content.src || '') + '" alt="' + escapeAttr(image.content.alt || '') +
        '" width="1500" height="1500" loading="lazy">'
        : (title ? '<img src="/cdn/shop/files/ecofort-logo-icon.png" alt="' + escapeAttr(String(name).replace(/<[^>]*>/g, '').trim()) +
          '" width="1500" height="1500" loading="lazy">' : '');
      return '<div class="row" style="margin-bottom: 3rem;"><div class="col-sm-6 image-section"><figure>' + picture +
        '</figure></div><div class="col-sm-6 text-section"><h3 id="">' + name + '</h3></div></div>';
    };
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el || el.localeHidden === true) continue;
      var content = el.content || {};
      if (el.type === 'heading' && !titled && String(content.level || '').toLowerCase() === 'h2') {
        html += '<h2 style="margin-top: 3rem;">' + stripOuterParagraph(content.text != null ? content.text : '') + '</h2>';
        titled = true;
      } else if (el.type === 'columns' && Array.isArray(el.children)) {
        var image = null, title = null;
        for (var j = 0; j < el.children.length; j++) {
          var child = el.children[j];
          if (!child || child.localeHidden === true) continue;
          if (child.type === 'image' && !image) image = child;
          else if (child.type === 'heading' && !title) title = child;
        }
        html += row(image, title);
      } else if (el.type === 'heading') {
        html += row(null, el);
      } else if (el.type === 'image') {
        html += row(el, null);
      }
    }
    return html + '</div>';
  }

  // A Wistia video inside a legacy card, as the legacy block embedded it: the
  // player library's responsive box in `.video-col` (the tab gives the box its
  // bottom margin and corners). The page loads the library itself
  // (aico-neo-entity.js, only when such a box was drawn).
  function renderLegacyWistia(el) {
    var content = el.content || {};
    var id = wistiaId(content);
    if (!id) return '';
    var ratio = String(parseAspectRatio(content.aspectRatio, '16/9')).split('/');
    var w = parseFloat(ratio[0]), h = parseFloat(ratio.length > 1 ? ratio[1] : 1);
    var pad = (w > 0 && h > 0) ? Math.round(h / w * 1000000) / 10000 : 56.25;
    return '<div class="video-col"><div class="wistia_responsive_padding" style="padding: ' + pad + '% 0 0 0; position: relative">' +
      '<div class="wistia_responsive_wrapper" style="height: 100%; left: 0; position: absolute; top: 0; width: 100%">' +
      '<div class="wistia_embed wistia_async_' + escapeAttr(id) + ' seo=true videoFoam=true" style="height: 100%; position: relative; width: 100%">&nbsp;</div>' +
      '</div></div></div>';
  }

  // A Bootstrap grid row of a legacy block (aico marks the `columns` element
  // with legacyGrid: the row's classes and every column's classes in source
  // order, -1 for a spacer column the element does not keep): drawn as the
  // block printed it, the theme's grid rules sizing the columns.
  function renderLegacyGrid(el) {
    var grid = el.content && el.content.legacyGrid;
    return renderBootstrapGrid(el, grid.row, grid && Array.isArray(grid.cells) ? grid.cells : []);
  }

  // A native `columns` element in a card whose columnRatio comes out in whole
  // twelfths (aico native settings contract v2, section 4; the converter writes
  // the ratio from the legacy grid's column classes): drawn as the Bootstrap
  // row the legacy block printed, `div.row > div.col-sm-<span>`, so the tab's
  // grid rules size it exactly as before (and stack it below 768px).
  function renderRatioGrid(el, spans) {
    var cells = [];
    for (var i = 0; i < spans.length; i++) cells.push({ column: i, 'class': 'col-sm-' + spans[i] });
    return renderBootstrapGrid(el, 'row', cells);
  }

  function renderBootstrapGrid(el, rowClass, cells) {
    var children = Array.isArray(el.children) ? el.children : [];
    var cleanClass = function (value) { return String(value || '').replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').trim(); };
    var html = '<div class="' + (cleanClass(rowClass) || 'row') + '">';
    for (var c = 0; c < cells.length; c++) {
      var cell = cells[c] || {};
      var bucket = [];
      for (var j = 0; j < children.length; j++) {
        var child = children[j];
        if (child && child.content && child.content.columnIndex === cell.column && cell.column >= 0) bucket.push(child);
      }
      html += '<div class="' + cleanClass(cell['class']) + '">' + renderLegacyCardInner('html', bucket) + '</div>';
    }
    return html + '</div>';
  }

  // The legacy tag of each paragraph of a text a Custom-HTML block became (aico
  // --legacy-block-tags marks `content.legacyBlockTags`, one entry per top-level
  // block of the text, in order): `{tag: 'div'}` a line the block printed as a
  // `div` (or as words straight in the block), `{tag: 'p', class: '...'}` a
  // paragraph (or, on a heading element, the heading) with that class,
  // `{tag: 'blockquote', bare: true}` a blockquote
  // holding its words without a paragraph, `{tag, fill}` an empty paragraph that
  // was a blank line of another tag or content (printed as renderLegacyBlankLines
  // does), `{tag: <its own>}` anything else; an entry with words may add
  // `span: true` (its words stood in one span), with `font: 'body'` when that span
  // set the Bootstrap body font;
  // `{tag: 'h2'}` on a paragraph: its words were a heading that also held a picture.
  // The product tab's stylesheet sizes that markup per tag (a `div` line at 22px
  // with no margin, a `br` straight in a `div` as a 15px gap), so the paragraph
  // gets its tag back. Anything that does not line up one to one with the text's
  // blocks leaves the markup as it is.
  function withLegacyBlockTags(el, html) {
    var tags = el && (el.type === 'text' || el.type === 'heading') && el.content ? el.content.legacyBlockTags : null;
    if (!Array.isArray(tags) || tags.length === 0 || !html) return html;
    try {
      if (typeof document === 'undefined' || !document.createElement) return html;
      var tpl = document.createElement('template');
      if (!('content' in tpl)) return html;
      tpl.innerHTML = html;
      var blocks = [];
      var nodes = tpl.content.childNodes;
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (n.nodeType === 1) blocks.push(n);
        else if (n.nodeType === 3 && /\S/.test(n.nodeValue.replace(/ /g, ' '))) return html;
      }
      if (blocks.length !== tags.length) return html;
      for (var b = 0; b < blocks.length; b++) {
        var tag = tags[b];
        var node = blocks[b];
        if (!tag || typeof tag !== 'object') continue;
        var name = node.tagName.toLowerCase();
        if (Array.isArray(tag.fill) && name === 'p' && !/\S/.test(node.textContent.replace(/\u00a0/g, ' ')) && !node.querySelector('img, iframe, video, table, hr')) {
          var blank = renderLegacyBlankLines([tag]);
          var holder = document.createElement('template');
          holder.innerHTML = blank;
          node.parentNode.replaceChild(holder.content, node);
        } else if (typeof tag.tag === 'string' && /^h[1-6]$/.test(tag.tag) && name === 'p') {
          var heading = document.createElement(tag.tag);
          while (node.firstChild) heading.appendChild(node.firstChild);
          node.parentNode.replaceChild(heading, node);
          node = heading;
        } else if (tag.tag === 'div' && name === 'p') {
          var div = document.createElement('div');
          while (node.firstChild) div.appendChild(node.firstChild);
          node.parentNode.replaceChild(div, node);
          node = div;
        } else if (typeof tag['class'] === 'string' && (name === 'p' || (/^h[1-6]$/.test(name) && name === tag.tag))) {
          var cls = tag['class'].replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').trim();
          if (cls) node.setAttribute('class', cls);
        } else if (tag.bare === true && name === 'blockquote') {
          var kids = [];
          for (var k = 0; k < node.childNodes.length; k++) {
            var kid = node.childNodes[k];
            if (kid.nodeType === 1) kids.push(kid);
            else if (kid.nodeType === 3 && /\S/.test(kid.nodeValue.replace(/ /g, ' '))) { kids = null; break; }
          }
          if (kids && kids.length === 1 && kids[0].tagName.toLowerCase() === 'p') {
            var p = kids[0];
            while (node.firstChild) node.removeChild(node.firstChild);
            while (p.firstChild) node.appendChild(p.firstChild);
          }
        }
        // `span: true`: the words stood in one span, which the tab's rules size as body text even
        // in a caption or a heading; `font: 'body'`: that span set the Bootstrap body font, which
        // the tab's rules leave alone. The words get that span back.
        if ((tag.span === true || tag.font === 'body') && node && node.parentNode && /\S/.test(node.textContent.replace(/\u00a0/g, ' '))) {
          var span = document.createElement('span');
          if (tag.font === 'body') span.setAttribute('style', 'font-family: var(--bs-body-font-family);');
          while (node.firstChild) span.appendChild(node.firstChild);
          node.appendChild(span);
        }
      }
      return tpl.innerHTML;
    } catch (e) {
      return html;
    }
  }

  // The blank lines a legacy Custom-HTML block printed that no element draws (aico
  // --legacy-blank-lines marks `content.legacyBlankBefore` on the element they stood
  // before, and `content.legacyBlankAfter` on the block's last element): each a tag and
  // what it held, line breaks and non-breaking spaces. Printed again as the block did,
  // so the lines keep their height and everything below them its place.
  function renderLegacyBlankLines(lines) {
    if (!Array.isArray(lines) || lines.length === 0) return '';
    var html = '';
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (!line || typeof line !== 'object') continue;
      var tag = String(line.tag || '').toLowerCase();
      var fill = '';
      var tokens = Array.isArray(line.fill) ? line.fill : [];
      for (var t = 0; t < tokens.length; t++) {
        if (tokens[t] === 'br') fill += '<br>';
        else if (tokens[t] === 'nbsp') fill += '&nbsp;';
      }
      if (tag === 'inline') html += fill;
      else if (/^(p|div|h[1-6])$/.test(tag)) html += '<' + tag + '>' + fill + '</' + tag + '>';
    }
    return html;
  }

  // ============================================================
  // Native inline pictures (aico native settings contract v2, section 2)
  //
  //   image  content.inlineWith   'none' | 'next' | 'previous'
  //   image  content.inlineBreak  boolean (only with 'next')
  //
  // 'next': the picture opens the first paragraph (or line, for a text whose
  // paragraphSpacing is 'none') of the NEXT sibling text element, or that
  // sibling heading, before its words; inlineBreak puts a `<br>` between the
  // picture and the words. 'previous': the picture closes the LAST paragraph
  // of the PREVIOUS sibling text element, or that heading. The paragraph keeps
  // its own attributes; the picture keeps its own settings on its `<img>` and
  // loses only the engine's block wrapper. Where the sibling is not a visible
  // text or heading, or its markup does not start (end) with a paragraph, the
  // picture is drawn as an ordinary block. This is the native counterpart of
  // the legacy marks legacyWrapContinues / legacyWrapEndsParagraph, which the
  // legacy card drawing below still reads for documents that carry them.
  // ============================================================

  function nativeInlineMode(el) {
    if (!el || el.type !== 'image' || !el.content) return '';
    var mode = el.content.inlineWith;
    return mode === 'next' || mode === 'previous' ? mode : '';
  }

  function isInlineHost(el) {
    return !!el && (el.type === 'text' || el.type === 'heading') && el.localeHidden !== true && el.hidden !== true;
  }

  // The engine's own wrapper of an element's markup (`<div class="content-type-...">`,
  // with any attributes) split off, so a picture can go inside the markup it wraps.
  function splitEngineWrapper(html) {
    var m = /^<div class="content-type-[^"]*"[^>]*>/.exec(html);
    if (!m || !/<\/div>$/.test(html)) return { open: '', inner: html, close: '' };
    var re = /<div\b|<\/div>/g;
    re.lastIndex = m[0].length;
    var depth = 1, t;
    while ((t = re.exec(html))) {
      depth += t[0] === '</div>' ? -1 : 1;
      if (depth === 0) break;
    }
    if (!t || t.index !== html.length - 6) return { open: '', inner: html, close: '' };
    return { open: m[0], inner: html.slice(m[0].length, html.length - 6), close: '</div>' };
  }

  // The host's markup with the picture put into its first (where 'next') or
  // last (where 'previous') paragraph or heading; null when it has none there.
  function insertInlinePicture(hostHtml, host, picture, where, lineBreak) {
    if (!hostHtml || !picture) return null;
    var parts = splitEngineWrapper(hostHtml);
    var inner = parts.inner;
    var tags = host.type === 'heading' ? 'h[1-6]'
      : (host.content && host.content.paragraphSpacing === 'none' ? 'p|div' : 'p');
    var m;
    if (where === 'next') {
      m = new RegExp('^<(' + tags + ')\\b[^>]*>', 'i').exec(inner);
      if (!m) return null;
      return parts.open + m[0] + picture + (lineBreak ? '<br>' : '') + inner.slice(m[0].length) + parts.close;
    }
    m = new RegExp('</(' + tags + ')>$', 'i').exec(inner);
    if (!m) return null;
    return parts.open + inner.slice(0, m.index) + picture + inner.slice(m.index) + parts.close;
  }

  // An image element's markup without its engine block wrapper, or null when
  // the markup is not the plain wrapper (then the picture stays a block).
  function inlinePictureMarkup(rendered) {
    if (!/^<div class="content-type-neo-image">/.test(rendered || '')) return null;
    var inner = unwrapPlainBox(rendered);
    return inner === rendered ? null : inner;
  }

  // Sibling elements drawn one by one, with native inline pictures put into
  // the text next to them (for the plain section stack and a column of a
  // `columns` row, where each element gets its own `.content` wrapper). Each
  // entry keeps the element's position in the list. A list without an inline
  // picture gives exactly the element-by-element markup.
  function drawSiblings(elements) {
    var out = [];
    var pending = null;
    var flush = function () {
      if (pending) out.push(pending.entry);
      pending = null;
    };
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      var rendered = renderElement(el);
      if (pending) {
        var merged = isInlineHost(el) && rendered ? insertInlinePicture(rendered, el, pending.picture, 'next', pending.lineBreak) : null;
        if (merged !== null) {
          out.push({ el: el, index: i, html: merged });
          pending = null;
          continue;
        }
        flush();
      }
      if (!rendered) continue;
      var mode = nativeInlineMode(el);
      var picture = mode ? inlinePictureMarkup(rendered) : null;
      if (picture !== null && mode === 'next' && isInlineHost(elements[i + 1])) {
        pending = { picture: picture, lineBreak: el.content.inlineBreak === true, entry: { el: el, index: i, html: rendered } };
        continue;
      }
      var last = out.length ? out[out.length - 1] : null;
      if (picture !== null && mode === 'previous' && last && last.index === i - 1 && isInlineHost(last.el)) {
        var closed = insertInlinePicture(last.html, last.el, picture, 'previous', false);
        if (closed !== null) {
          last.html = closed;
          continue;
        }
      }
      out.push({ el: el, index: i, html: rendered });
    }
    flush();
    return out;
  }

  function renderLegacyCardInner(type, elements) {
    if (type === 'sub_sub_category') return renderLegacyCategoryList(elements);
    // An attribute list block without values: the tab still printed its empty list after the
    // heading (the list's bottom margin stays inside the card).
    if (type === 'attribute') {
      var listed = false;
      for (var a = 0; a < elements.length; a++) if (elements[a] && elements[a].type !== 'heading') listed = true;
      if (!listed) return renderLegacyCardInner('html', elements) + '<ul></ul>';
    }
    var html = '';
    var blankAfter = '';
    // A native inline picture (inlineWith 'next') waiting for the text it opens,
    // and the text or heading drawn last (for 'previous'): see drawSiblings().
    var pendingPicture = null;
    var pendingBreak = false;
    var lastHost = null;
    var flushPicture = function () {
      if (pendingPicture) html += pendingPicture;
      pendingPicture = null;
    };
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el || el.localeHidden === true) {
        flushPicture();
        lastHost = null;
        continue;
      }
      var previousHost = lastHost;
      lastHost = null;
      if (pendingPicture && !isInlineHost(el)) flushPicture();
      var blankBefore = renderLegacyBlankLines(el.content && el.content.legacyBlankBefore);
      if (blankBefore) {
        flushPicture();
        previousHost = null;
      }
      html += blankBefore;
      blankAfter = renderLegacyBlankLines(el.content && el.content.legacyBlankAfter);
      if (el.type === 'columns' && el.content && el.content.legacyGrid && Array.isArray(el.content.legacyGrid.cells)) {
        html += withTierVisibility(el, renderLegacyGrid(el));
        continue;
      }
      if (el.type === 'columns' && el.content && !el.content.legacyGrid) {
        var gridCount = parseInt(el.content.columnCount, 10);
        if (isNaN(gridCount) || gridCount < 1) gridCount = 2;
        if (gridCount > MAX_COLUMNS) gridCount = MAX_COLUMNS;
        var gridSpans = columnRatioTwelfths(el.content, gridCount);
        if (gridSpans) {
          html += withTierVisibility(el, renderRatioGrid(el, gridSpans));
          continue;
        }
      }
      if (el.type === 'video' && el.content && el.content.type === 'wistia') {
        html += withTierVisibility(el, renderLegacyWistia(el));
        continue;
      }
      if (type === 'attribute' && el.type === 'table') {
        html += withTierVisibility(el, renderLegacyAttributeTable(el));
        continue;
      }
      var full = renderElement(el);
      var drawn = unwrapPlainBox(full);
      // A table with native table settings drawn without the engine's box: its tier
      // classes (hidden on phones, styles.mobile.display) went with the box, so they go
      // on what is left, as for a legacy classed table below.
      if (el.type === 'table' && drawn !== full && hasNativeTableSettings(el.content)) drawn = withTierVisibility(el, drawn);
      if (el.type === 'text' || el.type === 'heading') drawn = withLegacyBlockTags(el, drawn);
      // A table read out of legacy markup with its own classes (aico legacyTableClass): the block
      // printed it bare with those classes, and the tab's stylesheet drew its look from them.
      if (el.type === 'table' && el.content && typeof el.content.legacyTableClass === 'string') {
        var tableClass = el.content.legacyTableClass.replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').trim();
        var bare = /<table>([\s\S]*)<\/table>/.exec(drawn);
        if (tableClass && bare) drawn = withTierVisibility(el, '<table class="' + tableClass + '">' + bare[1] + '</table>');
      }
      // A table the legacy body held inside the old shop's `div.rte` wrapper (aico legacyRte): put
      // back inside it, so the theme's `.rte table` rules (fixed layout, cell padding) apply again.
      if (el.type === 'table' && el.content && el.content.legacyRte === true && /^\s*<table\b/.test(drawn)) {
        drawn = '<div class="rte"><div class="contents">' + drawn + '</div></div>';
      }
      // A size the legacy picture carried as its width attribute (aico legacyWidth) stays an
      // attribute, which the tab's picture rules outrank exactly as they did.
      if (el.type === 'image' && el.content && /^\d{1,5}(\.\d+)?%?$/.test(String(el.content.legacyWidth || ''))) {
        drawn = drawn.replace(/<img /, '<img width="' + el.content.legacyWidth + '" ');
      }
      // The width the legacy picture's own style gave it in this language (aico legacyStyleWidth):
      // a translation sized its copy of the picture on its own, and only the content is localized.
      if (el.type === 'image' && el.content && /^\d{1,5}(\.\d+)?(px|%)$/.test(String(el.content.legacyStyleWidth || ''))) {
        var styleWidth = String(el.content.legacyStyleWidth);
        drawn = drawn.replace(/<img\b[^>]*>/, function (tag) {
          if (/\sstyle="/.test(tag)) {
            return tag.replace(/\sstyle="([^"]*)"/, function (all, css) {
              var rest = css.replace(/(^|;)\s*width\s*:[^;]*/g, '$1').replace(/^;+|;+$/g, '');
              return ' style="' + (rest ? rest + ';' : '') + 'width:' + styleWidth + '"';
            });
          }
          return tag.replace(/^<img/, '<img style="width:' + styleWidth + '"');
        });
      }
      // The float the legacy picture's own style gave it in this language (aico legacyStyleFloat;
      // an empty value is a language whose copy did not float).
      if (el.type === 'image' && el.content && typeof el.content.legacyStyleFloat === 'string' && /^(left|right|)$/.test(el.content.legacyStyleFloat)) {
        var styleFloat = el.content.legacyStyleFloat;
        drawn = drawn.replace(/<img\b[^>]*>/, function (tag) {
          if (/\sstyle="/.test(tag)) {
            return tag.replace(/\sstyle="([^"]*)"/, function (all, css) {
              var rest = css.replace(/(^|;)\s*float\s*:[^;]*/g, '$1').replace(/^;+|;+$/g, '');
              var next = (rest ? rest + (styleFloat ? ';' : '') : '') + (styleFloat ? 'float:' + styleFloat : '');
              return next ? ' style="' + next + '"' : '';
            });
          }
          return styleFloat ? tag.replace(/^<img/, '<img style="float:' + styleFloat + '"') : tag;
        });
      }
      // The legacy picture's own classes (aico legacyImageClass), for the theme's rules for them.
      if (el.type === 'image' && el.content && typeof el.content.legacyImageClass === 'string') {
        var imageClass = el.content.legacyImageClass.replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').trim();
        if (imageClass) drawn = drawn.replace(/<img /, '<img class="' + imageClass + '" ');
      }
      // A picture that stood in a paragraph or a heading of the block (aico marks it legacyWrap):
      // the tag goes back round it, with its own alignment. When the paragraph went on with
      // words (legacyWrapContinues), those are the next text element's first paragraph, and
      // they rejoin the picture in one paragraph, as the block printed them.
      // A native inline picture (content.inlineWith, see drawSiblings()).
      var inlineMode = drawn && drawn.indexOf('<div') !== 0 ? nativeInlineMode(el) : '';
      if (inlineMode === 'previous' && previousHost) {
        var closedHost = insertInlinePicture(html.slice(previousHost.start), previousHost.el, drawn, 'previous', false);
        if (closedHost !== null) {
          html = html.slice(0, previousHost.start) + closedHost;
          lastHost = previousHost;
          continue;
        }
      }
      if (inlineMode === 'next' && isInlineHost(elements[i + 1])) {
        pendingPicture = drawn;
        pendingBreak = el.content.inlineBreak === true;
        continue;
      }
      var wrap = el.type === 'image' && el.content ? String(el.content.legacyWrap || '').toLowerCase() : '';
      // A picture that ended a paragraph of words (aico legacyWrapEndsParagraph): it goes back in
      // before the close of the paragraph the previous text element ended with.
      if (drawn && wrap === 'p' && el.content.legacyWrapEndsParagraph === true && /<\/p>$/.test(html)) {
        html = html.slice(0, -4) + drawn + '</p>';
        continue;
      }
      // A picture that ended a heading's words (legacyWrapEndsParagraph with a heading tag): it goes
      // back in before the close of the heading drawn just before it.
      if (drawn && /^h[1-6]$/.test(wrap) && el.content.legacyWrapEndsParagraph === true && html.slice(-5).toLowerCase() === '</' + wrap + '>') {
        html = html.slice(0, -5) + drawn + '</' + wrap + '>';
        continue;
      }
      // A picture that stood bare in its box with its caption words straight after it (aico
      // legacyWrap 'bare' with legacyWrapContinues): the next text element's first paragraph is
      // drawn after the picture without the paragraph, as the legacy box printed it.
      // A picture that stood alone in a CKEditor picture box (aico legacyWrap 'figure').
      if (drawn && wrap === 'figure') {
        var figureClass = String(el.content.legacyWrapClass || 'image').replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').trim() || 'image';
        var figureStyle = '';
        // A figure CKEditor resized: the box takes the width and the picture fills it.
        if ((' ' + figureClass + ' ').indexOf(' image_resized ') >= 0) {
          drawn = drawn.replace(/<img\b[^>]*>/, function (tag) {
            return tag.replace(/\sstyle="([^"]*)"/, function (all, css) {
              var width = /(?:^|;)\s*width\s*:\s*([^;]+)/.exec(css);
              if (width) figureStyle = ' style="width:' + width[1].trim() + ';"';
              var rest = css.replace(/(^|;)\s*width\s*:[^;]*/g, '$1').replace(/^;+|;+$/g, '');
              return rest ? ' style="' + rest + '"' : '';
            });
          });
        }
        html += '<figure class="' + figureClass + '"' + figureStyle + '>' + drawn + '</figure>';
        continue;
      }
      if (drawn && wrap === 'bare') {
        var bareNext = elements[i + 1];
        var bareGoesOn = el.content.legacyWrapContinues === 'br' || el.content.legacyWrapContinues === 'inline';
        if (bareGoesOn && bareNext && bareNext.type === 'text' && bareNext.localeHidden !== true) {
          var bareHtml = unwrapPlainBox(renderElement(bareNext));
          var bareFirst = /^<p>([\s\S]*?)<\/p>/.exec(bareHtml);
          if (bareFirst && bareHtml.indexOf('<div') !== 0) {
            html += drawn + (el.content.legacyWrapContinues === 'br' ? '<br>' : '') + bareFirst[1] + bareHtml.slice(bareFirst[0].length);
            i++;
            continue;
          }
        }
      }
      if (drawn && /^(p|h[1-6])$/.test(wrap)) {
        var align = String(el.content.legacyWrapAlign || '').toLowerCase();
        var open = '<' + wrap + (/^(left|center|right|justify)$/.test(align) ? ' style="text-align:' + align + ';"' : '') + '>';
        // The emphasis tags the picture stood in inside its paragraph (aico legacyWrapInline).
        var inlineTags = wrap === 'p' ? String(el.content.legacyWrapInline || '').toLowerCase().split(/\s+/) : [];
        for (var t = inlineTags.length - 1; t >= 0; t--) {
          if (/^(strong|b|em|i|u)$/.test(inlineTags[t])) drawn = '<' + inlineTags[t] + '>' + drawn + '</' + inlineTags[t] + '>';
        }
        // The line breaks that stood before the picture in its paragraph (aico legacyWrapLeadingBreaks).
        var breaks = wrap === 'p' ? parseInt(el.content.legacyWrapLeadingBreaks, 10) : 0;
        if (breaks > 0 && breaks <= 3) open += new Array(breaks + 1).join('<br>');
        var tail = '';
        var next = elements[i + 1];
        if (wrap === 'p' && (el.content.legacyWrapContinues === 'br' || el.content.legacyWrapContinues === 'inline') && next && next.type === 'text' && next.localeHidden !== true) {
          var nextHtml = unwrapPlainBox(renderElement(next));
          var first = /^<p>([\s\S]*?)<\/p>/.exec(nextHtml);
          if (first && nextHtml.indexOf('<div') !== 0) {
            drawn += (el.content.legacyWrapContinues === 'br' ? '<br>' : '') + first[1];
            tail = nextHtml.slice(first[0].length);
            i++;
          }
        }
        // A heading that opened with the picture and went on with its words: the next element is
        // that heading (same level), drawn with its own attributes around the picture and words.
        var continues = el.content.legacyWrapContinues;
        if (wrap !== 'p' && (continues === 'br' || continues === 'inline') && next && next.type === 'heading' && next.localeHidden !== true) {
          var headingHtml = unwrapPlainBox(renderElement(next));
          var heading = /^<(h[1-6])\b([^>]*)>([\s\S]*)<\/\1>$/.exec(headingHtml);
          if (heading && heading[1].toLowerCase() === wrap) {
            html += '<' + heading[1] + heading[2] + '>' + drawn + (continues === 'br' ? '<br>' : '') + heading[3] + '</' + heading[1] + '>';
            i++;
            continue;
          }
        }
        drawn = open + drawn + '</' + wrap + '>' + tail;
      }
      if (pendingPicture) {
        var opened = drawn && isInlineHost(el) ? insertInlinePicture(drawn, el, pendingPicture, 'next', pendingBreak) : null;
        if (opened !== null) {
          drawn = opened;
          pendingPicture = null;
        } else {
          flushPicture();
        }
      }
      if (drawn && (el.type === 'text' || el.type === 'heading')) lastHost = { start: html.length, el: el };
      html += drawn;
    }
    flushPicture();
    return html + blankAfter;
  }

  function renderLegacyCard(run) {
    var mark = legacyCardOf(run[0]);
    var type = String(mark.blockType || 'html').replace(/[^\w-]/g, '');
    var inner = renderLegacyCardInner(type, run);
    var title = '';
    for (var i = 0; i < run.length && !title; i++) title = subsectionTitleOf(run[i]);
    if (!inner && !title) return '';
    return '<div class="card content-card"' + (title ? ' data-subsection-title="' + escapeAttr(title) + '"' : '') + '>' +
      '<div class="card-body"><div class="content-type-' + type + '">' + inner + '</div></div></div>';
  }

  // ============================================================
  // Native cards
  //
  // The builder's `card` element (aiconeo cardModel.ts): one block of the
  // tab as a box of child elements, with a title (the in-page navigation
  // label), a look and, for a category list, a row limit. It is drawn with
  // exactly the markup a legacyCard run is drawn with, so every rule of the
  // theme's stylesheet and scripts applies to it unchanged; the document
  // carries no class, no CSS and no hidden mark. Its own styles (padding,
  // margin, radius, background, border, set in the builder's Style tab) are
  // drawn inline, and an unset style leaves the box to the theme.
  //
  //   content.title     string   ''         data-subsection-title
  //   content.look      string   'default'  default | data-sheet | category-list
  //   content.rowLimit  integer  10         category-list: rows drawn, 0 = all
  // ============================================================

  var NATIVE_CARD_BLOCK_TYPES = { 'default': 'html', 'data-sheet': 'attribute', 'category-list': 'sub_sub_category' };

  // The children a category list draws: its first h2 heading (the list's
  // title), every child that is not a category row, and the first rowLimit
  // rows (a columns, heading or image child each). Other looks draw them all.
  function nativeCardChildren(el, look) {
    var children = Array.isArray(el.children) ? el.children : [];
    if (look !== 'category-list') return children;
    var raw = el.content ? el.content.rowLimit : undefined;
    var limit = typeof raw === 'number' ? raw : parseInt(raw, 10);
    if (isNaN(limit) || limit < 0) limit = 10;
    var out = [];
    var titled = false;
    var rows = 0;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      if (!child || child.localeHidden === true || child.hidden === true) continue;
      var level = String((child.content && child.content.level) || '').toLowerCase();
      if (child.type === 'heading' && !titled && level === 'h2') {
        titled = true;
        out.push(child);
        continue;
      }
      if (child.type !== 'columns' && child.type !== 'heading' && child.type !== 'image') {
        out.push(child);
        continue;
      }
      rows++;
      if (limit === 0 || rows <= limit) out.push(child);
    }
    return out;
  }

  function renderNativeCard(el) {
    if (!el || el.localeHidden === true || el.hidden === true) return '';
    var content = el.content || {};
    var look = Object.prototype.hasOwnProperty.call(NATIVE_CARD_BLOCK_TYPES, content.look) ? content.look : 'default';
    var type = NATIVE_CARD_BLOCK_TYPES[look];
    var inner = renderLegacyCardInner(type, nativeCardChildren(el, look));
    var title = typeof content.title === 'string' ? content.title.trim() : '';
    if (!inner && !title) return '';
    return '<div class="card content-card"' + (title ? ' data-subsection-title="' + escapeAttr(title) + '"' : '') + inlineStyleAttr(el) + '>' +
      '<div class="card-body"><div class="content-type-' + type + '">' + inner + '</div></div></div>';
  }

  // A card anywhere else (inside a container, a nested columns): the same markup.
  renderers.card = renderNativeCard;

  // One column of a legacy row: consecutive elements of one block form a card;
  // an element without the mark keeps the engine's own wrapper. A native card
  // is drawn as its own card.
  function renderLegacyCardColumn(elements, columnIndex) {
    var html = '';
    var run = [];
    var flush = function () {
      if (run.length) html += renderLegacyCard(run);
      run = [];
    };
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el || el.localeHidden === true) continue;
      if (el.type === 'card') {
        flush();
        html += withTierVisibility(el, renderNativeCard(el));
        continue;
      }
      var mark = legacyCardOf(el);
      if (!mark) {
        flush();
        var rendered = renderElement(el);
        if (rendered) html += wrapContent(rendered, i + 1, el);
        continue;
      }
      if (run.length && String(legacyCardOf(run[0]).block) !== String(mark.block)) flush();
      run.push(el);
    }
    flush();
    return wrapColumn([html], columnIndex);
  }

  function renderLegacyCardSection(section, startRowIndex) {
    var isFullWidth = !!section.fullWidth;
    var elements = section.elements;
    var html = '';
    var rowIndex = startRowIndex;
    var stack = [];
    var flushStack = function () {
      if (stack.length === 0) return;
      html += wrapRow(renderLegacyCardColumn(stack, 1), rowIndex++, 1, isFullWidth);
      stack = [];
    };
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el || el.localeHidden === true) continue;
      // A `columns` element without the mark is the legacy row's own grid; one
      // with it is the content of a block and belongs inside that block's card.
      if (el.type === 'columns' && !legacyCardOf(el)) {
        flushStack();
        var content = el.content || {};
        var count = parseInt(content.columnCount, 10);
        if (isNaN(count) || count < 1) count = 2;
        var children = Array.isArray(el.children) ? el.children : [];
        var buckets = [];
        for (var c = 0; c < count; c++) buckets.push([]);
        for (var j = 0; j < children.length; j++) {
          var child = children[j];
          var ci = child && child.content && typeof child.content.columnIndex === 'number' ? child.content.columnIndex : 0;
          if (ci < 0) ci = 0;
          if (ci >= count) ci = count - 1;
          buckets[ci].push(child);
        }
        var columnsHtml = '';
        for (var b = 0; b < count; b++) columnsHtml += renderLegacyCardColumn(buckets[b], b + 1);
        html += withTierVisibility(el, wrapRow(columnsHtml, rowIndex++, count, isFullWidth, columnRatioTemplate(content, count)));
      } else {
        stack.push(el);
      }
    }
    flushStack();
    return { html: html, rowCount: rowIndex - startRowIndex };
  }

  // Emits N rows per section: consecutive non-columns elements stack into one row;
  // each `columns` element becomes its own multi-column row, in document order.
  function renderSection(section, startRowIndex) {
    if (!section || !Array.isArray(section.elements) || section.elements.length === 0) {
      return { html: '', rowCount: 0 };
    }
    if (sectionHasLegacyCards(section)) return renderLegacyCardSection(section, startRowIndex);

    var isFullWidth = !!section.fullWidth;
    var elements = section.elements;
    var html = '';
    var rowIndex = startRowIndex;
    var buffer = [];
    // Consecutive non-columns elements, drawn together so a native inline
    // picture can go into the text beside it (drawSiblings()).
    var stack = [];

    function flushStack() {
      var drawnList = drawSiblings(stack);
      for (var d = 0; d < drawnList.length; d++) {
        buffer.push(wrapContent(drawnList[d].html, buffer.length + 1, drawnList[d].el));
      }
      stack = [];
    }

    function flushBuffer() {
      flushStack();
      if (buffer.length === 0) return;
      var singleCol = wrapColumn(buffer, 1);
      html += wrapRow(singleCol, rowIndex++, 1, isFullWidth);
      buffer = [];
    }

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el) continue;
      if (el.localeHidden === true) continue;
      if (el.type === 'columns') {
        flushBuffer();
        var columnsRow = withTierVisibility(el, renderColumnsRow(el, rowIndex, isFullWidth));
        if (columnsRow) { html += columnsRow; rowIndex++; }
      } else {
        stack.push(el);
      }
    }
    flushBuffer();

    return { html: html, rowCount: rowIndex - startRowIndex };
  }

  function render(data, options) {
    if (!hasNeoContent(data)) return '';
    var neo = data.contentBuilderNeoJson;
    // One warning per type PER RENDER, so a re-render reports again.
    warnedTypes = {};

    var previousThemeTables = THEME_TABLES;
    THEME_TABLES = !!(options && options.themeTables === true);
    try {
      var html = '';
      var nextRowIndex = 1;
      for (var i = 0; i < neo.sections.length; i++) {
        var result = renderSection(neo.sections[i], nextRowIndex);
        html += result.html;
        nextRowIndex += result.rowCount;
      }
      return html;
    } finally {
      THEME_TABLES = previousThemeTables;
    }
  }

  // ============================================================
  // Self-contained styles for slideshow/video/youtube/gallery.
  // Injected once per page so the renderer ships its own CSS.
  // ============================================================

  var STYLES_INJECTED = false;
  var NEO_STYLES = [
    '.neo-slideshow__viewport{position:relative;overflow:hidden;border-radius:inherit;}',
    '.neo-slideshow__track{display:flex;width:100%;height:100%;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;scroll-behavior:smooth;scrollbar-width:none;-ms-overflow-style:none;}',
    '.neo-slideshow__track::-webkit-scrollbar{display:none;}',
    '.neo-slideshow__slide{flex:0 0 100%;height:100%;scroll-snap-align:start;position:relative;}',
    '.neo-slideshow__slide img{width:100%;height:100%;object-fit:cover;display:block;}',
    '.neo-slideshow__nav{position:absolute;top:50%;transform:translateY(-50%);background:rgba(0,0,0,0.5);color:#fff;border:0;width:36px;height:36px;border-radius:50%;cursor:pointer;z-index:1;font-size:16px;line-height:1;padding:0;display:flex;align-items:center;justify-content:center;}',
    '.neo-slideshow__nav--prev{left:8px;}',
    '.neo-slideshow__nav--next{right:8px;}',
    '.neo-slideshow__dots{display:flex;justify-content:center;gap:8px;margin-top:8px;}',
    '.neo-slideshow__dot{width:8px;height:8px;border-radius:50%;border:0;background:#ccc;cursor:pointer;padding:0;}',
    '.neo-slideshow__dot.is-active{background:#333;}',
    '.neo-slideshow__caption{position:absolute;inset:0;padding:24px;background:rgba(0,0,0,0.35);color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;gap:12px;}',
    '.neo-slideshow__headline{margin:0;color:#fff !important;font-size:28px;font-weight:600;line-height:1.2;}',
    '.neo-slideshow__btn{display:inline-block;padding:8px 16px;background:#fff;color:#111;text-decoration:none;border-radius:4px;}',
    '.neo-video__frame{position:relative;width:100%;}',
    '.neo-video__frame iframe,.neo-video__frame video{position:absolute;inset:0;width:100%;height:100%;border:0;}',
    '.content-type-neo-image img{max-width:100%;height:auto;display:block;}',
    '.content-type-neo-image.content-type-neo-image--line>.neo-image-line{margin-left:0;margin-right:0;}',
    // Rich text and html blocks carry bare <img width=1600> from the legacy editor; keep them in the column.
    '.content-type-neo-text img,.content-type-neo-html img{max-width:100%;height:auto;}',
    // overflow:hidden so wrapper border-radius actually clips the iframe/slides inside.
    '.content-type-neo-youtube,.content-type-neo-video,.content-type-neo-slideshow{overflow:hidden;}',
    '.content-type-neo-gallery{display:grid;gap:8px;}',
    '.content-type-neo-gallery img{width:100%;height:auto;display:block;}',
    '.neo-media-gallery__title{margin:0 0 16px;}',
    '.neo-media-gallery__grid{display:grid;gap:8px;}',
    '.neo-media-gallery__item{margin:0;}',
    '.neo-media-gallery__item img{width:100%;height:auto;display:block;}',
    '.neo-media-gallery__caption{font-size:12px;color:#555;margin-top:4px;}',
    '[class*="content-type-neo-"] a{text-decoration:underline;}',
    // Styled islands (see LOOK_DEPTH): descendants take colour, size, weight
    // and line height from the island instead of the product tab's forced
    // values. The reset is scoped to the two places that force those values
    // (`.product_submenu .tab-content .content-row div *` in
    // section-main-product.css and `.template-collection .produkte
    // #tab-weitere-infos .content-row div *` in template-collection.css) and
    // outranks each of them; elsewhere nothing is forced and the engine's own
    // defaults apply untouched. Inside an island an element's own inline
    // typography is written with !important (importantizeTypography), so it
    // still wins over the reset.
    // Headings keep the theme's heading look unless the element styles them.
    '.product_submenu .tab-content .content-row .neo-look *:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6),' +
      '.template-collection .produkte #tab-weitere-infos .content-row .neo-look *:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6)' +
      '{color:inherit!important;font-size:inherit!important;font-weight:inherit!important;line-height:inherit!important;}',
    '.product_submenu .tab-content .content-row .neo-look strong,.product_submenu .tab-content .content-row .neo-look b,' +
      '.template-collection .produkte #tab-weitere-infos .content-row .neo-look strong,.template-collection .produkte #tab-weitere-infos .content-row .neo-look b' +
      '{font-weight:700!important;}',
    // The product tab also gives every body cell of every table `width:50%`
    // (custom.css, `.product_submenu .tab-content table tbody tr td`), which
    // overrides the column widths a styled table carries in its <colgroup>:
    // a 6-column comparison table came out 2 wide columns and 3 squeezed ones
    // (product 2729). Inside a styled table the cells size from the colgroup.
    '.product_submenu .tab-content .content-row .neo-look td,.product_submenu .tab-content .content-row .neo-look th,' +
      '.template-collection .produkte #tab-weitere-infos .content-row .neo-look td,.template-collection .produkte #tab-weitere-infos .content-row .neo-look th' +
      '{width:auto;}',
    '.neo-media-gallery__masonry{column-gap:8px;}',
    '.neo-media-gallery__masonry .neo-media-gallery__item{break-inside:avoid;margin:0 0 8px;display:block;}',
    '.neo-media-gallery__carousel{position:relative;}',
    '.neo-media-gallery__carousel-track{display:flex;gap:8px;overflow-x:auto;overflow-y:hidden;scrollbar-width:none;-ms-overflow-style:none;}',
    '.neo-media-gallery__carousel-track::-webkit-scrollbar{display:none;}',
    '.neo-media-gallery__carousel .neo-media-gallery__item{flex:0 0 calc(60% - 8px);}',
    '.neo-media-gallery__nav{position:absolute;top:50%;transform:translateY(-50%);background:rgba(0,0,0,0.5);color:#fff;border:0;width:36px;height:36px;border-radius:50%;cursor:pointer;z-index:1;font-size:16px;line-height:1;padding:0;display:flex;align-items:center;justify-content:center;}',
    '.neo-media-gallery__nav--prev{left:8px;}',
    '.neo-media-gallery__nav--next{right:8px;}',
    '.neo-media-gallery__scrubber{position:relative;height:8px;background:#e5e5e5;border-radius:4px;margin-top:16px;cursor:pointer;touch-action:none;user-select:none;overflow:hidden;}',
    '.neo-media-gallery__scrubber-thumb{position:absolute;top:0;left:0;height:100%;width:48px;background:#333;border-radius:4px;pointer-events:none;min-width:48px;display:block;}',
    '.neo-media-gallery__scrubber:hover .neo-media-gallery__scrubber-thumb{background:#111;}',
    // ---- types added 2026-09-10 (see the renderer blocks above) ----
    // Deliberately minimal: enough that each new type reads as a block instead
    // of a run-on paragraph, and never so much that it fights theme CSS.
    '.content-type-neo-container{display:block;}',
    '.content-type-neo-table{overflow-x:auto;margin:16px 0;}',
    '.content-type-neo-table table{border-collapse:collapse;width:100%;}',
    '.content-type-neo-table th,.content-type-neo-table td{border:1px solid rgba(0,0,0,0.12);padding:8px 12px;text-align:left;vertical-align:top;}',
    '.content-type-neo-table thead th{font-weight:600;background:rgba(0,0,0,0.04);}',
    // ---- types added 2026-09-11: quote, audio ----
    // Both arms also inline these on the markup (a news body can land in a
    // theme that never loaded this CSS); the rules here only add what an
    // inline style cannot carry cheaply.
    '.content-type-neo-quote{max-width:100%;}',
    '.content-type-neo-quote blockquote{quotes:none;}',
    '.content-type-neo-quote cite{font-style:normal;}',
    '.content-type-neo-audio{margin:16px 0;}',
    '.content-type-neo-audio iframe{max-width:100%;border:0;}',
    '.content-type-neo-audio audio{width:100%;}',
    '.neo-audio__mount{min-height:0;}',
    '.content-type-neo-spacer{width:100%;}',
    '.content-type-neo-badge .badge{display:inline-block;padding:4px 12px;border-radius:999px;background:rgba(0,0,0,0.08);font-size:0.875em;}',
    '.content-type-neo-features,.content-type-neo-multicolumn,.content-type-neo-team,.content-type-neo-pricing{display:grid;gap:24px;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));}',
    '.content-type-neo-multicolumn--vertical{grid-template-columns:1fr;}',
    '.content-type-neo-features img,.content-type-neo-team img{max-width:100%;height:auto;display:block;}',
    '.content-type-neo-testimonial blockquote,.content-type-neo-reviews blockquote{margin:0 0 16px;padding:0;}',
    '.content-type-neo-testimonial .rating,.content-type-neo-reviews .rating{color:#f5a623;letter-spacing:2px;}',
    '.content-type-neo-testimonial .role{display:block;font-size:0.875em;opacity:0.7;}',
    '.content-type-neo-testimonial img.avatar{width:56px;height:56px;border-radius:50%;object-fit:cover;}',
    '.content-type-neo-partner-logos .logos{display:flex;flex-wrap:wrap;gap:24px;align-items:center;}',
    '.content-type-neo-partner-logos img{max-height:64px;width:auto;}',
    '.content-type-neo-accordion details,.content-type-neo-faq details,.content-type-neo-feature-accordion details,.content-type-neo-tabs details{border-bottom:1px solid rgba(0,0,0,0.12);}',
    '.content-type-neo-accordion summary,.content-type-neo-faq summary,.content-type-neo-feature-accordion summary,.content-type-neo-tabs summary{cursor:pointer;padding:12px 0;font-weight:600;}',
    '.content-type-neo-accordion .accordion-content,.content-type-neo-faq .faq-answer,.content-type-neo-tabs .neo-tabs__content{padding:0 0 12px;}',
    // Accordion itemStyle 'pill' (accordionPillLook()). Scoped by the mount, not by the
    // accordion's own root, which a native card's body unwraps.
    '.content-builder-neo details.neo-accordion--pill{border-bottom:0;margin:0;}',
    '.content-builder-neo details.neo-accordion--pill>summary{list-style:none;cursor:pointer;user-select:none;display:inline-flex;align-items:center;gap:8px;padding:6px 10px;border-radius:999px;border:1px solid transparent;font-weight:inherit;}',
    '.content-builder-neo details.neo-accordion--pill>summary::-webkit-details-marker{display:none;}',
    '.content-builder-neo details.neo-accordion--pill .neo-accordion__badge{display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:999px;font-size:14px;line-height:1;}',
    '.content-builder-neo details.neo-accordion--pill>.accordion-content{margin-top:10px;padding:12px 14px;border-radius:12px;border:1px solid transparent;}',
    // The answer box's own padding is its edge: its first and last paragraph keep no outer margin
    // (above the product tab's paragraph margin rule); paragraphs between them keep their gap.
    '.product_submenu .tab-content .content-builder-content details.neo-accordion--pill>.accordion-content>:first-child,' +
      '.content-builder-neo details.neo-accordion--pill>.accordion-content>:first-child{margin-top:0!important;}',
    '.product_submenu .tab-content .content-builder-content details.neo-accordion--pill>.accordion-content>:last-child,' +
      '.content-builder-neo details.neo-accordion--pill>.accordion-content>:last-child{margin-bottom:0!important;}',
    '.content-type-neo-before-after{display:grid;gap:8px;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));}',
    '.content-type-neo-before-after img,.content-type-neo-slideshow figure img{width:100%;height:auto;display:block;}',
    '.content-type-neo-map{width:100%;}',
    '.content-type-neo-map iframe,.content-type-neo-social-embed iframe,.content-type-neo-booking-embed iframe{width:100%;border:0;}',
    '.content-type-neo-job-listings .job{padding:16px 0;border-bottom:1px solid rgba(0,0,0,0.12);}',
    '.content-type-neo-job-listings .job-meta span{margin-right:12px;opacity:0.75;font-size:0.875em;}',
    '.content-type-neo-site-header,.content-type-neo-site-footer{display:flex;flex-wrap:wrap;gap:16px;align-items:center;}',
    '.content-type-neo-site-header nav{display:flex;flex-wrap:wrap;gap:16px;}',
    '.content-type-neo-file-list__items,.neo-file-list__items{list-style:none;margin:0;padding:0;}',
    '.neo-file-list__item{border-bottom:1px solid rgba(0,0,0,0.12);}',
    '.neo-file-list__link{display:flex;gap:12px;align-items:center;padding:10px 0;text-decoration:none;}',
    '.neo-file-list__icon{font-size:0.7em;font-weight:700;letter-spacing:0.05em;opacity:0.6;min-width:3em;}',
    '.neo-file-list__meta{margin-left:auto;font-size:0.8em;opacity:0.65;}',
    '.content-type-neo-cta{display:flex;flex-direction:column;align-items:center;gap:12px;text-align:center;}',
    '.content-type-neo-hero{min-height:280px;padding:48px 24px;text-align:center;}',
    '.content-type-neo-anchor-nav .neo-anchor-nav{display:flex;flex-wrap:wrap;gap:20px;}',
    '.neo-anchor-nav--vertical{flex-direction:column;}',
    '.content-type-neo-marquee{overflow:hidden;}',
    '.neo-marquee--bordered{border-top:1px solid rgba(0,0,0,0.12);border-bottom:1px solid rgba(0,0,0,0.12);}',
    '.neo-marquee__track{display:flex;gap:40px;align-items:center;padding:16px 0;animation-name:neo-marquee-scroll;animation-timing-function:linear;animation-iteration-count:infinite;}',
    '.neo-marquee__track--pausable:hover{animation-play-state:paused;}',
    '@keyframes neo-marquee-scroll{from{transform:translateX(0);}to{transform:translateX(-50%);}}',
    '.neo-marquee__item img{max-height:48px;width:auto;display:block;}',
    '.content-type-neo-announcement-bar{display:flex;gap:12px;align-items:center;justify-content:center;padding:10px 16px;}',
    '.neo-carousel__card{flex:0 0 var(--neo-carousel-card,300px);}',
    '.neo-pricing__plan{border:1px solid rgba(0,0,0,0.12);border-radius:12px;padding:20px;}',
    '.neo-pricing__plan.is-highlighted{border-color:currentColor;}',
    '.neo-pricing__features{list-style:none;margin:12px 0 0;padding:0;}',
    '.content-type-neo-products{display:grid;gap:16px;}',
    '.content-type-neo-products img{width:100%;height:auto;display:block;}',
    '.neo-product__badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:0.75em;background:rgba(0,0,0,0.08);}',
    '.content-type-neo-brand-news{display:grid;gap:16px;}',
    '.neo-brand-news__item img{width:100%;height:auto;display:block;object-fit:cover;}',
    '.content-type-neo-brand-gender-links{display:flex;gap:16px;}',
    '.content-type-neo-form .form-item,.content-type-neo-linked-form .form-item{margin-bottom:12px;}',
    '.content-type-neo-form .aico_label,.content-type-neo-linked-form .aico_label{display:block;margin-bottom:4px;font-weight:600;}',
    '.content-type-neo-form .aico_input,.content-type-neo-linked-form .aico_input{width:100%;padding:8px 10px;border:1px solid rgba(0,0,0,0.25);border-radius:6px;font:inherit;}',
    '.aico_label__required{color:#c0392b;}',
    '.aico-custom-form__fields{display:grid;gap:12px;}',
    '.form-item--half{grid-column:span 1;}',
    '.aico-form-banner{padding:12px 16px;border-radius:6px;margin-bottom:12px;}',
    '.aico-form-banner--success{background:#e6f6ec;color:#1e6b3a;}',
    '.aico-form-banner--error{background:#fdecea;color:#8e2a20;}',
    '.content-type-neo-countdown{font-variant-numeric:tabular-nums;}',
    // An unresolvable/unknown element is never invisible, but it must not look
    // like a designed block either — its copy inherits the article's own type.
    '.content-type-neo-unknown,.content-type-neo-unresolved{display:block;}',
    // Runtime lists. The rules the AICO-rendered page ships inline with each
    // block (core::shopify-pages.list-agenda-page / list-asking-page /
    // news-page), so the fetched list looks like the one it replaces.
    '.neo-runtime-list .split-row-in-columns{width:30%;float:left;}',
    '.neo-runtime-list .neo-runtime-list__colhead{margin-top:6px;text-align:center;}',
    '.neo-runtime-list .neo-runtime-list__colhead h3{font-weight:bold;color:#adaeaf;}',
    '.neo-runtime-list .neo-runtime-list__heading{text-transform:uppercase;}',
    '.neo-runtime-list .neo-runtime-list__month{font-weight:bold;text-transform:uppercase;}',
    '.neo-runtime-list .team-table{width:100%;margin:1rem 0;border:0;border-collapse:separate;border-spacing:0 11px;clear:both;}',
    '.neo-runtime-list .team-table td,.neo-runtime-list .team-table th{border:2px solid #e7e7e7;border-style:solid none;padding:11px;}',
    '.neo-runtime-list .team-table td:first-child,.neo-runtime-list .team-table th:first-child{border-left-style:solid;border-top-left-radius:11px;border-bottom-left-radius:11px;}',
    '.neo-runtime-list .team-table td:last-child{border-right-style:solid;border-top-right-radius:11px;border-bottom-right-radius:11px;}',
    '.neo-runtime-list .team-table h3{font-weight:bold;margin:0;}',
    '.neo-runtime-list .ask-tbody{display:table-row-group;width:100%;}',
    '.neo-runtime-list .neo-runtime-list__date{width:30%;}',
    '.neo-runtime-list .neo-runtime-list__time,.neo-runtime-list .neo-runtime-list__what{width:30%;text-align:center;}',
    '.neo-runtime-list .ask-col-date{width:25%;}.neo-runtime-list .ask-col-time{width:20%;}.neo-runtime-list .ask-col-title{width:30%;}',
    '.neo-runtime-list .more-info-agenda{cursor:pointer;padding-left:20px;font-size:29px;font-weight:900;background-color:#e7e7e7;}',
    '.neo-runtime-list .more-info{width:25%;cursor:pointer;color:#1a0dab;}',
    '.neo-runtime-list .ask-details-cell{padding:0;border:none;}',
    '.neo-runtime-list .more-details-table{width:100%;text-align:left;margin-left:1%;border-collapse:collapse;}',
    '.neo-runtime-list .more-details-table th{text-transform:uppercase;border:none;}',
    '.neo-runtime-list .more-details-table td{border:none;font-weight:bold;}',
    '.neo-runtime-list .ask-pre{white-space:pre-line;}',
    '@media screen and (max-width:768px){.neo-runtime-list h2,.neo-runtime-list h3,.neo-runtime-list th,.neo-runtime-list td{font-size:13px;}.neo-runtime-list .more-info-agenda{padding-left:7px;font-size:20px;}}',
    '.content-type-neo-blog-feed .neo-news-card{position:relative;}',
    '.content-type-neo-blog-feed .neo-news-card a{text-decoration:none;}',
    '.content-type-neo-blog-feed .neo-news-card__body{margin:20px 10px 250px 10px;max-height:100%;float:left;}',
    '.content-type-neo-blog-feed .neo-news-card__image{max-width:300px;height:230px;object-fit:cover;border-radius:5px;}',
    '.content-type-neo-blog-feed .text-block{position:absolute;padding-left:20px;padding-right:20px;width:300px;border-radius:5px;margin-top:-7px;}',
    '.neo-unknown__copy{margin:0 0 12px;}',
    // Nested columns in a card (renderNestedColumns()): not the category tiles the tab draws for a
    // `.card .content-row .content-column` (section-main-product.css), but the columns the legacy
    // Bootstrap row drew: equal tracks that a wide picture cannot widen, no frame, no clipping.
    '#product-info .content-builder-content .card .content-row[data-neo-columns]>.content-column{width:auto!important;max-width:none!important;min-width:0;padding:0!important;margin:0!important;border:0;border-radius:0;overflow:visible;}',
    '#product-info .content-builder-content .card .content-row.total-columns-2[data-neo-columns]{grid-template-columns:repeat(2,minmax(0,1fr));}',
    '#product-info .content-builder-content .card .content-row.total-columns-3[data-neo-columns]{grid-template-columns:repeat(3,minmax(0,1fr));}',
    '#product-info .content-builder-content .card .content-row.total-columns-4[data-neo-columns]{grid-template-columns:repeat(4,minmax(0,1fr));}',
    // A narrow page stacks them, as the legacy row's col-sm-* columns did below 768 px: one under
    // the other with no gap between them (the legacy columns were floats that stopped floating).
    '@media (max-width:767px){#product-info .content-builder-content .card .content-row[data-neo-columns]{grid-template-columns:minmax(0,1fr)!important;row-gap:0!important;}}',
  ].join('');

  function injectStyles() {
    if (STYLES_INJECTED) return;
    STYLES_INJECTED = true;
    try {
      var style = document.createElement('style');
      style.setAttribute('data-neo-styles', '1');
      style.appendChild(document.createTextNode(NEO_STYLES));
      (document.head || document.documentElement).appendChild(style);
    } catch (e) { /* no-op */ }
  }

  // ============================================================
  // Hydration (runs after the HTML string has been injected)
  // ============================================================

  function hydrateSlideshow(root) {
    if (root.__neoWired) return;
    root.__neoWired = true;

    var track = root.querySelector('.neo-slideshow__track');
    if (!track) return;
    var slides = track.children;
    var count = slides.length;
    if (count === 0) return;

    var dots = root.querySelectorAll('.neo-slideshow__dot');
    var prev = root.querySelector('.neo-slideshow__nav--prev');
    var next = root.querySelector('.neo-slideshow__nav--next');
    var idx = 0;
    var timer = null;

    function updateDots() {
      for (var k = 0; k < dots.length; k++) {
        if (k === idx) dots[k].classList.add('is-active');
        else dots[k].classList.remove('is-active');
      }
    }

    function goTo(i) {
      if (i < 0) i = count - 1;
      if (i >= count) i = 0;
      idx = i;
      track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
      updateDots();
    }

    function resetAutoplay() {
      if (timer) { clearInterval(timer); timer = null; }
      if (root.getAttribute('data-autoplay') !== '1' || count < 2) return;
      var ms = parseInt(root.getAttribute('data-interval'), 10) || 5000;
      timer = setInterval(function () {
        if (!root.isConnected) { clearInterval(timer); timer = null; return; }
        goTo(idx + 1);
      }, ms);
    }

    if (prev) prev.addEventListener('click', function () { goTo(idx - 1); resetAutoplay(); });
    if (next) next.addEventListener('click', function () { goTo(idx + 1); resetAutoplay(); });
    for (var k = 0; k < dots.length; k++) {
      (function (k) {
        dots[k].addEventListener('click', function () { goTo(k); resetAutoplay(); });
      })(k);
    }

    // Track manual swipes so dots stay in sync.
    var scrollTick;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTick);
      scrollTick = setTimeout(function () {
        var w = track.clientWidth;
        if (w === 0) return;
        var i = Math.round(track.scrollLeft / w);
        if (i !== idx) { idx = i; updateDots(); }
      }, 80);
    });

    updateDots();
    resetAutoplay();
  }

  // ============================================================
  // Lightbox: click a media-gallery image → full-size singleton overlay.
  // ============================================================

  var lightbox = { el: null, imgEl: null, capEl: null, prevBtn: null, nextBtn: null, items: [], idx: 0 };

  function ensureLightbox() {
    if (lightbox.el) return;
    var el = document.createElement('div');
    el.setAttribute('data-neo-lightbox', '1');
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = 'position:fixed;inset:0;z-index:999999;background:rgba(0,0,0,0.9);display:none;align-items:center;justify-content:center;padding:40px;';
    el.innerHTML =
      '<button type="button" data-neo-lb-close aria-label="Close" style="position:absolute;top:16px;right:16px;background:rgba(0,0,0,0.5);border:0;color:#fff;width:40px;height:40px;border-radius:50%;cursor:pointer;font-size:22px;line-height:1;padding:0;display:flex;align-items:center;justify-content:center;z-index:2;">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
      '</button>' +
      '<button type="button" data-neo-lb-prev aria-label="Previous" style="position:absolute;top:50%;left:16px;transform:translateY(-50%);background:rgba(0,0,0,0.5);border:0;color:#fff;width:48px;height:48px;border-radius:50%;cursor:pointer;font-size:20px;display:flex;align-items:center;justify-content:center;z-index:1;">&#10094;</button>' +
      '<button type="button" data-neo-lb-next aria-label="Next" style="position:absolute;top:50%;right:16px;transform:translateY(-50%);background:rgba(0,0,0,0.5);border:0;color:#fff;width:48px;height:48px;border-radius:50%;cursor:pointer;font-size:20px;display:flex;align-items:center;justify-content:center;z-index:1;">&#10095;</button>' +
      '<div data-neo-lb-stage style="max-width:90vw;max-height:90vh;display:flex;flex-direction:column;align-items:center;gap:12px;">' +
        '<img data-neo-lb-img alt="" style="max-width:100%;max-height:80vh;object-fit:contain;display:block;">' +
        '<div data-neo-lb-cap style="color:#fff;text-align:center;font-size:14px;display:none;"></div>' +
      '</div>';
    document.body.appendChild(el);

    lightbox.el = el;
    lightbox.imgEl = el.querySelector('[data-neo-lb-img]');
    lightbox.capEl = el.querySelector('[data-neo-lb-cap]');
    lightbox.prevBtn = el.querySelector('[data-neo-lb-prev]');
    lightbox.nextBtn = el.querySelector('[data-neo-lb-next]');

    el.querySelector('[data-neo-lb-close]').addEventListener('click', closeLightbox);
    lightbox.prevBtn.addEventListener('click', function () { stepLightbox(-1); });
    lightbox.nextBtn.addEventListener('click', function () { stepLightbox(1); });
    el.addEventListener('click', function (e) { if (e.target === el) closeLightbox(); });
    document.addEventListener('keydown', function (e) {
      if (!lightbox.el || lightbox.el.style.display !== 'flex') return;
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') stepLightbox(-1);
      else if (e.key === 'ArrowRight') stepLightbox(1);
    });
  }

  function showLightboxItem() {
    var item = lightbox.items[lightbox.idx];
    if (!item) return;
    lightbox.imgEl.src = item.src;
    lightbox.imgEl.alt = item.alt || '';
    if (item.caption) {
      lightbox.capEl.textContent = item.caption;
      lightbox.capEl.style.display = 'block';
    } else {
      lightbox.capEl.textContent = '';
      lightbox.capEl.style.display = 'none';
    }
    var many = lightbox.items.length > 1;
    lightbox.prevBtn.style.display = many ? 'flex' : 'none';
    lightbox.nextBtn.style.display = many ? 'flex' : 'none';
  }

  function openLightbox(items, idx) {
    if (!items || items.length === 0) return;
    ensureLightbox();
    lightbox.items = items;
    lightbox.idx = Math.max(0, Math.min(items.length - 1, idx || 0));
    showLightboxItem();
    lightbox.el.style.display = 'flex';
    lightbox.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    if (!lightbox.el) return;
    lightbox.el.style.display = 'none';
    lightbox.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    lightbox.items = [];
    lightbox.idx = 0;
  }

  function stepLightbox(dir) {
    if (lightbox.items.length === 0) return;
    var n = lightbox.items.length;
    lightbox.idx = (lightbox.idx + dir + n) % n;
    showLightboxItem();
  }

  function hydrateMediaGalleryLightbox(root) {
    if (root.__neoLbWired) return;
    root.__neoLbWired = true;

    var imgs = root.querySelectorAll('.neo-media-gallery__item img');
    for (var i = 0; i < imgs.length; i++) imgs[i].style.cursor = 'zoom-in';

    root.addEventListener('click', function (e) {
      var img = e.target && e.target.tagName === 'IMG' ? e.target : null;
      if (!img || !img.closest('.neo-media-gallery__item')) return;
      var all = root.querySelectorAll('.neo-media-gallery__item img');
      var items = [];
      var startIdx = 0;
      for (var k = 0; k < all.length; k++) {
        var fig = all[k].closest('.neo-media-gallery__item');
        var capNode = fig ? fig.querySelector('.neo-media-gallery__caption') : null;
        items.push({
          src: all[k].getAttribute('src') || all[k].src,
          alt: all[k].getAttribute('alt') || '',
          caption: capNode ? capNode.textContent.trim() : ''
        });
        if (all[k] === img) startIdx = k;
      }
      openLightbox(items, startIdx);
    });
  }

  function hydrateMediaGalleryCarousel(root) {
    if (root.__neoWired) return;
    root.__neoWired = true;
    var track = root.querySelector('.neo-media-gallery__carousel-track');
    if (!track) return;
    var prev = root.querySelector('.neo-media-gallery__nav--prev');
    var next = root.querySelector('.neo-media-gallery__nav--next');
    var scrubber = root.querySelector('.neo-media-gallery__scrubber');
    var thumb = root.querySelector('.neo-media-gallery__scrubber-thumb');

    function snapToItem(dir) {
      var firstItem = track.querySelector('.neo-media-gallery__item');
      var step = firstItem ? firstItem.offsetWidth + 8 : track.clientWidth;
      if (step <= 0) return;
      var current = track.scrollLeft / step;
      var nearest = Math.round(current);
      // If we're already sitting on (or sub-pixel near) an item slot, step away from it.
      // Otherwise floor/ceil to the next slot in the requested direction.
      var targetIdx = (Math.abs(current - nearest) < 0.1)
        ? nearest + dir
        : (dir > 0 ? Math.ceil(current) : Math.floor(current));
      if (targetIdx < 0) targetIdx = 0;
      track.scrollTo({ left: targetIdx * step, behavior: 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { snapToItem(-1); });
    if (next) next.addEventListener('click', function () { snapToItem(1); });

    if (!scrubber || !thumb) return;

    function updateThumb() {
      var sw = track.scrollWidth;
      var cw = track.clientWidth;
      if (sw <= cw) { thumb.style.width = '100%'; thumb.style.left = '0'; return; }
      var widthPct = (cw / sw) * 100;
      var leftPct = (track.scrollLeft / (sw - cw)) * (100 - widthPct);
      thumb.style.width = widthPct + '%';
      thumb.style.left = leftPct + '%';
    }

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { updateThumb(); ticking = false; });
    });
    window.addEventListener('resize', updateThumb);

    function pointerToScrollLeft(clientX) {
      var rect = scrubber.getBoundingClientRect();
      var ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      var sw = track.scrollWidth;
      var cw = track.clientWidth;
      return ratio * (sw - cw);
    }

    var dragging = false;
    scrubber.addEventListener('pointerdown', function (e) {
      dragging = true;
      scrubber.setPointerCapture(e.pointerId);
      track.scrollLeft = pointerToScrollLeft(e.clientX);
    });
    scrubber.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      track.scrollLeft = pointerToScrollLeft(e.clientX);
    });
    scrubber.addEventListener('pointerup', function (e) {
      dragging = false;
      if (scrubber.hasPointerCapture && scrubber.hasPointerCapture(e.pointerId)) {
        scrubber.releasePointerCapture(e.pointerId);
      }
    });
    scrubber.addEventListener('pointercancel', function () { dragging = false; });

    // Defer to after layout (initial scrollWidth/clientWidth can both be 0),
    // and re-run when the track resizes (lazy images, viewport changes).
    requestAnimationFrame(updateThumb);
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(updateThumb);
      ro.observe(track);
    }
  }

  // ============================================================
  // Countdown: the markup ships a readable target date, and this upgrades it
  // to a live timer. Without script the reader still sees the date — the
  // element never renders empty.
  // ============================================================

  function hydrateCountdown(root) {
    if (root.__neoCountdownWired) return;
    root.__neoCountdownWired = true;

    var target = Date.parse(root.getAttribute('data-neo-countdown'));
    if (isNaN(target)) return;

    var parts = [
      ['data-show-days', 86400000, 'd'],
      ['data-show-hours', 3600000, 'h'],
      ['data-show-minutes', 60000, 'm'],
      ['data-show-seconds', 1000, 's']
    ];

    function tick() {
      if (!root.isConnected) { clearInterval(timer); return; }
      var left = target - Date.now();
      if (left <= 0) {
        root.textContent = '00:00:00';
        clearInterval(timer);
        return;
      }
      var out = '';
      for (var i = 0; i < parts.length; i++) {
        if (root.getAttribute(parts[i][0]) !== '1') continue;
        var unit = Math.floor(left / parts[i][1]);
        left -= unit * parts[i][1];
        out += (out ? ' ' : '') + unit + parts[i][2];
      }
      root.textContent = out;
    }

    var timer = setInterval(tick, 1000);
    tick();
  }

  // ============================================================
  // linked-form: the element carries `formId` + `submitEndpoint` (stamped by
  // the builder precisely so a headless renderer can POST it). The field list
  // comes from the PUBLIC, CORS-enabled read endpoint derived from that same
  // URL, so no per-theme or per-tenant constant is needed here.
  //
  // Submission is a NATIVE multipart POST, not fetch: the endpoint answers 302
  // back to `redirect_to` with ?isSuccessful=true / ?isError=true, which a
  // top-level form navigation follows without needing CORS.
  // ============================================================

  /** …/<tenant>/api/v1/news/form-submissions → …/<tenant>/api/aiconeo/public/aico-forms/<id> */
  function neoFormReadUrl(submitEndpoint, formId) {
    var base = String(submitEndpoint || '')
      .replace(/\/api\/v1\/news\/form-submissions\/?$/, '')
      .replace(/\/+$/, '');
    if (!base) return '';
    return base + '/api/aiconeo/public/aico-forms/' + encodeURIComponent(formId);
  }

  /** Current URL without the outcome flags, so a resubmit does not stack them. */
  function neoFormRedirectTarget() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('isSuccessful');
      url.searchParams.delete('isError');
      return url.toString();
    } catch (e) {
      return window.location.href;
    }
  }

  function neoT(key, fallback) {
    return (window.AICO_NEO_I18N && window.AICO_NEO_I18N[key]) || fallback;
  }

  function neoFormFieldHtml(field, formId) {
    var control = formControlFor(field.type);
    var name = escapeAttr(field.key);
    var id = 'aico-form-' + escapeAttr(formId) + '-' + escapeAttr(field.key);
    var label = escapeHtml(field.label || field.key);
    var required = field.isRequired ? ' required' : '';
    var mark = field.isRequired ? ' <span class="aico_label__required" aria-hidden="true">*</span>' : '';
    var half = String(field.columnSize || '').toUpperCase() === 'HALF_ROW';
    var options = listOf(field.config && field.config.possibleValues);

    var body;
    var i;
    if (control.kind === 'textarea') {
      body = '<textarea class="aico_input" id="' + id + '" name="' + name + '" rows="5"' + required + '></textarea>';
    } else if (control.kind === 'select') {
      body = '<select class="aico_input" id="' + id + '" name="' + name + '"' + required + '><option value=""></option>';
      for (i = 0; i < options.length; i++) {
        body += '<option value="' + escapeAttr(options[i].value) + '">' +
          escapeHtml(options[i].label || options[i].value) + '</option>';
      }
      body += '</select>';
    } else if (control.kind === 'radio') {
      body = '<div class="aico_options">';
      for (i = 0; i < options.length; i++) {
        var rid = id + '-' + i;
        body += '<label class="aico_option" for="' + rid + '">' +
          '<input type="radio" id="' + rid + '" name="' + name + '" value="' +
          escapeAttr(options[i].value) + '"' + required + '>' +
          '<span>' + escapeHtml(options[i].label || options[i].value) + '</span></label>';
      }
      body += '</div>';
    } else if (control.kind === 'checkbox') {
      // Single opt-in: the label sits beside the box, not above it.
      return '<div class="form-item form-item--checkbox' + (half ? ' form-item--half' : '') + '">' +
        '<label class="aico_label aico_label--inline" for="' + id + '">' +
        '<input type="checkbox" id="' + id + '" name="' + name + '" value="1"' + required + '>' +
        '<span>' + label + mark + '</span></label></div>';
    } else {
      body = '<input class="aico_input" type="' + control.inputType + '" id="' + id + '" name="' + name + '"' + required + '>';
    }

    return '<div class="form-item' + (half ? ' form-item--half' : '') + '">' +
      '<label class="aico_label" for="' + id + '">' + label + mark + '</label>' + body + '</div>';
  }

  function neoBuildForm(host, form) {
    var formId = host.getAttribute('data-neo-form-id');
    var endpoint = host.getAttribute('data-neo-form-endpoint');
    var elements = (form && Array.isArray(form.elements)) ? form.elements : [];

    var fields = elements.filter(function (f) {
      return f && !f.isHidden && f.type !== 'title' && f.type !== 'html' && f.key;
    }).sort(function (a, b) { return (a.position || 0) - (b.position || 0); });

    if (fields.length === 0) {
      warnType('linked-form', 'form ' + formId + ' has no visible fields');
      return;
    }

    // No `novalidate`: native browser validation is the only client-side check.
    var html = '<form class="aico-custom-form" method="post" enctype="multipart/form-data" action="' +
      escapeAttr(endpoint) + '">' +
      '<input type="hidden" name="form_id" value="' + escapeAttr(formId) + '">' +
      '<input type="hidden" name="redirect_to" value="' + escapeAttr(neoFormRedirectTarget()) + '">';

    if (host.getAttribute('data-neo-form-title') === '1' && form.name) {
      html += '<h3 class="aico-custom-form__title">' + escapeHtml(form.name) + '</h3>';
    }

    html += '<div class="aico-custom-form__fields">';
    for (var i = 0; i < fields.length; i++) html += neoFormFieldHtml(fields[i], formId);
    html += '</div>';
    html += '<button type="submit" class="aico-custom-form__submit">' +
      escapeHtml(form.submissionText || neoT('common.send', 'Senden')) + '</button></form>';

    var slot = host.querySelector('.aico-form-slot');
    if (slot) slot.innerHTML = html;
  }

  function neoFormBanners(host) {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var success = host.querySelector('.aico-form-banner--success');
    var error = host.querySelector('.aico-form-banner--error');

    if (params.get('isSuccessful') === 'true' && success) {
      success.textContent = neoT('js.form_success', 'Vielen Dank! Ihre Anfrage wurde erfolgreich gesendet.');
      success.hidden = false;
    } else if (params.get('isError') === 'true' && error) {
      error.textContent = neoT('js.form_error', 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
      error.hidden = false;
    }
  }

  function hydrateLinkedForm(host) {
    if (host.getAttribute('data-neo-form-hydrated') === '1') return;
    host.setAttribute('data-neo-form-hydrated', '1');

    var formId = host.getAttribute('data-neo-form-id');
    var url = neoFormReadUrl(host.getAttribute('data-neo-form-endpoint'), formId);
    if (!url || typeof fetch !== 'function') {
      warnType('linked-form', 'no usable read endpoint for form ' + formId);
      return;
    }

    fetch(url, { credentials: 'omit', headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        neoBuildForm(host, (payload && payload.data) ? payload.data : payload);
        neoFormBanners(host);
      })
      .catch(function (error) {
        if (window.console && console.warn) {
          console.warn('content-builder-neo: could not load aico form ' + formId, error);
        }
        // Left un-hydrated so a later hydrate() call can retry.
        host.setAttribute('data-neo-form-hydrated', '0');
      });
  }

  function hydrate(root) {
    injectStyles();
    var scope = root && root.querySelectorAll ? root : document;
    var slideshows = scope.querySelectorAll('[data-neo-slideshow="1"]');
    for (var i = 0; i < slideshows.length; i++) hydrateSlideshow(slideshows[i]);
    var carousels = scope.querySelectorAll('[data-neo-mg-carousel="1"]');
    for (var j = 0; j < carousels.length; j++) hydrateMediaGalleryCarousel(carousels[j]);
    var galleries = scope.querySelectorAll('.content-type-neo-media-gallery');
    for (var k = 0; k < galleries.length; k++) hydrateMediaGalleryLightbox(galleries[k]);
    var countdowns = scope.querySelectorAll('[data-neo-countdown]');
    for (var m = 0; m < countdowns.length; m++) hydrateCountdown(countdowns[m]);
    var forms = scope.querySelectorAll('[data-neo-form="1"]');
    for (var n = 0; n < forms.length; n++) hydrateLinkedForm(forms[n]);
    // The only hydration step that injects third-party code. See the audio arm.
    var players = scope.querySelectorAll('[data-neo-audio="1"]');
    for (var p = 0; p < players.length; p++) hydrateAudio(players[p]);
    var runtimeLists = scope.querySelectorAll('[data-neo-runtime-list]');
    for (var q = 0; q < runtimeLists.length; q++) hydrateRuntimeList(runtimeLists[q]);
    if (runtimeLists.length) bindRuntimeListToggles();
    replayCategoryRowLayout(root);
  }

  // The theme's custom.js lays the category rows of the product tab out as a
  // tile grid on jQuery ready: it wraps every
  // `.product_submenu .tab-content .content-row div .content-html-text .row`
  // in one `.slickSlider` and gives their parent `item_col__wrap col__wrap_<n>`.
  // A document inlined in the page is painted before that handler runs, so
  // the rows are there when it looks. A document too large to inline
  // (custom_fields.content_builder_json absent, mount without
  // data-neo-data-id) is fetched and painted after it has run, so the rows
  // would stack one under the other. For that mount only, the same step is
  // replayed once the rows exist. It runs in a timer started from a ready
  // handler, so it always comes after custom.js's own ready handler (which is
  // registered before the document is ready, even when the fetch resolves
  // between two deferred scripts), and it is skipped when custom.js did reach
  // the rows (they already sit in a `.slickSlider`). An inlined mount never
  // takes this path, so its output is unchanged.
  var CATEGORY_ROW_SELECTOR = '.product_submenu .tab-content .content-row div .content-html-text .row';
  function replayCategoryRowLayout(root) {
    if (!root || !root.getAttribute || !root.hasAttribute('data-aico-neo-mount')) return;
    if (root.getAttribute('data-neo-data-id')) return;
    var $ = window.jQuery;
    if (typeof $ !== 'function') return;
    $(function () { window.setTimeout(function () {
      var $rows = $(CATEGORY_ROW_SELECTOR);
      if (!$rows.length) return;
      if ($rows.parent('.slickSlider').length) return;
      // custom.js, verbatim in effect
      $rows.wrapAll('<div class="slickSlider"></div>');
      if ($rows.length > 10) {
        $rows.slice(10).hide();
        $rows.parent().addClass('item_col__wrap col__wrap_10');
      } else {
        $rows.parent().addClass('item_col__wrap col__wrap_' + $rows.length);
      }
    }, 0); });
  }

  // ============================================================
  // Locale resolution. Products and collections inline the RAW neo document
  // in `custom_fields.content_builder_json` (ProductMetafieldConverterService::
  // resolveContentBuilderNeoJson does not apply a locale), and the URL
  // fallback is raw as well, so the storefront applies the visitor's
  // language itself before render(). Port of aico
  // Modules/Storefront/Support/NeoLocaleResolver, unchanged from FC-Zurich.
  // ============================================================
  function localeNormalize(locale) {
    return String(locale == null ? '' : locale).trim().replace(/-/g, '_');
  }

  function localeLanguage(locale) {
    var normalized = localeNormalize(locale);
    var separator = normalized.indexOf('_');
    return (separator === -1 ? normalized : normalized.slice(0, separator)).toLowerCase();
  }

  function localeSameLanguage(first, second) {
    var language = localeLanguage(first);
    return language !== '' && language === localeLanguage(second);
  }

  /** Entries serving `locale`, weakest first: same-language keys, then the exact key last so it wins the merge. */
  function localeMatchKeys(availableKeys, locale) {
    var exact = null;
    var sameLanguage = [];
    var wanted = localeNormalize(locale).toLowerCase();
    for (var i = 0; i < availableKeys.length; i++) {
      var key = availableKeys[i];
      if (localeNormalize(key).toLowerCase() === wanted) {
        exact = key;
      } else if (localeSameLanguage(key, locale)) {
        sameLanguage.push(key);
      }
    }
    return exact === null ? sameLanguage : sameLanguage.concat([exact]);
  }

  function localePickOverrideKeys(availableKeys, locale, defaultLocale, baseLocale) {
    if (!availableKeys.length || !locale) return [];

    // The base content already speaks the requested language.
    if (baseLocale && localeSameLanguage(locale, baseLocale)) return [];

    var matches = localeMatchKeys(availableKeys, locale);
    if (matches.length) return matches;

    if (defaultLocale && !(baseLocale && localeSameLanguage(defaultLocale, baseLocale))) {
      matches = localeMatchKeys(availableKeys, defaultLocale);
      if (matches.length) return matches;
    }

    return [];
  }

  function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function localeApplyElementOverrides(elements, overridesByElementId) {
    for (var i = 0; i < elements.length; i++) {
      var element = elements[i];
      if (!isPlainObject(element)) continue;

      var elementId = element.id;
      var override = (typeof elementId === 'string' || typeof elementId === 'number')
        ? overridesByElementId[elementId]
        : null;
      if (isPlainObject(override) && isPlainObject(override.content)) {
        var merged = {};
        var base = isPlainObject(element.content) ? element.content : {};
        for (var baseKey in base) if (Object.prototype.hasOwnProperty.call(base, baseKey)) merged[baseKey] = base[baseKey];
        for (var overrideKey in override.content) if (Object.prototype.hasOwnProperty.call(override.content, overrideKey)) merged[overrideKey] = override.content[overrideKey];
        element.content = merged;
      }
      // A locale's own desktop settings (`override.styles.desktop`, e.g. a translation's own
      // picture width, aico native settings contract v2, section 6): merged over the base
      // desktop settings, an empty value clearing a base setting in that language only. Same
      // rule as aico NeoLocaleResolver::mergeDesktopStyles(). The other tiers stay the base's.
      if (isPlainObject(override) && isPlainObject(override.styles) && isPlainObject(override.styles.desktop) &&
          Object.keys(override.styles.desktop).length) {
        var styles = {};
        var baseStyles = isPlainObject(element.styles) ? element.styles : {};
        for (var tierKey in baseStyles) if (Object.prototype.hasOwnProperty.call(baseStyles, tierKey)) styles[tierKey] = baseStyles[tierKey];
        var desktop = {};
        var baseDesktop = isPlainObject(baseStyles.desktop) ? baseStyles.desktop : {};
        for (var desktopKey in baseDesktop) if (Object.prototype.hasOwnProperty.call(baseDesktop, desktopKey)) desktop[desktopKey] = baseDesktop[desktopKey];
        for (var styleKey in override.styles.desktop) {
          if (!Object.prototype.hasOwnProperty.call(override.styles.desktop, styleKey)) continue;
          var styleValue = override.styles.desktop[styleKey];
          if (styleValue === '' || styleValue === null) delete desktop[styleKey];
          else desktop[styleKey] = styleValue;
        }
        styles.desktop = desktop;
        element.styles = styles;
      }
      // An element the locale's page does not have at all: its override hides it in that
      // language (aico NeoLocaleResolver marks the same `localeHidden`). Only a literal true.
      if (isPlainObject(override) && override.hidden === true) {
        element.localeHidden = true;
      }

      if (Array.isArray(element.children)) {
        localeApplyElementOverrides(element.children, overridesByElementId);
      }
    }
  }

  function hasLocaleStructure(document) {
    if (!isPlainObject(document)) return false;
    if (typeof document.baseLocale === 'string' && document.baseLocale.trim() !== '') return true;
    var overrides = document.localeOverrides;
    return isPlainObject(overrides) && Object.keys(overrides).length > 0;
  }

  function resolveLocale(document, locale, defaultLocale) {
    if (!isPlainObject(document)) return document;

    var overrides = isPlainObject(document.localeOverrides) ? document.localeOverrides : {};
    var baseLocale = typeof document.baseLocale === 'string' ? document.baseLocale : null;
    var overrideKeys = localePickOverrideKeys(Object.keys(overrides), locale, defaultLocale, baseLocale);
    var filledIds = {};

    for (var i = 0; i < overrideKeys.length; i++) {
      var override = overrides[overrideKeys[i]];
      if (!isPlainObject(override)) continue;

      var elementOverrides = isPlainObject(override.elements) ? override.elements : null;
      if (elementOverrides) {
        for (var filledId in elementOverrides) {
          if (!Object.prototype.hasOwnProperty.call(elementOverrides, filledId)) continue;
          var filled = elementOverrides[filledId];
          if (isPlainObject(filled) && isPlainObject(filled.content) && Object.keys(filled.content).length) {
            filledIds[filledId] = true;
          }
        }
      }
      if (elementOverrides && Array.isArray(document.sections)) {
        for (var s = 0; s < document.sections.length; s++) {
          var section = document.sections[s];
          if (isPlainObject(section) && Array.isArray(section.elements)) {
            localeApplyElementOverrides(section.elements, elementOverrides);
          }
        }
      }

      // ECOFORT ADDITION (not in aico NeoLocaleResolver, which ignores it): the legacy
      // converter (BuildsNeoLocaleOverrides::diffLocaleSections) writes translated row
      // titles as `sections: {<sectionId>: {name}}`. The engine never prints a section
      // name, so this only feeds theme code that does (Ecofort product sidebar titles).
      var sectionOverrides = isPlainObject(override.sections) ? override.sections : null;
      if (sectionOverrides && Array.isArray(document.sections)) {
        for (var n = 0; n < document.sections.length; n++) {
          var target = document.sections[n];
          if (!isPlainObject(target)) continue;
          var sectionOverride = sectionOverrides[target.id];
          if (isPlainObject(sectionOverride) && typeof sectionOverride.name === 'string') {
            target.name = sectionOverride.name;
          }
        }
      }

      if (isPlainObject(override.seo) && Object.keys(override.seo).length) {
        var seo = {};
        var baseSeo = isPlainObject(document.seo) ? document.seo : {};
        for (var seoKey in baseSeo) if (Object.prototype.hasOwnProperty.call(baseSeo, seoKey)) seo[seoKey] = baseSeo[seoKey];
        for (var overrideSeoKey in override.seo) if (Object.prototype.hasOwnProperty.call(override.seo, overrideSeoKey)) seo[overrideSeoKey] = override.seo[overrideSeoKey];
        document.seo = seo;
      }
    }

    // An element only one language has (`localeOnly`, written by the aico legacy imports for content
    // only a translation held): drawn where an applied override fills it and nowhere else, the base
    // language included, exactly as aico NeoLocaleResolver marks it.
    if (Array.isArray(document.sections)) {
      for (var h = 0; h < document.sections.length; h++) {
        var hiddenSection = document.sections[h];
        if (isPlainObject(hiddenSection) && Array.isArray(hiddenSection.elements)) {
          localeHideUnfilledOnlyElements(hiddenSection.elements, filledIds);
        }
      }
    }

    delete document.localeOverrides;
    delete document.baseLocale;

    return document;
  }

  function localeHideUnfilledOnlyElements(elements, filledIds) {
    for (var i = 0; i < elements.length; i++) {
      var element = elements[i];
      if (!isPlainObject(element)) continue;
      var elementId = element.id;
      if (typeof element.localeOnly === 'string' && element.localeOnly.trim() !== ''
          && !((typeof elementId === 'string' || typeof elementId === 'number') && filledIds[elementId] === true)) {
        element.localeHidden = true;
      }
      if (Array.isArray(element.children)) {
        localeHideUnfilledOnlyElements(element.children, filledIds);
      }
    }
  }

  // ============================================================
  // Public API
  // ============================================================

  window.NewsNeo = {
    hasContent: hasNeoContent,
    hasLegacyCards: function (data) {
      var neo = data && data.contentBuilderNeoJson;
      var sections = neo && Array.isArray(neo.sections) ? neo.sections : [];
      for (var i = 0; i < sections.length; i++) if (sectionHasLegacyCards(sections[i])) return true;
      return false;
    },
    render: render,
    hydrate: hydrate,
    hasLocaleStructure: hasLocaleStructure,
    resolveLocale: resolveLocale,
    registerType: function (type, fn) {
      if (typeof type === 'string' && typeof fn === 'function') {
        renderers[type] = fn;
      }
    },
    _renderers: renderers
  };

})(window);

/**
 * Bootstrap (page-renderer addition, ported from the Lausanne theme).
 *
 * Reads the inline page-data JSON, renders it into the page mount and hydrates.
 * Inert when there is no mount, no data, or no Neo content — so an empty page
 * (or a theme that only sometimes carries Neo content) renders nothing instead
 * of erroring.
 */
(function (window, document) {
  'use strict';

  function boot() {
    var mount = document.getElementById('aico-content');
    var dataEl = document.getElementById('aico-page-data');
    if (!mount || !dataEl || !window.NewsNeo) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent || dataEl.innerText || '{}');
    } catch (e) {
      if (window.console && console.warn) console.warn('content-builder-neo: bad page data JSON', e);
      return;
    }

    if (!window.NewsNeo.hasContent(data)) return;

    mount.innerHTML = window.NewsNeo.render(data);
    window.NewsNeo.hydrate(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(window, document);
